import React, { Component, Fragment, CSSProperties } from "react";
import ReactDOM from "react-dom";
import "bootstrap/dist/css/bootstrap.css";
import Card from "react-bootstrap/Card";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Badge from "react-bootstrap/Badge";
import Alert from "react-bootstrap/Alert";
import InputGroup from "react-bootstrap/InputGroup";
import Collapse from "react-bootstrap/Collapse";
import {
  ToastsContainer,
  ToastsContainerPosition,
  ToastsStore,
} from "react-toasts";
import Select from "react-select";
import { callApi } from "../../utils/api";
import { dropdownFollowUpBranchKey } from "../../utils/dynamicChecklistDropdownValue";
import { MasterFieldDuplicateTable } from "./MasterFieldDuplicateTable";
import { DeedGroupFieldTable } from "./DeedGroupFieldTable";
import {
  reorderAdjacentFields,
  resolveFieldFromSourceKey,
} from "./duplicateFieldKeys";
import {
  NATURE_PROPERTY_CATALOG,
  naturePropertyLabel,
  surveyGroupIdForNature,
  surveyLinkedGroups,
  surveyGroupsVisibleForFile,
  surveyNatureSectionFieldCount,
  matchNaturePropertyKey,
} from "../../utils/dynamicChecklistSurveyNature";
import {
  SURVEY_HIERARCHY_DYNAMIC_TAGS,
  SURVEY_VERIFICATION_TAGS_OVERLAPPING_GD,
  SURVEY_HIERARCHY_LEVELS,
  legacySurveyHierarchyGroupId,
  surveyHierarchyGroupId,
  surveyHierarchyGroupForLevel,
  surveyHierarchyLevelsForNature,
  surveyHierarchyLinkedGroups,
  SurveyHierarchyLevel,
  buildMeasurementDirectionDynamicTags,
} from "../../utils/dynamicChecklistSurveyHierarchy";
import {
  EC_HIERARCHY_LEVELS,
  EC_VERIFICATION_DYNAMIC_TAGS,
  EcHierarchyLevel,
  ecBlockGroup,
  ecEntryGroup,
  ecLinkedGroups,
  ensureEcVerificationGroups,
} from "../../utils/dynamicChecklistEcVerification";

interface SearchableSelectOption {
  value: string;
  label: string;
  isDisabled?: boolean;
}

interface SearchableSelectProps {
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  className?: string;
  ariaLabel?: string;
  inputId?: string;
}

export const DVC_SELECT_STYLES: any = {
  control: (provided: any, state: any) => ({
    ...provided,
    minHeight: 31,
    height: 31,
    fontSize: 14,
    borderColor: state.isFocused ? "#80bdff" : provided.borderColor,
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : "none",
    ":hover": { borderColor: state.isFocused ? "#80bdff" : "#ced4da" },
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    height: 29,
    padding: "0 8px",
  }),
  input: (provided: any) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    height: 29,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (provided: any) => ({ ...provided, padding: "0 6px" }),
  clearIndicator: (provided: any) => ({ ...provided, padding: "0 4px" }),
  singleValue: (provided: any) => ({ ...provided, lineHeight: "29px" }),
  placeholder: (provided: any) => ({
    ...provided,
    lineHeight: "29px",
    color: "#6c757d",
  }),
  menu: (provided: any) => ({ ...provided, zIndex: 9999, fontSize: 14 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

export function SearchableSelect(props: SearchableSelectProps) {
  const {
    value,
    options,
    onChange,
    placeholder = "Select…",
    isClearable = true,
    isDisabled,
    className,
    ariaLabel,
    inputId,
  } = props;
  const selected = options.find((o) => o.value === value) || null;
  return (
    <Select
      classNamePrefix="dvc-rs"
      className={className}
      options={options as any}
      value={selected as any}
      onChange={(opt: any) => onChange(opt && opt.value != null ? String(opt.value) : "")}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      aria-label={ariaLabel}
      inputId={inputId}
      styles={DVC_SELECT_STYLES}
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      menuPosition="fixed"
    />
  );
}

/**
 * Dynamic master checklist with runtime rendering and conditional follow-ups.
 */

export type ChecklistFieldType =
  | "text"
  | "dropdown"
  | "date"
  | "checkbox"
  | "remarks"
  | "yes_no";

export type ChecklistFieldLayout = "full" | "half" | "third";

export interface DropdownOptionFollowUpDef {
  /**
   * Branch key: for a parent `dropdown`, must match an option string exactly.
   * For a parent `checkbox`, use `"true"` or `"false"` (checked / unchecked).
   */
  whenOption: string;
  questionLabel: string;
  questionType: ChecklistFieldType;
  /** Storage key in `checklistValues` (stable when possible). */
  valueKey: string;
  /** When `questionType` is `dropdown`, options for the follow-up control. */
  followUpOptions?: string[];
  /** When answer type is `dropdown` or `checkbox`, allow another level of follow-ups. */
  nestedFollowUpsEnabled?: boolean;
  nestedFollowUps?: DropdownOptionFollowUpDef[];
  /** When true, this follow-up answer is required when its branch is visible. */
  required?: boolean;
  /**
   * Stored value for the "Dynamic field value" select. Interpreted against the
   * master named by `dynamicValueSource` (defaults to `tag`).
   */
  scrutinyLinkedDeedId?: string | number;
  /** Which master powers the "Dynamic field value" select. Defaults to `tag`. */
  dynamicValueSource?: DynamicValueSource;
}

export type ChecklistSectionHeaderVariant = "default" | "warning";

/** Section-first setup: checklist, document types, or bank-product checklist groups. */
export type ChecklistSectionFlowMode =
  | "checklist"
  | "document_types"
  | "bank_product"
  | "survey_verification"
  | "ec_verification";

/**
 * Which master powers the "Dynamic field value" dropdown for a given field/follow-up:
 *  - `tag`           → `autocomplete_tag.php` scrutiny tags (the `@@name@@` family).
 *  - `front_office`  → Front office master (managed from the "Front office master" tab).
 * Defaults to `tag` when omitted (backwards compatible with existing data).
 */
export type DynamicValueSource = "tag" | "front_office";

export interface ChecklistFieldDef {
  id: string;
  label: string;
  type: ChecklistFieldType;
  required?: boolean;
  /** When true, field is read-only at runtime (values still visible). */
  disabled?: boolean;
  order: number;
  options?: string[];
  documentMapKey?: string;
  /** Runtime: show empty verification checkbox beside the answer (General Verification style). */
  showVerifyCheckbox?: boolean;
  /** Runtime grid width; defaults to half (2 columns on md+). */
  layout?: ChecklistFieldLayout;
  /** When `type` is `dropdown` or `checkbox`, show extra questions per branch. */
  dropdownFollowUpsEnabled?: boolean;
  /** Follow-up questions keyed by option (`dropdown`) or `"true"` / `"false"` (`checkbox`). */
  optionFollowUps?: DropdownOptionFollowUpDef[];
  /**
   * Stored value for the "Dynamic field value" select. Interpreted against the
   * master named by `dynamicValueSource` (defaults to `tag`).
   */
  scrutinyLinkedDeedId?: string | number;
  /** Which master powers the "Dynamic field value" select. Defaults to `tag`. */
  dynamicValueSource?: DynamicValueSource;
  /** Deed master id (`deed_master_get.php`) — same as Scrutiny Form "Document Type". */
  deedMasterId?: string | number;
  /** Bank-product row id (`bank_product_get.php`). */
  bankProductId?: string | number;
  /** Optional badge text shown beside the label at runtime (e.g. "from Front Office: Borrower Name"). */
  labelTag?: string;
}

export interface ChecklistRepeatableGroupDef {
  id: string;
  label: string;
  order: number;
  addButtonLabel: string;
  fields: ChecklistFieldDef[];
  minInstances?: number;
  maxInstances?: number;
  /** When set, group is shown only if gate yes/no field is "Yes". */
  deedMasterId?: number;
  gateFieldId?: string;
  /** Bank-product checklist block (`bank_product_get.php`). */
  bankProductId?: number;
  /** Nature of property key (`agri`, `existing_building`, …) for survey verification flow. */
  naturePropertyKey?: string;
  /** General Detail hierarchy level for survey verification repeatables. */
  surveyHierarchyLevel?: SurveyHierarchyLevel;
  /** EC verification hierarchy level (document block / entry line). */
  ecHierarchyLevel?: EcHierarchyLevel;
}

export interface ChecklistSectionDef {
  id: string;
  title: string;
  order: number;
  fields: ChecklistFieldDef[];
  /** How this section is configured in master setup and rendered at runtime. */
  flowMode?: ChecklistSectionFlowMode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  headerVariant?: ChecklistSectionHeaderVariant;
  repeatableGroups?: ChecklistRepeatableGroupDef[];
  /** Document types (deed master) enabled for this section in the designer. */
  enabledDeedMasterIds?: number[];
  /** Bank products enabled for this section in the designer. */
  enabledBankProductIds?: number[];
  /** Nature-of-property keys enabled for survey verification in this section. */
  enabledNaturePropertyKeys?: string[];
  /** General Detail hierarchy levels enabled (schedule / survey / boundary / measurement). */
  enabledSurveyHierarchyLevels?: SurveyHierarchyLevel[];
}

/** Row from `bank_product_get.php` (active bank + product combinations). */
export interface BankProductRow {
  id: number;
  bank_name: string;
  product_name: string;
  product_id?: number;
  bank_id?: number;
  branch_id?: number;
}

export interface DeedMasterRow {
  id: number;
  name: string;
}

export interface InputMasterFieldRow {
  id: number;
  label: string;
  type: string;
  name: string;
  optionList?: { name: string }[] | null;
}

export function sortSections(list: ChecklistSectionDef[]): ChecklistSectionDef[] {
  return [...list]
    .sort((a, b) => a.order - b.order)
    .map((s) => ({
      ...s,
      fields: [...s.fields].sort((a, b) => a.order - b.order),
      repeatableGroups: [...(s.repeatableGroups || [])]
        .sort((a, b) => a.order - b.order)
        .map((g) => ({
          ...g,
          fields: [...g.fields].sort((a, b) => a.order - b.order),
        })),
    }));
}

/** Coerce API/master JSON booleans on follow-up nodes (including nested). */
export function normalizeFollowUpFromApi(
  fu: DropdownOptionFollowUpDef
): DropdownOptionFollowUpDef {
  return {
    ...fu,
    required: !!fu.required,
    nestedFollowUpsEnabled: !!fu.nestedFollowUpsEnabled,
    nestedFollowUps: fu.nestedFollowUps?.map(normalizeFollowUpFromApi),
  };
}

/** Coerce API/master JSON booleans so `disabled` and `required` behave consistently after load. */
export function normalizeChecklistFieldFromApi(f: ChecklistFieldDef): ChecklistFieldDef {
  const raw = f as ChecklistFieldDef & { disabled?: unknown };
  return {
    ...f,
    required: !!f.required,
    disabled: !!raw.disabled,
    showVerifyCheckbox: !!f.showVerifyCheckbox,
    dropdownFollowUpsEnabled: !!(
      f.dropdownFollowUpsEnabled ||
      (f.optionFollowUps?.length &&
        fieldTypeSupportsOptionFollowUps(f.type) &&
        f.optionFollowUps.some((fu) => String(fu.questionLabel ?? "").trim() !== ""))
    ),
    optionFollowUps: f.optionFollowUps?.map(normalizeFollowUpFromApi),
  };
}

export function normalizeMasterFromApi(sections: ChecklistSectionDef[]): ChecklistSectionDef[] {
  return sortSections(
    sections.map((s) => ({
      ...s,
      fields: (s.fields || []).map(normalizeChecklistFieldFromApi),
      repeatableGroups: (s.repeatableGroups || []).map((g) => ({
        ...g,
        fields: (g.fields || []).map(normalizeChecklistFieldFromApi),
      })),
    }))
  );
}

export function repeatableEntriesKey(sectionId: string, groupId: string): string {
  return `${sectionId}::${groupId}`;
}

export function emptyRepeatableRow(fields: ChecklistFieldDef[]): Record<string, string> {
  const row: Record<string, string> = {};
  fields.forEach((f) => {
    if (f.type === "checkbox") row[f.id] = "false";
    else row[f.id] = "";
  });
  return row;
}

export function emptyRepeatableValuesFromMaster(sections: ChecklistSectionDef[]): Record<string, Record<string, string>[]> {
  const out: Record<string, Record<string, string>[]> = {};
  sections.forEach((s) => {
    (s.repeatableGroups || []).forEach((g) => {
      const min = Math.max(1, g.minInstances ?? 1);
      const key = repeatableEntriesKey(s.id, g.id);
      out[key] = Array.from({ length: min }, () => emptyRepeatableRow(g.fields));
    });
  });
  return out;
}

export function initSectionOpen(sections: ChecklistSectionDef[]): Record<string, boolean> {
  const o: Record<string, boolean> = {};
  sections.forEach((s) => {
    o[s.id] = !s.collapsible || s.defaultExpanded !== false;
  });
  return o;
}

export function layoutToColMd(layout?: ChecklistFieldLayout): number {
  if (layout === "full") return 12;
  if (layout === "third") return 4;
  return 6;
}

const MAX_FOLLOW_UP_NEST_DEPTH = 4;

interface DropdownFollowUpDraftRow {
  whenOption: string;
  questionLabel: string;
  questionType: ChecklistFieldType;
  /** Lines for follow-up answer when `questionType` is `dropdown`. */
  followUpDropdownOptionsText: string;
  nestedFollowUpsEnabled: boolean;
  nestedRows: DropdownFollowUpDraftRow[];
  required: boolean;
  /** Stored value for the "Dynamic field value" select. */
  scrutinyLinkedDeedId: string;
  /** Master powering "Dynamic field value" (tag vs front_office). */
  dynamicValueSource: DynamicValueSource;
}

const FOLLOW_UP_ANSWER_TYPES: ChecklistFieldType[] = [
  "text",
  "dropdown",
  "date",
  "checkbox",
  "yes_no",
  "remarks",
];

function parseOptionsLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Branch keys for text-field follow-ups (filled vs empty). */
export const TEXT_FOLLOW_UP_FILLED = "__filled__";
export const TEXT_FOLLOW_UP_EMPTY = "__empty__";

/** Parent checklist fields that can define conditional follow-up branches. */
export function fieldTypeSupportsOptionFollowUps(type: ChecklistFieldType): boolean {
  return (
    type === "dropdown" ||
    type === "checkbox" ||
    type === "yes_no" ||
    type === "text"
  );
}

/** True when a field has at least one configured follow-up branch to show at runtime. */
export function fieldHasConfiguredFollowUps(field: ChecklistFieldDef): boolean {
  if (!fieldTypeSupportsOptionFollowUps(field.type)) return false;
  if (!field.optionFollowUps?.length) return false;
  return (
    !!field.dropdownFollowUpsEnabled ||
    field.optionFollowUps.some((fu) => String(fu.questionLabel ?? "").trim() !== "")
  );
}

/** Type + optional follow-up badges for checklist management field tables. */
export function renderChecklistFieldTypeBadges(field: ChecklistFieldDef): React.ReactNode {
  return (
    <>
      <span className="dvc-type-pill">{field.type}</span>
      {fieldHasConfiguredFollowUps(field) ? (
        <span
          className="dvc-fu-pill"
          title="Follow-up questions when an answer is selected"
        >
          Follow-up
        </span>
      ) : null}
    </>
  );
}

function questionTypeSupportsNestedFollowUps(type: ChecklistFieldType): boolean {
  return (
    type === "dropdown" ||
    type === "checkbox" ||
    type === "yes_no" ||
    type === "text"
  );
}

export function followUpBranchKeysForFieldType(
  type: ChecklistFieldType,
  options?: string[],
  dropdownOptionsText?: string
): string[] {
  if (type === "checkbox") return ["true", "false"];
  if (type === "yes_no") return ["Yes", "No"];
  if (type === "text") return [TEXT_FOLLOW_UP_FILLED, TEXT_FOLLOW_UP_EMPTY];
  if (type === "dropdown") {
    const parsed = options?.length
      ? options
      : parseOptionsLines(dropdownOptionsText ?? "");
    return parsed.length ? parsed : ["Option A", "Option B"];
  }
  return [];
}

function followUpRowsFromFieldDef(field: ChecklistFieldDef): DropdownFollowUpDraftRow[] {
  const branchOpts = followUpBranchKeysForFieldType(
    field.type,
    field.options,
    (field.options || []).join("\n")
  );
  return branchOpts.map((opt) => {
    const ex = field.optionFollowUps?.find((x) => x.whenOption === opt);
    return {
      whenOption: opt,
      questionLabel: ex?.questionLabel ?? "",
      questionType: ex?.questionType ?? "text",
      followUpDropdownOptionsText:
        (ex?.followUpOptions ?? []).join("\n") || "Option A\nOption B",
      nestedFollowUpsEnabled: !!(
        ex &&
        questionTypeSupportsNestedFollowUps(ex.questionType) &&
        ex.nestedFollowUpsEnabled
      ),
      nestedRows: ex ? nestedDraftRowsFromDefChildren(ex) : [],
      required: !!ex?.required,
      scrutinyLinkedDeedId: normalizeDeedIdForSelect(ex?.scrutinyLinkedDeedId),
      dynamicValueSource: (ex?.dynamicValueSource ?? "tag") as DynamicValueSource,
    };
  });
}

function buildOptionFollowUpsFromDraft(
  fieldId: string,
  type: ChecklistFieldType,
  enabled: boolean,
  followUpRows: DropdownFollowUpDraftRow[],
  dropdownOptionsText: string,
  oldField?: ChecklistFieldDef
): {
  optionFollowUps?: DropdownOptionFollowUpDef[];
  dropdownFollowUpsEnabled: boolean;
  options?: string[];
} {
  if (type === "dropdown") {
    const parsed = parseOptionsLines(dropdownOptionsText);
    const options = parsed.length ? parsed : oldField?.options;
    if (!fieldTypeSupportsOptionFollowUps(type) || !enabled) {
      return { dropdownFollowUpsEnabled: false, options };
    }
    const rows = syncFollowUpRowsToOptions(
      followUpBranchKeysForFieldType(type, options, dropdownOptionsText),
      followUpRows
    );
    const built = buildSavedOptionFollowUps(fieldId, rows, oldField);
    return built?.length
      ? { optionFollowUps: built, dropdownFollowUpsEnabled: true, options }
      : { dropdownFollowUpsEnabled: false, options };
  }
  if (!fieldTypeSupportsOptionFollowUps(type) || !enabled) {
    return { dropdownFollowUpsEnabled: false };
  }
  const rows = syncFollowUpRowsToOptions(
    followUpBranchKeysForFieldType(type, undefined, dropdownOptionsText),
    followUpRows
  );
  const built = buildSavedOptionFollowUps(fieldId, rows, oldField);
  return built?.length
    ? { optionFollowUps: built, dropdownFollowUpsEnabled: true }
    : { dropdownFollowUpsEnabled: false };
}

function followUpStateForFieldTypeChange(
  type: ChecklistFieldType,
  prev: {
    dropdownOptionsText: string;
    followUpRows: DropdownFollowUpDraftRow[];
    dropdownFollowUpsEnabled: boolean;
  }
): Pick<NewFieldDraft, "followUpRows" | "dropdownFollowUpsEnabled"> {
  if (!fieldTypeSupportsOptionFollowUps(type)) {
    return { dropdownFollowUpsEnabled: false, followUpRows: [] };
  }
  return {
    dropdownFollowUpsEnabled: prev.dropdownFollowUpsEnabled,
    followUpRows: syncFollowUpRowsToOptions(
      followUpBranchKeysForFieldType(type, undefined, prev.dropdownOptionsText),
      prev.followUpRows
    ),
  };
}

export function labelForParentFollowBranch(fieldType: ChecklistFieldType, whenOption: string): string {
  if (fieldType === "checkbox") {
    if (whenOption === "true") return "Checked";
    if (whenOption === "false") return "Unchecked";
  }
  if (fieldType === "text") {
    if (whenOption === TEXT_FOLLOW_UP_FILLED) return "Text filled";
    if (whenOption === TEXT_FOLLOW_UP_EMPTY) return "Text empty";
  }
  return whenOption;
}

export function textFollowUpBranchKey(value: string | undefined | null): string {
  return String(value ?? "").trim() ? TEXT_FOLLOW_UP_FILLED : TEXT_FOLLOW_UP_EMPTY;
}

export function triggerFollowBranchKey(field: ChecklistFieldDef, checklist: Record<string, string>): string {
  return triggerFollowBranchKeyForValue(field, checklist[field.id]);
}

/** Branch key for follow-ups when the parent answer is already known (e.g. repeatable row cell). */
export function triggerFollowBranchKeyForValue(
  field: ChecklistFieldDef,
  value: string | undefined
): string {
  const v = value ?? "";
  if (field.type === "checkbox") return v === "true" ? "true" : "false";
  if (field.type === "text") return textFollowUpBranchKey(v);
  if (field.type === "dropdown") {
    const tag = normalizeDeedIdForSelect(field.scrutinyLinkedDeedId);
    return dropdownFollowUpBranchKey(field, v, tag || undefined);
  }
  return v;
}

/** Storage key for a follow-up answer inside a repeatable deed/document row. */
export function repeatableRowFollowUpStorageKey(
  sectionId: string,
  groupId: string,
  rowIndex: number,
  followUpValueKey: string
): string {
  return `${repeatableEntriesKey(sectionId, groupId)}::${rowIndex}::fu::${followUpValueKey}`;
}

function initFollowUpDefaultValue(qt: ChecklistFieldType): string {
  if (qt === "checkbox") return "false";
  return "";
}

function initFollowUpKeysForDefTree(
  nodes: DropdownOptionFollowUpDef[] | undefined,
  out: Record<string, string>,
  storageKey: (valueKey: string) => string = (k) => k
): void {
  if (!nodes) return;
  nodes.forEach((fu) => {
    const sk = storageKey(fu.valueKey);
    if (out[sk] === undefined) {
      out[sk] = initFollowUpDefaultValue(fu.questionType);
    }
    initFollowUpKeysForDefTree(fu.nestedFollowUps, out, storageKey);
  });
}

/** Seed follow-up answer keys for each repeatable row (deed document entries, etc.). */
export function seedRepeatableRowFollowUpKeys(
  sections: ChecklistSectionDef[],
  repeatableValues: Record<string, Record<string, string>[]>,
  out: Record<string, string>
): void {
  sections.forEach((s) => {
    (s.repeatableGroups || []).forEach((g) => {
      if (g.bankProductId != null || g.naturePropertyKey != null) return;
      const rk = repeatableEntriesKey(s.id, g.id);
      const rows = repeatableValues[rk] || [];
      rows.forEach((_, rowIndex) => {
        g.fields.forEach((f) => {
          if (!fieldTypeSupportsOptionFollowUps(f.type) || !f.optionFollowUps?.length) return;
          initFollowUpKeysForDefTree(
            f.optionFollowUps,
            out,
            (vk) => repeatableRowFollowUpStorageKey(s.id, g.id, rowIndex, vk)
          );
        });
      });
    });
  });
}

function initFollowUpKeysForField(field: ChecklistFieldDef, out: Record<string, string>): void {
  if (!fieldTypeSupportsOptionFollowUps(field.type) || !field.optionFollowUps?.length) return;
  initFollowUpKeysForDefTree(field.optionFollowUps, out);
}

export function collectFollowUpValueKeys(nodes: DropdownOptionFollowUpDef[] | undefined): string[] {
  if (!nodes) return [];
  return nodes.flatMap((n) => [n.valueKey, ...collectFollowUpValueKeys(n.nestedFollowUps)]);
}

function ensureFollowUpDefaultsInChecklist(
  nodes: DropdownOptionFollowUpDef[] | undefined,
  checklist: Record<string, string>
): Record<string, string> {
  let next = { ...checklist };
  if (!nodes) return next;
  nodes.forEach((n) => {
    if (next[n.valueKey] === undefined) {
      next[n.valueKey] = initFollowUpDefaultValue(n.questionType);
    }
    next = ensureFollowUpDefaultsInChecklist(n.nestedFollowUps, next);
  });
  return next;
}

/** Add default keys for follow-up trees without overwriting saved answers. */
export function preserveFollowUpDefaultsInChecklist(
  master: ChecklistSectionDef[],
  checklist: Record<string, string>
): Record<string, string> {
  let next = { ...checklist };
  master.forEach((s) => {
    s.fields.forEach((f) => {
      if (f.optionFollowUps?.length) {
        next = ensureFollowUpDefaultsInChecklist(f.optionFollowUps, next);
      }
    });
  });
  return next;
}

function initChecklistSliceForFollowUpTree(
  nodes: DropdownOptionFollowUpDef[] | undefined,
  out: Record<string, string>
): void {
  if (!nodes) return;
  nodes.forEach((n) => {
    out[n.valueKey] = initFollowUpDefaultValue(n.questionType);
    initChecklistSliceForFollowUpTree(n.nestedFollowUps, out);
  });
}

function resetFollowUpSubtree(
  node: DropdownOptionFollowUpDef,
  checklist: Record<string, string>,
  storageKey: (valueKey: string) => string = (k) => k
): Record<string, string> {
  let next = { ...checklist };
  next[storageKey(node.valueKey)] = initFollowUpDefaultValue(node.questionType);
  (node.nestedFollowUps || []).forEach((ch) => {
    next = resetFollowUpSubtree(ch, next, storageKey);
  });
  return next;
}

export function branchKeyForFollowUpAnswer(
  node: DropdownOptionFollowUpDef,
  checklist: Record<string, string>,
  storageKey: (valueKey: string) => string = (k) => k
): string {
  const v = checklist[storageKey(node.valueKey)] ?? "";
  if (node.questionType === "checkbox") return v === "true" ? "true" : "false";
  if (node.questionType === "yes_no") return v === "Yes" ? "Yes" : v === "No" ? "No" : "";
  if (node.questionType === "text") return textFollowUpBranchKey(v);
  return v;
}

function applyNestedFollowUpClearForNode(
  node: DropdownOptionFollowUpDef,
  checklist: Record<string, string>,
  storageKey: (valueKey: string) => string = (k) => k
): Record<string, string> {
  if (!node.nestedFollowUpsEnabled || !node.nestedFollowUps?.length) return checklist;
  if (!questionTypeSupportsNestedFollowUps(node.questionType)) return checklist;
  let next = { ...checklist };
  const sub = branchKeyForFollowUpAnswer(node, next, storageKey);
  node.nestedFollowUps.forEach((ch) => {
    if (node.questionType === "dropdown" && !sub) {
      next = resetFollowUpSubtree(ch, next, storageKey);
    } else if (!sub || ch.whenOption !== sub) {
      next = resetFollowUpSubtree(ch, next, storageKey);
    } else {
      next = applyNestedFollowUpClearForNode(ch, next, storageKey);
    }
  });
  return next;
}

/** Reset inactive branches when the parent answer is known (repeatable rows). */
export function applyConditionalFollowUpClearForParentValue(
  field: ChecklistFieldDef,
  parentValue: string | undefined,
  checklist: Record<string, string>,
  storageKey: (valueKey: string) => string = (k) => k
): Record<string, string> {
  if (!fieldTypeSupportsOptionFollowUps(field.type) || !field.optionFollowUps?.length) {
    return checklist;
  }
  const selected = triggerFollowBranchKeyForValue(field, parentValue);
  let next = { ...checklist };
  field.optionFollowUps.forEach((fu) => {
    if (!selected || fu.whenOption !== selected) {
      next = resetFollowUpSubtree(fu, next, storageKey);
    } else {
      next = applyNestedFollowUpClearForNode(fu, next, storageKey);
    }
  });
  return next;
}

/** Reset inactive branches and nested answers for one checklist field with conditional follow-ups. */
function applyConditionalFollowUpClear(
  field: ChecklistFieldDef,
  checklist: Record<string, string>
): Record<string, string> {
  return applyConditionalFollowUpClearForParentValue(
    field,
    checklist[field.id],
    checklist
  );
}

export function reconcileAllFollowUpClears(
  master: ChecklistSectionDef[],
  checklist: Record<string, string>
): Record<string, string> {
  let next = { ...checklist };
  master.forEach((s) => {
    s.fields.forEach((f) => {
      if (f.optionFollowUps?.length && fieldTypeSupportsOptionFollowUps(f.type)) {
        next = applyConditionalFollowUpClear(f, next);
      }
    });
  });
  return next;
}

/** Reset inactive follow-up branches inside repeatable deed/document rows (preserves active branch answers). */
export function reconcileRepeatableRowFollowUpClears(
  master: ChecklistSectionDef[],
  repeatableValues: Record<string, Record<string, string>[]>,
  checklist: Record<string, string>
): Record<string, string> {
  let next = { ...checklist };
  master.forEach((s) => {
    (s.repeatableGroups || []).forEach((g) => {
      if (g.bankProductId != null || g.naturePropertyKey != null) return;
      const rk = repeatableEntriesKey(s.id, g.id);
      const rows = repeatableValues[rk] || [];
      rows.forEach((row, rowIndex) => {
        g.fields.forEach((f) => {
          if (!fieldTypeSupportsOptionFollowUps(f.type) || !f.optionFollowUps?.length) return;
          const scopedKey = (vk: string) =>
            repeatableRowFollowUpStorageKey(s.id, g.id, rowIndex, vk);
          next = applyConditionalFollowUpClearForParentValue(
            f,
            row[f.id],
            next,
            scopedKey
          );
        });
      });
    });
  });
  return next;
}

function nestedBranchKeysForDraftRow(row: DropdownFollowUpDraftRow): string[] {
  if (row.questionType === "checkbox") return ["true", "false"];
  if (row.questionType === "yes_no") return ["Yes", "No"];
  if (row.questionType === "text") return [TEXT_FOLLOW_UP_FILLED, TEXT_FOLLOW_UP_EMPTY];
  if (row.questionType === "dropdown") return parseOptionsLines(row.followUpDropdownOptionsText);
  return [];
}

function syncFollowUpRowsToOptions(
  options: string[],
  rows: DropdownFollowUpDraftRow[]
): DropdownFollowUpDraftRow[] {
  const byOpt = new Map(rows.map((r) => [r.whenOption, r]));
  return options.map((opt) => {
    const prev = byOpt.get(opt);
    const questionType = prev?.questionType ?? "text";
    const followUpDropdownOptionsText = prev?.followUpDropdownOptionsText ?? "Option A\nOption B";
    const nestedFollowUpsEnabled =
      questionTypeSupportsNestedFollowUps(questionType) && !!prev?.nestedFollowUpsEnabled;
    const nestedKeys = nestedBranchKeysForDraftRow({
      whenOption: opt,
      questionLabel: prev?.questionLabel ?? "",
      questionType,
      followUpDropdownOptionsText,
      nestedFollowUpsEnabled,
      nestedRows: prev?.nestedRows ?? [],
      required: prev?.required ?? false,
      scrutinyLinkedDeedId: prev?.scrutinyLinkedDeedId ?? "",
      dynamicValueSource: prev?.dynamicValueSource ?? "tag",
    });
    const nestedRows =
      nestedFollowUpsEnabled && nestedKeys.length
        ? syncFollowUpRowsToOptions(nestedKeys, prev?.nestedRows || [])
        : [];
    return {
      whenOption: opt,
      questionLabel: prev?.questionLabel ?? "",
      questionType,
      followUpDropdownOptionsText,
      nestedFollowUpsEnabled,
      nestedRows,
      required: prev?.required ?? false,
      scrutinyLinkedDeedId: prev?.scrutinyLinkedDeedId ?? "",
      dynamicValueSource: prev?.dynamicValueSource ?? "tag",
    };
  });
}

function replaceFollowUpNestedRows(
  rows: DropdownFollowUpDraftRow[],
  rowIndex: number,
  nested: DropdownFollowUpDraftRow[]
): DropdownFollowUpDraftRow[] {
  return rows.map((r, i) => (i === rowIndex ? { ...r, nestedRows: nested } : r));
}

function buildSavedBranches(
  fieldId: string,
  pathSuffix: string,
  rows: DropdownFollowUpDraftRow[],
  oldBranches: DropdownOptionFollowUpDef[] | undefined
): DropdownOptionFollowUpDef[] | undefined {
  const withLabel = rows.filter((r) => r.questionLabel.trim());
  if (!withLabel.length) return undefined;
  const prevBy = new Map((oldBranches || []).map((x) => [x.whenOption, x]));
  return withLabel.map((r, idx) => {
    const prev = prevBy.get(r.whenOption);
    const followUpOptions =
      r.questionType === "dropdown"
        ? (() => {
          const o = parseOptionsLines(r.followUpDropdownOptionsText);
          return o.length ? o : ["Option A", "Option B"];
        })()
        : undefined;
    const valueKey = prev?.valueKey ?? `${fieldId}_fu${pathSuffix}_${idx}`;
    let nestedFollowUps: DropdownOptionFollowUpDef[] | undefined;
    let nestedEnabled = false;
    if (questionTypeSupportsNestedFollowUps(r.questionType) && r.nestedFollowUpsEnabled) {
      const nk = nestedBranchKeysForDraftRow(r);
      const nestedDraft = syncFollowUpRowsToOptions(nk, r.nestedRows || []);
      nestedFollowUps = buildSavedBranches(
        fieldId,
        `${pathSuffix}_${idx}n`,
        nestedDraft,
        prev?.nestedFollowUps
      );
      nestedEnabled = !!(nestedFollowUps?.length);
    }
    return {
      whenOption: r.whenOption,
      questionLabel: r.questionLabel.trim(),
      questionType: r.questionType,
      valueKey,
      followUpOptions,
      nestedFollowUpsEnabled: nestedEnabled,
      nestedFollowUps: nestedFollowUps,
      required: r.required ? true : undefined,
      scrutinyLinkedDeedId: r.scrutinyLinkedDeedId.trim() || undefined,
      dynamicValueSource:
        r.scrutinyLinkedDeedId.trim() && r.dynamicValueSource === "front_office"
          ? "front_office"
          : undefined,
    };
  });
}

function buildSavedOptionFollowUps(
  fieldId: string,
  rows: DropdownFollowUpDraftRow[],
  oldField: ChecklistFieldDef | undefined
): DropdownOptionFollowUpDef[] | undefined {
  return buildSavedBranches(fieldId, "", rows, oldField?.optionFollowUps);
}

function nestedDraftRowsFromDefChildren(ex: DropdownOptionFollowUpDef): DropdownFollowUpDraftRow[] {
  if (!questionTypeSupportsNestedFollowUps(ex.questionType)) return [];
  if (!ex.nestedFollowUpsEnabled) return [];
  const keys =
    ex.questionType === "checkbox"
      ? ["true", "false"]
      : ex.questionType === "yes_no"
        ? ["Yes", "No"]
        : ex.questionType === "text"
          ? [TEXT_FOLLOW_UP_FILLED, TEXT_FOLLOW_UP_EMPTY]
          : ex.followUpOptions || [];
  const byWhen = new Map((ex.nestedFollowUps || []).map((n) => [n.whenOption, n]));
  return keys.map((k) => {
    const n = byWhen.get(k);
    return {
      whenOption: k,
      questionLabel: n?.questionLabel ?? "",
      questionType: n?.questionType ?? "text",
      followUpDropdownOptionsText: (n?.followUpOptions ?? []).join("\n") || "Option A\nOption B",
      nestedFollowUpsEnabled: !!(
        n &&
        questionTypeSupportsNestedFollowUps(n.questionType) &&
        n.nestedFollowUpsEnabled
      ),
      nestedRows: n ? nestedDraftRowsFromDefChildren(n) : [],
      required: !!n?.required,
      scrutinyLinkedDeedId: normalizeDeedIdForSelect(n?.scrutinyLinkedDeedId),
      dynamicValueSource: (n?.dynamicValueSource ?? "tag") as DynamicValueSource,
    };
  });
}

export function isChecklistAnswerFilled(type: ChecklistFieldType, val: string | undefined): boolean {
  const v = val ?? "";
  if (type === "checkbox") return v === "true";
  if (type === "yes_no") return v === "Yes" || v === "No";
  return !!(v && String(v).trim());
}

export function collectVisibleFollowUpValidationMissing(
  nodes: DropdownOptionFollowUpDef[] | undefined,
  parentBranchKey: string,
  parentQuestionType: ChecklistFieldType,
  checklist: Record<string, string>,
  pathPrefix: string,
  missing: string[],
  parentDisabled?: boolean,
  storageKey: (valueKey: string) => string = (k) => k
): void {
  if (parentDisabled) return;
  if (!nodes?.length) return;
  const fu = nodes.find((x) => x.whenOption === parentBranchKey);
  if (!fu?.questionLabel.trim()) return;
  const answerKey = storageKey(fu.valueKey);
  if (!!fu.required && !isChecklistAnswerFilled(fu.questionType, checklist[answerKey])) {
    missing.push(`${pathPrefix} → ${fu.questionLabel}`);
  }
  if (
    fu.nestedFollowUpsEnabled &&
    fu.nestedFollowUps?.length &&
    questionTypeSupportsNestedFollowUps(fu.questionType)
  ) {
    const subKey = branchKeyForFollowUpAnswer(fu, checklist, storageKey);
    if (fu.questionType === "dropdown" && !subKey) return;
    if (fu.questionType === "yes_no" && !subKey) return;
    if (fu.questionType === "text" && !subKey) return;
    collectVisibleFollowUpValidationMissing(
      fu.nestedFollowUps,
      subKey,
      fu.questionType,
      checklist,
      `${pathPrefix} → ${fu.questionLabel}`,
      missing,
      undefined,
      storageKey
    );
  }
}

export function emptyValuesFromMaster(sections: ChecklistSectionDef[]): Record<string, string> {
  const out: Record<string, string> = {};
  sections.forEach((s) => {
    s.fields.forEach((f) => {
      if (f.type === "checkbox") out[f.id] = "false";
      else out[f.id] = "";
      initFollowUpKeysForField(f, out);
    });
    (s.repeatableGroups || []).forEach((g) => {
      if (g.bankProductId == null && g.naturePropertyKey == null) return;
      g.fields.forEach((f) => {
        if (f.type === "checkbox") out[f.id] = "false";
        else out[f.id] = "";
        initFollowUpKeysForField(f, out);
      });
    });
  });
  return out;
}

/** Match `<option value={String(id)}>` when loading JSON/API numeric ids. */
export function normalizeDeedIdForSelect(id: string | number | undefined | null): string {
  if (id === undefined || id === null || id === "") return "";
  return String(id);
}

interface Props {
  history?: any;
  match?: any;
}

/**
 * Scrutiny tag option loaded from `autocomplete_tag.php` (same source the deed-template
 * "Scrutiny Table Content" mention field uses). `id` and `name` both hold the tag string.
 */
interface DeedTemplateScrutinyRow {
  id: string;
  name: string;
}

const GENERAL_DETAIL_DYNAMIC_TAGS: DeedTemplateScrutinyRow[] = [
  { id: "ownerAadharName", name: "ownerAadharName — General Detail" },
  { id: "borrowerAadharName", name: "borrowerAadharName — General Detail" },
  { id: "coBorrowerAadharName", name: "coBorrowerAadharName — General Detail" },
  { id: "addressofProperty", name: "addressofProperty — General Detail Address" },
  { id: "AppIdverfy", name: "AppIdverfy — General Detail App ID" },
  { id: "natureofProperty", name: "natureofProperty — General Detail Nature" },
];

const MISSING_LOD_DYNAMIC_TAGS: DeedTemplateScrutinyRow[] = [
  { id: "fileNo", name: "fileNo — Missing LOD" },
  { id: "fileName", name: "fileName — Missing LOD" },
  { id: "totalSheets", name: "totalSheets — Missing LOD" },
  { id: "availableSheets", name: "availableSheets — Missing LOD" },
  { id: "missingSheets", name: "missingSheets — Missing LOD" },
];

/** Per-document fields on `doc_scurty` (Scrutiny Form — left panel). */
const DOCUMENT_SCRUTINY_DYNAMIC_TAGS: DeedTemplateScrutinyRow[] = [
  { id: "obtined", name: "obtined — To Be Obtained" },
  { id: "org_pres", name: "org_pres — Now Scrutinized" },
  { id: "remark", name: "remark — Document Remark" },
  { id: "favourOf", name: "favourOf — Favour of (Favour/Claimant)" },
  { id: "executedBy", name: "executedBy — Executed by (Executant)" },
];

export const DOCUMENT_SCRUTINY_DYNAMIC_TAG_IDS = new Set(
  DOCUMENT_SCRUTINY_DYNAMIC_TAGS.map((t) => t.id)
);

const ORIGINAL_PRESENT_DROPDOWN_OPTIONS_LINES = "Original\nXerox\nCertified Copy\nPhotocopy";

function defaultLabelForOriginalPresentTag(tagId: string): string {
  const id = normalizeDeedIdForSelect(tagId);
  if (id === "obtined") return "To Be Obtained";
  if (id === "org_pres") return "Now Scrutinized";
  return "";
}

function draftPatchForOriginalPresentScrutinyTag(
  tagId: string,
  currentDraft: { label?: string; layout?: ChecklistFieldLayout }
): {
  type: ChecklistFieldType;
  layout: ChecklistFieldLayout;
  label: string;
  dropdownOptionsText: string;
  dropdownFollowUpsEnabled: boolean;
  followUpRows: DropdownFollowUpDraftRow[];
  showVerifyCheckbox: boolean;
} | Record<string, never> {
  const id = normalizeDeedIdForSelect(tagId);
  if (!DOCUMENT_SCRUTINY_DYNAMIC_TAG_IDS.has(id) || (id !== "obtined" && id !== "org_pres")) {
    return {};
  }
  const defaultLabel = defaultLabelForOriginalPresentTag(id);
  return {
    type: "dropdown",
    layout: currentDraft.layout ?? "half",
    label: (currentDraft.label ?? "").trim() || defaultLabel,
    dropdownOptionsText: ORIGINAL_PRESENT_DROPDOWN_OPTIONS_LINES,
    dropdownFollowUpsEnabled: false,
    followUpRows: [],
    showVerifyCheckbox: false,
  };
}

/** Multi-set schedule rows from document scrutiny (`tbl_scrutiny_survey`). */
const DOCUMENT_SCHEDULE_DYNAMIC_TAGS: DeedTemplateScrutinyRow[] = [
  {
    id: "dynamic-schedule.schedule",
    name: "dynamic-schedule.schedule — Schedule (per set)",
  },
  {
    id: "dynamic-schedule.alloted_to",
    name: "dynamic-schedule.alloted_to — Allotted To (per set)",
  },
  {
    id: "dynamic-schedule.survey_no",
    name: "dynamic-schedule.survey_no — Survey No (per set)",
  },
  {
    id: "dynamic-schedule.extent",
    name: "dynamic-schedule.extent — Extent (per set)",
  },
];

export const DOCUMENT_SCHEDULE_DYNAMIC_TAG_IDS = new Set(
  DOCUMENT_SCHEDULE_DYNAMIC_TAGS.map((t) => t.id)
);

const ADDON_FEATURE_DYNAMIC_TAGS: DeedTemplateScrutinyRow[] = [
  {
    id: "thereSuchPropertyOwnerName",
    name: "thereSuchPropertyOwnerName — Addon Feature (Property Owner Name)",
  },
  { id: "totalExtent", name: "totalExtent — Addon Feature" },
  { id: "extentUnit", name: "extentUnit — Addon Feature (Select Unit)" },
  { id: "takeoverBank", name: "takeoverBank — Addon Feature" },
  { id: "finalRemarks", name: "finalRemarks — Addon Feature" },
  { id: "preDisbursementDate", name: "preDisbursementDate — Addon Feature Pre Disbursement" },
  {
    id: "preDisbursementDocumentNumber",
    name: "preDisbursementDocumentNumber — Addon Feature Pre Disbursement",
  },
  {
    id: "preDisbursementParticularDeed",
    name: "preDisbursementParticularDeed — Addon Feature Pre Disbursement",
  },
  {
    id: "preDisbursementNatureDeed",
    name: "preDisbursementNatureDeed — Addon Feature Pre Disbursement",
  },
  { id: "postDisbursementDate", name: "postDisbursementDate — Addon Feature Post Disbursement" },
  {
    id: "postDisbursementDocumentNumber",
    name: "postDisbursementDocumentNumber — Addon Feature Post Disbursement",
  },
  {
    id: "postDisbursementParticularDeed",
    name: "postDisbursementParticularDeed — Addon Feature Post Disbursement",
  },
  {
    id: "postDisbursementNatureDeed",
    name: "postDisbursementNatureDeed — Addon Feature Post Disbursement",
  },
];

export const PRE_DISBURSEMENT_DYNAMIC_TAG_IDS = new Set([
  "preDisbursementDate",
  "preDisbursementDocumentNumber",
  "preDisbursementParticularDeed",
  "preDisbursementNatureDeed",
]);

export const POST_DISBURSEMENT_DYNAMIC_TAG_IDS = new Set([
  "postDisbursementDate",
  "postDisbursementDocumentNumber",
  "postDisbursementParticularDeed",
  "postDisbursementNatureDeed",
]);

export const ADDON_SCALAR_DYNAMIC_TAG_IDS = new Set([
  "thereSuchPropertyOwnerName",
  "totalExtent",
  "extentUnit",
  "takeoverBank",
  "finalRemarks",
]);

/** Survey Verification tags without a gd* hierarchy duplicate (flat-only / register / patta). */
const SURVEY_VERIFICATION_DYNAMIC_TAGS: DeedTemplateScrutinyRow[] = [
  { id: "pattaissuedverfy", name: "pattaissuedverfy — Survey Verification" },
  { id: "Corelation", name: "Corelation — Survey Verification Title Deed" },
  { id: "proceedingpatta", name: "proceedingpatta — Survey Verification Title Deed" },
  { id: "tslrNo", name: "tslrNo — Survey Verification Register" },
  { id: "aRegisterMeanscompare", name: "aRegisterMeanscompare — Survey Verification Register" },
].filter((row) => !SURVEY_VERIFICATION_TAGS_OVERLAPPING_GD.has(String(row.id)));

/**
 * Resolved label for the "Dynamic field value" column.
 * - `tag`           → look up in scrutiny tag list (`autocomplete_tag.php`).
 * - `front_office`  → look up in front-office master list.
 */
function formatDynamicFieldValueLabel(
  scrutinyLinkedDeedId: string | number | undefined | null,
  tags: DeedTemplateScrutinyRow[],
  source: DynamicValueSource | undefined,
  frontOfficeFields: FrontOfficeFieldRow[]
): string | null {
  const id = normalizeDeedIdForSelect(scrutinyLinkedDeedId);
  if (!id) return null;
  if (source === "front_office") {
    const fo = frontOfficeFields.find((f) => f.id === id);
    return fo?.name ?? null;
  }
  const row = tags.find((d) => String(d.id) === id);
  return row?.name ?? null;
}

function formatDeedMasterLabel(
  deedMasterId: string | number | undefined | null,
  deeds: DeedMasterRow[]
): string | null {
  const id = normalizeDeedIdForSelect(deedMasterId);
  if (!id) return null;
  const row = deeds.find((d) => String(d.id) === id);
  return row?.name ?? null;
}

function gateFieldIdForDeed(deedId: number): string {
  return `f_gate_deed_${deedId}`;
}

function isDeedGateFieldDef(
  field: ChecklistFieldDef
): field is ChecklistFieldDef & { deedMasterId: string | number } {
  return (
    field.type === "yes_no" &&
    field.deedMasterId != null &&
    field.deedMasterId !== "" &&
    field.id === gateFieldIdForDeed(Number(field.deedMasterId))
  );
}

function deedGroupIdForDeed(deedId: number): string {
  return `rg_deed_${deedId}`;
}

function mapInputMasterToChecklistField(
  input: InputMasterFieldRow,
  order: number,
  deedMasterId: number,
  uniqueSuffix: string
): ChecklistFieldDef {
  const safeName = (input.name || `inp_${input.id}`).replace(/[^a-zA-Z0-9_]/g, "_");
  const id = `f_deed${deedMasterId}_${safeName}_${uniqueSuffix}`;
  let type: ChecklistFieldType = "text";
  let options: string[] | undefined;
  if (input.type === "checkbox" || input.type === "radio") {
    type = "yes_no";
  } else if (input.type === "select" && input.optionList && input.optionList.length) {
    type = "dropdown";
    options = input.optionList.map((o) => o.name);
  } else if (input.type === "textarea") {
    type = "remarks";
  }
  const layout: ChecklistFieldLayout = type === "remarks" ? "full" : "half";
  return {
    id,
    label: input.label,
    type,
    order,
    layout,
    options,
    deedMasterId,
    scrutinyLinkedDeedId: type === "yes_no" ? undefined : safeName,
  };
}

export function sectionFlowMode(section: ChecklistSectionDef): ChecklistSectionFlowMode {
  if (section.flowMode) return section.flowMode;
  if (ecLinkedGroups(section).length > 0) return "ec_verification";
  if (surveyLinkedGroups(section).length > 0 || surveyHierarchyLinkedGroups(section).length > 0) {
    return "survey_verification";
  }
  if (bankLinkedGroups(section).length > 0) return "bank_product";
  return deedLinkedGroups(section).length > 0 ? "document_types" : "checklist";
}

/** Any answer field except Yes/No gates may use the runtime verification checkbox. */
export function fieldTypeAllowsVerifyCheckbox(fieldType: ChecklistFieldType): boolean {
  return fieldType !== "yes_no";
}

/** @deprecated flow is ignored — verification checkbox is allowed on all section flows. */
export function sectionAllowsFieldVerifyCheckbox(
  _flow: ChecklistSectionFlowMode,
  fieldType: ChecklistFieldType
): boolean {
  return fieldTypeAllowsVerifyCheckbox(fieldType);
}

export function sectionGateFields(section: ChecklistSectionDef): ChecklistFieldDef[] {
  return section.fields.filter((f) => f.type === "yes_no" && f.deedMasterId != null && f.deedMasterId !== "");
}

export function initFieldVerified(sections: ChecklistSectionDef[]): Record<string, string> {
  const out: Record<string, string> = {};
  const seed = (f: ChecklistFieldDef) => {
    if (f.showVerifyCheckbox) out[f.id] = "false";
  };
  sections.forEach((s) => {
    s.fields.forEach(seed);
    (s.repeatableGroups || []).forEach((g) => g.fields.forEach(seed));
  });
  return out;
}

export function deedLinkedGroups(section: ChecklistSectionDef): ChecklistRepeatableGroupDef[] {
  return (section.repeatableGroups || []).filter((g) => g.deedMasterId != null);
}

export function bankLinkedGroups(section: ChecklistSectionDef): ChecklistRepeatableGroupDef[] {
  return (section.repeatableGroups || []).filter((g) => g.bankProductId != null);
}

/**
 * `front_office_get.php` returns the file's `tbl_bank_product.id` as `product_id`
 * (`get_product_id_front_office` reads `TBL_FRONTOFFICE.bank_product`).
 */
export function resolveFileBankProductId(
  frontOfficeRow: Record<string, string> | null | undefined
): number | null {
  if (!frontOfficeRow) return null;
  const raw =
    frontOfficeRow.product_id ??
    frontOfficeRow.bank_product ??
    frontOfficeRow.bankProductId ??
    "";
  const n = Number(String(raw).trim());
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Bank-product groups on the master that apply to this file's bank product. */
export function bankGroupsVisibleForFile(
  section: ChecklistSectionDef,
  fileBankProductId: number | null | undefined
): ChecklistRepeatableGroupDef[] {
  const groups = bankLinkedGroups(section);
  if (fileBankProductId == null || !Number.isFinite(Number(fileBankProductId))) {
    return [];
  }
  const target = Number(fileBankProductId);
  return groups.filter(
    (g) => g.bankProductId != null && Number(g.bankProductId) === target
  );
}

export function bankProductSectionFieldCount(
  section: ChecklistSectionDef,
  fileBankProductId: number | null | undefined
): number {
  return bankGroupsVisibleForFile(section, fileBankProductId).reduce(
    (n, g) => n + g.fields.length,
    0
  );
}

function bankGroupIdForProduct(bankProductId: number): string {
  return `rg_bank_${bankProductId}`;
}

function formatBankProductLabelFromRow(row: BankProductRow): string {
  const bank = (row.bank_name || "").trim();
  const product = (row.product_name || "").trim();
  if (bank && product) return `${bank} (${product})`;
  return bank || product || `Bank product #${row.id}`;
}

function formatBankProductLabel(
  id: string | number | undefined | null,
  list: BankProductRow[]
): string {
  const n = Number(id);
  if (!n) return "";
  const row = list.find((b) => b.id === n);
  return row ? formatBankProductLabelFromRow(row) : `Bank product #${n}`;
}

/** Runtime hint from optional `labelTag` configured in master setup. */
export function labelTagHintForField(
  field: ChecklistFieldDef
): { text: string; title: string } | null {
  const tag = String(field.labelTag ?? "").trim();
  if (!tag) return null;
  const text = tag.startsWith("(") ? ` ${tag}` : ` (${tag})`;
  return { text, title: tag };
}

export function fieldIdsInDeedGroups(section: ChecklistSectionDef): Set<string> {
  const ids = new Set<string>();
  deedLinkedGroups(section).forEach((g) => g.fields.forEach((f) => ids.add(f.id)));
  bankLinkedGroups(section).forEach((g) => g.fields.forEach((f) => ids.add(f.id)));
  surveyLinkedGroups(section).forEach((g) => g.fields.forEach((f) => ids.add(f.id)));
  return ids;
}

interface NewFieldDraft {
  sectionId: string;
  label: string;
  type: ChecklistFieldType;
  required: boolean;
  disabled: boolean;
  documentMapKey: string;
  labelTag: string;
  dropdownOptionsText: string;
  layout: ChecklistFieldLayout;
  dropdownFollowUpsEnabled: boolean;
  followUpRows: DropdownFollowUpDraftRow[];
  showVerifyCheckbox: boolean;
  /** Stored value for "Dynamic field value" select. */
  scrutinyLinkedDeedId: string;
  /** Which master powers "Dynamic field value". */
  dynamicValueSource: DynamicValueSource;
}

interface DeedGroupFieldDraft {
  label: string;
  labelTag: string;
  type: ChecklistFieldType;
  layout: ChecklistFieldLayout;
  required: boolean;
  disabled: boolean;
  showVerifyCheckbox: boolean;
  scrutinyLinkedDeedId: string;
  /** Which master powers "Dynamic field value". */
  dynamicValueSource: DynamicValueSource;
  dropdownOptionsText: string;
  dropdownFollowUpsEnabled: boolean;
  followUpRows: DropdownFollowUpDraftRow[];
  /** When set, add form updates this field instead of creating a new one. */
  editingFieldId?: string;
}

function deedGroupDraftKey(sectionId: string, groupId: string): string {
  return `${sectionId}::${groupId}`;
}

const defaultDeedGroupFieldDraft = (): DeedGroupFieldDraft => ({
  label: "",
  labelTag: "",
  type: "text",
  layout: "third",
  required: false,
  disabled: false,
  showVerifyCheckbox: false,
  scrutinyLinkedDeedId: "",
  dynamicValueSource: "tag",
  dropdownOptionsText: "Option A\nOption B",
  dropdownFollowUpsEnabled: false,
  followUpRows: [],
});

interface FieldEditDraft {
  label: string;
  labelTag: string;
  type: ChecklistFieldType;
  required: boolean;
  disabled: boolean;
  documentMapKey: string;
  dropdownOptionsText: string;
  layout: ChecklistFieldLayout;
  dropdownFollowUpsEnabled: boolean;
  followUpRows: DropdownFollowUpDraftRow[];
  showVerifyCheckbox: boolean;
  /** Stored value for "Dynamic field value" select. */
  scrutinyLinkedDeedId: string;
  /** Which master powers "Dynamic field value". */
  dynamicValueSource: DynamicValueSource;
}

/** Master row used to populate the "Front office" option of the Dynamic field value select. */
export interface FrontOfficeFieldRow {
  /**
   * Stable id stored in `scrutinyLinkedDeedId` when `dynamicValueSource === "front_office"`.
   * For catalog-bound rows this is the exact key used by `front_office_get.php` /
   * `TBL_FRONTOFFICE` (e.g. `borrower_name`, `app_id`).
   */
  id: string;
  /** Label shown in the dropdown and master table. */
  name: string;
}

/**
 * Catalog of Front Office form fields linked to their `TBL_FRONTOFFICE` columns
 * (the same keys returned by `front_office_get.php` and consumed by the Front Office
 * page — `lawhands_front_end/src/components/file/AddFile.tsx` + `BankForm.tsx`).
 * `id` is the DB column key, `name` is the label shown on the Front Office form.
 *
 * Adding a row to the "Front office master" tab uses this catalog so that at
 * runtime the dynamic checklist can resolve a value by `id`, e.g.
 *   response.data[0].arr[0][row.id]   // from front_office_get.php
 */
export const FRONT_OFFICE_CATALOG: FrontOfficeFieldRow[] = [
  { id: "bank_private", name: "Bank or Private" },
  { id: "doctype", name: "Type of Loan" },
  { id: "opinion_type", name: "File Type" },
  { id: "lawhands_branch", name: "Lawhands Branch" },
  { id: "bank_name", name: "Bank Name" },
  { id: "product_id", name: "Product" },
  { id: "branch", name: "Bank Branch" },
  { id: "borrower_name", name: "Borrower Name" },
  { id: "property_owner_name", name: "Property Owner Name" },
  { id: "app_id", name: "App Id" },
  { id: "contact_number", name: "LH Branch Contact Number" },
  { id: "loan_amount", name: "Loan Amount" },
  { id: "coApplicant", name: "Co-Applicant" },
  { id: "propertyAddress", name: "Property Address" },
  { id: "file_initiation_type", name: "File Initiation Received via" },
  { id: "mailSubject", name: "Mail Subject" },
];

/**
 * Front office catalog keys populated from title deed info (not TBL_FRONTOFFICE).
 * Checklist prefill reads them; save writes them back via dedicated handlers.
 */
export const FRONT_OFFICE_TITLE_DEED_SOURCE_IDS = new Set<string>([
  "property_owner_name",
]);

/** Lowercased label -> catalog row, used by the free-text migration. */
const FRONT_OFFICE_CATALOG_BY_NAME: Record<string, FrontOfficeFieldRow> = (() => {
  const map: Record<string, FrontOfficeFieldRow> = {};
  FRONT_OFFICE_CATALOG.forEach((row) => {
    map[row.name.toLowerCase()] = row;
  });
  return map;
})();

interface State {
  master: ChecklistSectionDef[];
  checklistValues: Record<string, string>;
  /** Per-field verification checkbox (General Verification style), keyed by field id. */
  fieldVerified: Record<string, string>;
  /** Row arrays keyed by `sectionId::groupId`. */
  repeatableValues: Record<string, Record<string, string>[]>;
  /** When `collapsible`, whether section body is expanded. */
  sectionUiOpen: Record<string, boolean>;
  newFieldDraft: NewFieldDraft;
  newSectionTitle: string;
  newSectionFlowMode: ChecklistSectionFlowMode;
  /** Selected deed master id when adding a document type inside a section. */
  documentTypeDraftBySection: Record<string, string>;
  /** Draft for manually adding a field inside a deed repeatable group (`sectionId::groupId`). */
  deedGroupFieldDrafts: Record<string, DeedGroupFieldDraft>;
  editingFieldKey: string | null;
  fieldEditDraft: FieldEditDraft;
  activeTab: string;
  validationMessages: string[];
  saveBanner: { variant: "success" | "danger"; text: string } | null;
  /**
   * Scrutiny tag options from `autocomplete_tag.php` (same source as the deed template
   * "Scrutiny Table Content" @-mention field). Powers the "Dynamic field value" selects.
   */
  deedTemplatesForScrutiny: DeedTemplateScrutinyRow[];
  /**
   * Template-manager dynamic fields from `input_master_get.php` (the same list shown
   * in Add Deed Template → Dynamic Fields). These are merged into the "Dynamic field value"
   * dropdown so checklist fields can bind to either a scrutiny tag or an input-master key.
   */
  inputMasterDynamicFields: DeedTemplateScrutinyRow[];
  /** Per-label measurement direction tags from `measure_get.php`. */
  measurementDirectionDynamicTags: DeedTemplateScrutinyRow[];
  /** Document types from `deed_master_get.php` (Scrutiny Form). */
  deedMasterList: DeedMasterRow[];
  /** Bank products from `bank_product_get.php`. */
  bankProductList: BankProductRow[];
  /** Selected bank-product id when adding inside a section. */
  bankProductDraftBySection: Record<string, string>;
  /** Selected nature-of-property key when adding inside a survey verification section. */
  naturePropertyDraftBySection: Record<string, string>;
  surveyHierarchyDraftBySection: Record<string, string>;
  /** Loading `input_master_get.php` for bulk add. */
  deedFieldsLoading: boolean;
  /**
   * Front-office catalog rows currently exposed to the dynamic checklist.
   * Each row's `id` is a `TBL_FRONTOFFICE` column key (see {@link FRONT_OFFICE_CATALOG}).
   */
  frontOfficeFields: FrontOfficeFieldRow[];
  /** Catalog id (TBL_FRONTOFFICE column key) currently selected in the picker. */
  newFrontOfficeFieldName: string;
  /** True while `dynamic_checklist_master_get.php` is in flight. */
  masterLoading: boolean;
  /** True while `dynamic_checklist_master_save.php` is in flight. */
  masterSaving: boolean;
  /** True while `front_office_master_get.php` is in flight. */
  frontOfficeMasterLoading: boolean;
  /** True while `front_office_master_save.php` is in flight. */
  frontOfficeMasterSaving: boolean;
  /** Master setup tab save/load result banner (sits at the top of the tab). */
  masterSaveBanner: { variant: "success" | "danger"; text: string } | null;
  /** JSON snapshot of last loaded/saved `master` — used to detect unsaved master edits. */
  masterSavedSnapshot: string;
  /** Front office master tab save/load result banner. */
  frontOfficeSaveBanner: { variant: "success" | "danger"; text: string } | null;
  /** Viewport position for the fixed unsaved banner (measured from layout chrome). */
  masterUnsavedBarInset: { top: number; left: number; width: number; height: number } | null;
}

type DeedFieldsLoadingStateUpdate = Pick<
  State,
  | "master"
  | "checklistValues"
  | "repeatableValues"
  | "deedFieldsLoading"
  | "documentTypeDraftBySection"
>;

type BankProductSectionStateUpdate = Pick<
  State,
  "master" | "repeatableValues" | "bankProductDraftBySection"
>;

type BankProductRemoveStateUpdate = Pick<
  State,
  "master" | "checklistValues" | "repeatableValues"
>;

type RepeatableRowStateUpdate = Pick<
  State,
  "repeatableValues" | "validationMessages" | "saveBanner"
>;

type RepeatableCellStateUpdate = Pick<
  State,
  "repeatableValues" | "validationMessages" | "saveBanner"
>;

type MoveSectionStateUpdate = Pick<State, "master">;

type RemoveDocumentTypeStateUpdate = Pick<
  State,
  | "master"
  | "checklistValues"
  | "repeatableValues"
  | "fieldVerified"
  | "editingFieldKey"
  | "deedGroupFieldDrafts"
>;

type DeedGroupFieldAddBankStateUpdate = Pick<
  State,
  "master" | "checklistValues" | "deedGroupFieldDrafts"
>;

type DeedGroupFieldAddDeedStateUpdate = Pick<
  State,
  "master" | "checklistValues" | "deedGroupFieldDrafts" | "repeatableValues"
>;

type DeedGroupFieldEditStateUpdate = Pick<State, "master" | "deedGroupFieldDrafts" | "checklistValues">;

function finishDeedFieldsLoading(prev: State): DeedFieldsLoadingStateUpdate {
  return {
    master: prev.master,
    checklistValues: prev.checklistValues,
    repeatableValues: prev.repeatableValues,
    documentTypeDraftBySection: prev.documentTypeDraftBySection,
    deedFieldsLoading: false,
  };
}

const defaultNewFieldDraft = (sectionId: string, flowMode: ChecklistSectionFlowMode = "checklist"): NewFieldDraft => ({
  sectionId,
  label: "",
  type: "text",
  required: false,
  disabled: false,
  documentMapKey: "",
  labelTag: "",
  dropdownOptionsText: "Option A\nOption B",
  layout: (flowMode === "checklist" ? "full" : "half") as ChecklistFieldLayout,
  dropdownFollowUpsEnabled: false,
  followUpRows: [],
  showVerifyCheckbox: false,
  scrutinyLinkedDeedId: "",
  dynamicValueSource: "tag",
});

const emptyFieldEditDraft = (): FieldEditDraft => ({
  label: "",
  labelTag: "",
  type: "text",
  required: false,
  disabled: false,
  documentMapKey: "",
  dropdownOptionsText: "",
  layout: "half",
  dropdownFollowUpsEnabled: false,
  followUpRows: [],
  showVerifyCheckbox: false,
  scrutinyLinkedDeedId: "",
  dynamicValueSource: "tag",
});

const FIELD_TYPES_RUNTIME: ChecklistFieldType[] = [
  "text",
  "dropdown",
  "date",
  "checkbox",
  "yes_no",
  "remarks",
];

/** Scoped light theme (Bootstrap 4–friendly). */
export const DVC_CSS = `
.dvc-root {
  --dvc-surface: #ffffff;
  --dvc-ink: #334155;
  --dvc-muted: #64748b;
  --dvc-accent: #3b82f6;
  --dvc-accent-soft: rgba(59, 130, 246, 0.14);
  --dvc-accent-faint: rgba(59, 130, 246, 0.06);
  --dvc-border: rgba(100, 116, 139, 0.14);
  --dvc-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 28px rgba(15, 23, 42, 0.06);
  --dvc-radius: 18px;
  --dvc-radius-sm: 14px;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: var(--dvc-ink);
  letter-spacing: 0.01em;
}
.dvc-root .dvc-hero {
  position: relative;
  overflow: hidden;
  border-radius: var(--dvc-radius);
  padding: 1.65rem 1.85rem;
  color: var(--dvc-ink);
  border: 1px solid rgba(255, 255, 255, 0.85);
  box-shadow: var(--dvc-shadow), 0 0 0 1px rgba(148, 163, 184, 0.12) inset;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 45%, rgba(241, 245, 249, 0.95) 100%);
}
.dvc-root .dvc-hero::before {
  content: "";
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 90% 70% at 100% 0%, rgba(186, 230, 253, 0.55), transparent 52%),
    radial-gradient(ellipse 70% 55% at 0% 100%, rgba(204, 251, 241, 0.45), transparent 48%),
    radial-gradient(ellipse 50% 40% at 80% 80%, rgba(224, 231, 255, 0.35), transparent 45%);
  pointer-events: none;
}
.dvc-root .dvc-hero-inner { position: relative; z-index: 1; }
.dvc-root .dvc-hero h4 {
  font-weight: 700;
  letter-spacing: -0.025em;
  color: #1e293b;
}
.dvc-root .dvc-hero .dvc-hero-lead {
  color: #64748b;
  font-weight: 400;
}
.dvc-root .dvc-hero-badge {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  background: linear-gradient(180deg, #e0f2fe 0%, #dbeafe 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  color: #1d4ed8;
  margin-bottom: 0.65rem;
}
.dvc-root .dvc-tabs.nav-tabs {
  border-bottom: 1px solid var(--dvc-border);
  gap: 0.2rem;
}
.dvc-root .dvc-tabs .nav-link {
  border: none;
  border-radius: var(--dvc-radius-sm) var(--dvc-radius-sm) 0 0;
  color: var(--dvc-muted);
  font-weight: 600;
  padding: 0.6rem 1.1rem;
  margin-bottom: -1px;
  transition: color 0.15s ease, background 0.15s ease;
}
.dvc-root .dvc-tabs .nav-link:hover {
  color: var(--dvc-accent);
  background: var(--dvc-accent-faint);
}
.dvc-root .dvc-tabs .nav-link.active {
  color: #1e40af;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid var(--dvc-border);
  border-bottom-color: transparent;
  box-shadow: 0 -2px 12px rgba(15, 23, 42, 0.04);
}
.dvc-root .dvc-card {
  border-radius: var(--dvc-radius-sm) !important;
  border: 1px solid var(--dvc-border) !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  background: rgba(255, 255, 255, 0.88) !important;
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.dvc-root .dvc-card:hover {
  box-shadow: 0 6px 28px rgba(15, 23, 42, 0.07);
  border-color: rgba(148, 163, 184, 0.22) !important;
}
.dvc-root .dvc-card-header-gradient {
  background: linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 55%, #dbeafe 100%) !important;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18) !important;
  color: #0f172a !important;
  font-weight: 600;
  letter-spacing: 0.01em;
}
.dvc-root .dvc-card-header-subtle {
  background: linear-gradient(180deg, #fafbfc 0%, #f1f5f9 100%) !important;
  border-bottom: 1px solid var(--dvc-border) !important;
  font-weight: 600;
  color: #334155;
}
.dvc-root .dvc-alert-intro {
  border-radius: var(--dvc-radius-sm) !important;
  border: 1px solid var(--dvc-border) !important;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%) !important;
  border-left: 4px solid #7dd3fc !important;
  box-shadow: 0 2px 14px rgba(15, 23, 42, 0.035);
}
.dvc-root .dvc-process-card {
  border-radius: var(--dvc-radius-sm) !important;
  border: 1px solid var(--dvc-border) !important;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%) !important;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.dvc-root .dvc-process-card:hover {
  border-color: rgba(59, 130, 246, 0.22) !important;
  box-shadow: 0 2px 16px rgba(59, 130, 246, 0.06);
}
.dvc-root .dvc-main-sections-card > .card-header {
  padding: 1rem 1.25rem !important;
}
.dvc-root .dvc-main-sections-card .dvc-sections-title {
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #0f172a;
}
.dvc-root .dvc-main-sections-card .dvc-sections-sub {
  font-size: 0.78rem;
  color: #64748b;
  margin-top: 0.15rem;
}
.dvc-root .dvc-master-toolbar {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid var(--dvc-border);
  border-radius: var(--dvc-radius-sm);
  padding: 1rem 1.15rem;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
}
.dvc-root .dvc-master-toolbar-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #64748b;
  margin-bottom: 0.45rem;
}
.dvc-root .dvc-master-toolbar .dvc-toolbar-action-btn {
  width: auto;
  min-width: 0;
  white-space: nowrap;
  flex-shrink: 0;
}
.dvc-root .dvc-section-card {
  width: 100%;
  max-width: 100%;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.045), 0 8px 28px rgba(15, 23, 42, 0.04) !important;
  border-color: rgba(148, 163, 184, 0.18) !important;
}
.dvc-root .dvc-section-card .card-body.dvc-field-table-wrap {
  width: 100%;
  max-width: 100%;
}
.dvc-root .dvc-section-card-header {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%) !important;
  border-bottom: 1px solid rgba(148, 163, 184, 0.16) !important;
}
.dvc-root .dvc-order-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2.25rem;
  height: 2.25rem;
  padding: 0 0.35rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.85rem;
  color: #1d4ed8;
  background: linear-gradient(145deg, #dbeafe 0%, #eff6ff 100%);
  border: 1px solid rgba(59, 130, 246, 0.22);
  box-shadow: 0 1px 2px rgba(37, 99, 235, 0.06);
  flex-shrink: 0;
}
.dvc-root .dvc-section-title-input {
  font-size: 1rem !important;
  font-weight: 600 !important;
  color: #0f172a !important;
  letter-spacing: -0.015em;
  border-radius: 8px !important;
  padding: 0.35rem 0.5rem !important;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}
.dvc-root .dvc-section-title-input:focus {
  background: #fff !important;
  box-shadow: 0 0 0 3px var(--dvc-accent-soft) !important;
}
.dvc-root .dvc-field-table-wrap .dvc-field-table thead th {
  border-bottom: 2px solid rgba(59, 130, 246, 0.12) !important;
  padding-top: 0.85rem !important;
  padding-bottom: 0.85rem !important;
  vertical-align: middle;
}
.dvc-root .dvc-field-table-wrap .dvc-field-table tbody td {
  vertical-align: middle !important;
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
  border-top: none !important;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12) !important;
}
.dvc-root .dvc-field-table-wrap .dvc-field-table tbody tr:last-child td {
  border-bottom: none !important;
}
.dvc-root .dvc-field-table-wrap .dvc-field-table tbody tr:hover td {
  background: rgba(248, 250, 252, 0.98) !important;
}
.dvc-root .dvc-field-table-wrap .dvc-field-table tbody tr:hover {
  background: transparent !important;
}
.dvc-root .dvc-col-num {
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.85rem;
}
.dvc-root .dvc-type-pill {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.28rem 0.55rem;
  border-radius: 8px;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}
.dvc-root .dvc-layout-muted {
  font-size: 0.78rem;
  font-weight: 500;
  color: #64748b;
  padding: 0.15rem 0.4rem;
  border-radius: 6px;
  background: #f8fafc;
}
.dvc-root .dvc-code-pill {
  font-size: 0.72rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  color: #475569;
  border: 1px solid #e2e8f0;
}
.dvc-root .dvc-req-pill {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.2rem 0.45rem;
  border-radius: 6px;
  background: linear-gradient(180deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  border: 1px solid rgba(245, 158, 11, 0.35);
  margin-left: 0.35rem;
  vertical-align: middle;
}
.dvc-root .dvc-req-yes {
  font-size: 0.78rem;
  font-weight: 600;
  color: #059669;
}
.dvc-root .dvc-req-dash {
  color: #cbd5e1;
  font-size: 1.1rem;
  line-height: 1;
}
.dvc-root .dvc-dis-pill {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.2rem 0.45rem;
  border-radius: 6px;
  background: linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #475569;
  border: 1px solid rgba(100, 116, 139, 0.35);
  margin-left: 0.35rem;
  vertical-align: middle;
}
.dvc-root .dvc-dis-yes {
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
}
.dvc-root .dvc-field-label-cell {
  font-weight: 600;
  color: #334155;
  font-size: 0.88rem;
}
.dvc-root .dvc-table-edit-row td {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%) !important;
  border-bottom: none !important;
  border-left: 3px solid #3b82f6;
  padding: 1rem 1.1rem !important;
}
.dvc-root .dvc-section-actions .btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.78rem;
}
.dvc-root .dvc-section-actions .btn-outline-secondary {
  min-width: 2.25rem;
}
.dvc-root .dvc-table-wrap table {
  border-color: var(--dvc-border) !important;
}
.dvc-root .dvc-table-wrap thead th {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  color: #94a3b8;
  border-top: none !important;
  background: #fafbfc !important;
}
.dvc-root .dvc-table-wrap tbody tr:hover {
  background: rgba(59, 130, 246, 0.04) !important;
}
.dvc-root .dvc-form-control .form-control,
.dvc-root .dvc-form-control select.form-control {
  border-radius: 10px;
  border-color: #e2e8f0;
  background: #fefefe;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.dvc-root .dvc-form-control .form-control:focus,
.dvc-root .dvc-form-control select.form-control:focus {
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px var(--dvc-accent-soft);
  background: #fff;
}
/* Match General Verification gray read-only look inside field tiles (Missing LOD, deed rows, etc.) */
.dvc-root .dvc-form-control .form-control:disabled,
.dvc-root .dvc-form-control select.form-control:disabled,
.dvc-root .dvc-form-control textarea.form-control:disabled {
  background-color: #f1f5f9 !important;
  color: #64748b;
  cursor: not-allowed;
  opacity: 1;
}
.dvc-root .dvc-form-control .form-check-input:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.dvc-root .dvc-rs--is-disabled .dvc-rs__control {
  background-color: #f1f5f9 !important;
  border-color: #e2e8f0 !important;
  cursor: not-allowed;
}
.dvc-root .dvc-rs--is-disabled .dvc-rs__single-value {
  color: #64748b;
}
.dvc-root .dvc-field-tile.dvc-field-disabled {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}
.dvc-root .dvc-field-tile.dvc-field-disabled:hover {
  border-color: var(--dvc-border);
  box-shadow: none;
}
.dvc-root .dvc-field-tile {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid var(--dvc-border);
  border-radius: var(--dvc-radius-sm);
  padding: 1rem 1.1rem;
  height: 100%;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.dvc-root .dvc-field-tile:hover {
  border-color: rgba(59, 130, 246, 0.2);
  box-shadow: 0 2px 12px rgba(59, 130, 246, 0.06);
}
.dvc-root .dvc-field-tile .form-label {
  color: #475569;
  font-size: 0.8rem;
}
.dvc-root .dvc-follow-up-shell {
  border-color: rgba(59, 130, 246, 0.18) !important;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.65) 0%, rgba(255, 255, 255, 0.4) 100%);
  border-radius: var(--dvc-radius-sm);
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--dvc-border);
}
.dvc-root .dvc-dynamic-field-panel {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.dvc-root .dvc-deed-type-block {
  border-color: var(--dvc-border) !important;
}
.dvc-root .dvc-deed-type-block:last-child {
  border-bottom: none !important;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}
.dvc-root .dvc-deed-gate-pill {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #1d4ed8;
  background: rgba(59, 130, 246, 0.12);
  border-radius: 6px;
  padding: 0.15rem 0.45rem;
  margin-left: 0.35rem;
}
.dvc-root .dvc-follow-up-panel {
  width: 100%;
  max-width: 100%;
  margin-top: 0.75rem;
  padding: 1rem 1.15rem;
  border: 1px solid rgba(59, 130, 246, 0.22);
  border-radius: 14px;
  background: #ffffff;
  box-sizing: border-box;
}
.dvc-root .dvc-follow-up-nested-wrap {
  width: 100%;
  max-width: 100%;
  margin-top: 0.5rem;
  margin-bottom: 0.75rem;
  padding: 0.75rem 0 0.75rem 1rem;
  border-left: 3px solid rgba(100, 116, 139, 0.45);
  box-sizing: border-box;
}
.dvc-root .dvc-follow-up-panel__title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin-bottom: 0.85rem;
}
.dvc-root .dvc-follow-up-draft-card {
  width: 100%;
  border: 1px solid rgba(59, 130, 246, 0.18);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.75) 100%);
  border-radius: 12px;
  padding: 0.9rem 1rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}
.dvc-root .dvc-follow-up-draft-card:last-child {
  margin-bottom: 0;
}
.dvc-root .dvc-follow-up-draft-card__header {
  margin-bottom: 0.75rem;
}
.dvc-root .dvc-follow-up-branch-badge {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  background: linear-gradient(180deg, #e0f2fe 0%, #dbeafe 100%);
  border: 1px solid rgba(59, 130, 246, 0.25);
  color: #1e40af;
}
.dvc-root .dvc-follow-up-draft-card .form-label {
  margin-bottom: 0.35rem;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 600;
}
.dvc-root .dvc-follow-up-draft-card .form-control,
.dvc-root .dvc-follow-up-draft-card select.form-control {
  background: #ffffff;
  width: 100%;
}
.dvc-root .dvc-follow-up-draft-card .row {
  margin-left: -0.5rem;
  margin-right: -0.5rem;
  width: calc(100% + 1rem);
  align-items: flex-end;
}
.dvc-root .dvc-follow-up-draft-card .row > [class*="col-"] {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}
.dvc-root .dvc-follow-up-draft-card .dvc-follow-up-required-col {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-bottom: 0.35rem;
}
.dvc-root .dvc-follow-up-draft-card .dvc-follow-up-required-col .form-check {
  margin-bottom: 0;
  white-space: nowrap;
}
.dvc-root .dvc-dfv-source-toggle {
  margin-bottom: 0;
  font-size: 0.78rem;
  color: #475569;
  white-space: nowrap;
}
.dvc-root .dvc-dfv-source-toggle--above {
  margin-bottom: 0.35rem;
  white-space: normal;
}
.dvc-root .dvc-dfv-source-toggle .form-check-label {
  padding-left: 0.15rem;
  cursor: pointer;
}
.dvc-root .dvc-dfv-source-toggle .form-check-input {
  margin-top: 0.18rem;
}
.dvc-root .dvc-fo-hint {
  display: inline-block;
  margin-left: 0.4rem;
  font-size: 0.72rem;
  font-weight: 500;
  color: #1d4ed8;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.18);
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  white-space: nowrap;
  vertical-align: middle;
}
.dvc-root .dvc-add-field-card .card-body > .row,
.dvc-root .dvc-dynamic-field-panel .row {
  width: 100%;
  margin-left: 0;
  margin-right: 0;
}
.dvc-root .dvc-dynamic-field-panel .row > [class*="col-"],
.dvc-root .dvc-add-field-card .card-body > .row > [class*="col-"] {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}
.dvc-root .dvc-field-table-wrap {
  overflow-x: auto;
}
.dvc-root .dvc-col-select {
  width: 2.25rem;
  min-width: 2.25rem;
  max-width: 2.25rem;
  text-align: center;
  vertical-align: middle;
  padding-left: 0.35rem !important;
  padding-right: 0.35rem !important;
}
.dvc-root .dvc-duplicate-bulk-toolbar-row td {
  background: linear-gradient(180deg, #f0f9ff 0%, #ffffff 100%) !important;
}
.dvc-root .dvc-table-edit-row > td {
  width: 100%;
  max-width: none;
}
.dvc-root .dvc-table-edit-row .dvc-edit-field-wrap {
  width: 100%;
  max-width: 100%;
}
.dvc-root .dvc-follow-up-nested {
  border-left-width: 3px !important;
  border-left-style: solid !important;
}
.dvc-root .dvc-follow-up-option {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--dvc-muted);
}
.dvc-root .dvc-fu-pill {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.12rem 0.4rem;
  border-radius: 6px;
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
  border: 1px solid rgba(59, 130, 246, 0.22);
  margin-left: 0.35rem;
  vertical-align: middle;
}
.dvc-root .dvc-runtime-section .card-header {
  background: transparent !important;
  border-bottom: 1px solid var(--dvc-border) !important;
  padding-bottom: 0.75rem !important;
}
.dvc-root .dvc-section-accordion-head:hover {
  background: rgba(241, 245, 249, 0.75) !important;
}
.dvc-root .dvc-section-accordion-btn {
  border: 0;
  background: rgba(148, 163, 184, 0.14);
  color: #475569;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, background 0.2s ease;
}
.dvc-root .dvc-section-accordion-btn.is-open {
  transform: rotate(180deg);
}
.dvc-root .dvc-runtime-section h6 {
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #1e293b !important;
}
.dvc-root .dvc-sync-panel .card-header {
  border: none !important;
  border-bottom: 1px solid var(--dvc-border) !important;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #334155 !important;
}
.dvc-root .dvc-sync-checklist .card-header {
  background: linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%) !important;
}
.dvc-root .dvc-sync-doc .card-header {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%) !important;
}
.dvc-root .dvc-doc-sheet {
  font-family: Georgia, "Times New Roman", serif !important;
  border-radius: var(--dvc-radius-sm) !important;
  border: 1px solid var(--dvc-border) !important;
  background: linear-gradient(165deg, #ffffff 0%, #fafbfc 50%, #f8fafc 100%) !important;
  box-shadow: 0 2px 16px rgba(15, 23, 42, 0.04) !important;
}
.dvc-root .dvc-json-pre {
  border-radius: var(--dvc-radius-sm) !important;
  border: 1px solid var(--dvc-border) !important;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #475569 !important;
  font-size: 0.75rem !important;
}
.dvc-root .dvc-last-sync {
  border-radius: 999px !important;
  padding: 0.35rem 0.9rem !important;
  font-weight: 500;
}
.dvc-root .dvc-btn-save {
  border-radius: 10px;
  font-weight: 600;
  padding: 0.55rem 1.5rem;
  box-shadow: 0 3px 12px rgba(34, 197, 94, 0.22);
  border: none;
}
.dvc-root .dvc-btn-master-save-pending {
  font-weight: 700;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.35);
}
.dvc-root .dvc-sections-header-sticky {
  position: sticky;
  top: 0;
  z-index: 1010;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%) !important;
  box-shadow: 0 2px 14px rgba(15, 23, 42, 0.07);
}
.dvc-root .dvc-master-unsaved-fixed-spacer {
  margin-bottom: 0.75rem;
}
/* Position (top/left/width) set inline after measuring header + main column. */
.dvc-master-unsaved-fixed {
  position: fixed;
  z-index: 1029;
  margin: 0;
  padding: 0.75rem 1.25rem;
  min-height: 3.25rem;
  box-sizing: border-box;
  border-radius: 0;
  border: none;
  border-bottom: 2px solid rgba(245, 158, 11, 0.45);
  background: linear-gradient(180deg, rgba(255, 251, 235, 0.98) 0%, #fef3c7 100%);
  box-shadow: 0 4px 18px rgba(15, 23, 42, 0.1);
}
.dvc-master-unsaved-fixed-inner {
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.65rem 1rem;
}
.dvc-master-unsaved-fixed-text {
  font-size: 0.875rem;
  color: #92400e;
  line-height: 1.45;
  flex: 1 1 16rem;
}
.dvc-root .dvc-add-field-card {
  border: 2px dashed rgba(59, 130, 246, 0.28) !important;
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.65) 0%, #ffffff 55%) !important;
}
.dvc-root .dvc-add-field-card .card-header {
  background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%) !important;
  border: none !important;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2) !important;
  color: #1e40af !important;
  font-weight: 600;
}
.dvc-root .dvc-runtime-warn-head {
  background: linear-gradient(180deg, #ffff8d 0%, #fef08a 100%) !important;
  border-bottom: 1px solid rgba(245, 158, 11, 0.35) !important;
}
.dvc-root .dvc-flow-pill {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  margin-left: 0.5rem;
  vertical-align: middle;
}
.dvc-root .dvc-flow-pill--checklist {
  background: rgba(245, 158, 11, 0.18);
  color: #92400e;
  border: 1px solid rgba(245, 158, 11, 0.35);
}
.dvc-root .dvc-flow-pill--docs {
  background: rgba(59, 130, 246, 0.12);
  color: #1d4ed8;
  border: 1px solid rgba(59, 130, 246, 0.25);
}
.dvc-root .dvc-flow-pill--bank {
  background: rgba(16, 185, 129, 0.14);
  color: #047857;
  border: 1px solid rgba(16, 185, 129, 0.3);
}
.dvc-root .dvc-master-bank-panel {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%);
  border-top: 1px solid var(--dvc-border);
  padding: 1rem 1.15rem;
}
.dvc-root .dvc-bank-product-card {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: visible;
  border-color: rgba(16, 185, 129, 0.22) !important;
  background: linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%) !important;
}
.dvc-root .dvc-general-field-row {
  margin-bottom: 1.1rem;
}
.dvc-root .dvc-general-field-row .dvc-general-label {
  font-weight: 700;
  font-size: 0.88rem;
  color: #334155;
  margin-bottom: 0.45rem;
  display: block;
}
.dvc-root .dvc-general-input-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.dvc-root .dvc-general-input-row .form-control,
.dvc-root .dvc-general-input-row select.form-control {
  flex: 1 1 auto;
  background: #f1f5f9;
  border-radius: 8px;
}
.dvc-root .dvc-verify-checkbox {
  flex: 0 0 auto;
  margin: 0;
  padding-left: 0;
}
.dvc-root .dvc-verify-checkbox .form-check-input {
  width: 1.15rem;
  height: 1.15rem;
  margin-top: 0;
}
.dvc-root .dvc-deed-type-head {
  border-bottom: 1px solid var(--dvc-border);
  padding-bottom: 0.65rem;
}
.dvc-root .dvc-deed-type-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: #1e293b;
  margin-right: 1rem;
}
.dvc-root .dvc-deed-group-card {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: visible;
  border-color: var(--dvc-border) !important;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%) !important;
}
.dvc-root .dvc-deed-group-add-form {
  overflow: visible;
}
.dvc-root .dvc-deed-group-card .table {
  width: 100%;
}
.dvc-root .dvc-deed-group-add-form .row {
  width: 100%;
  margin-left: 0;
  margin-right: 0;
}
.dvc-root .dvc-deed-group-add-form .row > [class*="col-"] {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}
.dvc-root .dvc-dfv-with-action-row {
  margin-left: 0;
  margin-right: 0;
}
.dvc-root .dvc-dfv-with-action-row > [class*="col-"] {
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}
.dvc-root .dvc-dfv-select-col {
  min-width: 0;
}
.dvc-root .dvc-dfv-select-col .dvc-rs__control {
  width: 100%;
}
.dvc-root .dvc-master-survey-panel,
.dvc-root .dvc-master-survey-hierarchy-panel {
  max-width: 100%;
  overflow-x: hidden;
}
.dvc-root .dvc-master-survey-flow {
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.35) 0%, #ffffff 100%);
  border-top: 1px solid var(--dvc-border);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.dvc-root .dvc-survey-nature-flow-card {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  border-color: rgba(59, 130, 246, 0.25) !important;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%) !important;
}
.dvc-root .dvc-survey-nature-flow-card .dvc-survey-hierarchy-nested {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.dvc-root .dvc-survey-nature-flow-card .dvc-survey-hierarchy-card {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.dvc-root .dvc-survey-hierarchy-card .dvc-field-table-wrap .dvc-field-table {
  min-width: 920px;
}
.dvc-root .dvc-survey-nature-flow-card .dvc-deed-group-add-form {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.dvc-root .dvc-ec-flow-card {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  border-color: rgba(234, 179, 8, 0.35) !important;
  background: linear-gradient(180deg, #ffffff 0%, #fffbeb 100%) !important;
}
.dvc-root .dvc-ec-entry-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  padding: 0.75rem;
  border: 1px solid #e3e5e8;
  border-radius: 10px;
  max-height: 300px;
  overflow-y: auto;
}
.dvc-root .dvc-ec-entry-grid-cell {
  flex: 0 0 3.25rem;
  text-align: center;
  padding: 0.35rem 0.25rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
}
.dvc-root .dvc-ec-entry-grid-cell.is-related {
  background: #556b2f;
  border-color: #556b2f;
  color: #fff;
}
.dvc-root .dvc-ec-entry-detail-card {
  border-radius: 10px;
  border: 1px solid #dee2e6;
  padding: 1rem;
  margin-bottom: 1rem;
  background: #fff;
}
.dvc-root .dvc-master-survey-add-block {
  border-top: 2px dashed rgba(59, 130, 246, 0.22);
  padding-top: 1rem;
  margin-top: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem;
  width: 100%;
}
.dvc-root .dvc-master-survey-add-block .dvc-master-survey-add-fields {
  flex: 1 1 14rem;
  min-width: 0;
  max-width: 100%;
}
.dvc-root .dvc-master-survey-add-block .dvc-master-survey-select-wrap {
  min-width: 0;
  width: 100%;
  max-width: 100%;
}
.dvc-root .dvc-master-survey-add-block .dvc-master-survey-select-wrap .dvc-rs {
  width: 100%;
}
.dvc-root .dvc-master-survey-add-block .dvc-master-survey-add-actions {
  flex: 0 0 auto;
  padding-bottom: 0.1rem;
}
.dvc-root .dvc-master-survey-add-block .dvc-btn-add-field,
.dvc-root .dvc-survey-hierarchy-card .dvc-deed-group-add-form .dvc-btn-add-field {
  position: relative !important;
  float: none !important;
  top: auto !important;
  right: auto !important;
}
.dvc-root .dvc-survey-hierarchy-nested {
  border-left: 3px solid rgba(59, 130, 246, 0.35);
  padding-left: 0.85rem;
  margin-left: 0.35rem;
}
.dvc-root .dvc-survey-hierarchy-nested--schedule {
  margin-left: 0.5rem;
}
.dvc-root .dvc-survey-hierarchy-nested--survey {
  margin-left: 1.25rem;
}
.dvc-root .dvc-survey-hierarchy-nested--boundary {
  margin-left: 2rem;
}
.dvc-root .dvc-survey-hierarchy-nested--measurement {
  margin-left: 2.75rem;
}
.dvc-root .dvc-master-survey-hierarchy-panel {
  border-top: 2px dashed rgba(59, 130, 246, 0.22);
  padding-top: 1rem;
  margin-top: 0.75rem;
}
.dvc-root .dvc-dfv-action-col .dvc-deed-group-actions {
  margin-bottom: 0;
  padding-bottom: 0;
}
.dvc-root .dvc-deed-group-add-form .dvc-btn-add-field {
  position: relative !important;
  float: none !important;
  top: auto !important;
  right: auto !important;
}
.dvc-root .dvc-deed-group-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.15rem;
}
.dvc-root .dvc-deed-group-actions .btn,
.dvc-root .dvc-btn-add-field {
  height: auto !important;
  width: auto !important;
  min-width: 4.25rem;
  border-radius: 8px !important;
  font-weight: 600;
  font-size: 0.8125rem;
  padding: 0.4rem 1.1rem;
  white-space: nowrap;
  box-shadow: none;
}
.dvc-root .dvc-master-doc-panel {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  border-top: 1px solid var(--dvc-border);
  padding: 1rem 1.15rem;
}
.dvc-root .dvc-master-add-field-panel,
.dvc-root .dvc-deed-group-add-form {
  border-top: 2px dashed rgba(59, 130, 246, 0.22);
  padding: 1rem 1.15rem;
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.5) 0%, #ffffff 70%);
  font-family: inherit;
  font-size: 0.88rem;
  color: var(--dvc-ink);
  letter-spacing: 0.01em;
}
.dvc-root .dvc-deed-group-add-form {
  margin-top: 0.5rem;
}
.dvc-root .dvc-panel-heading {
  font-family: inherit;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 0.65rem;
}
.dvc-root .dvc-master-add-field-panel .form-label,
.dvc-root .dvc-master-add-field-panel label.form-label,
.dvc-root .dvc-deed-group-add-form .form-label,
.dvc-root .dvc-deed-group-add-form label.form-label {
  font-family: inherit;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 0.35rem;
}
.dvc-root .dvc-master-add-field-panel .form-control,
.dvc-root .dvc-master-add-field-panel .form-check-label,
.dvc-root .dvc-master-add-field-panel textarea.form-control,
.dvc-root .dvc-deed-group-add-form .form-control,
.dvc-root .dvc-deed-group-add-form .form-check-label,
.dvc-root .dvc-deed-group-add-form textarea.form-control {
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--dvc-ink);
  letter-spacing: 0.01em;
}
.dvc-root .dvc-master-add-field-panel .dvc-rs__control,
.dvc-root .dvc-master-add-field-panel .dvc-rs__single-value,
.dvc-root .dvc-master-add-field-panel .dvc-rs__placeholder,
.dvc-root .dvc-master-add-field-panel .dvc-rs__input-container input,
.dvc-root .dvc-deed-group-add-form .dvc-rs__control,
.dvc-root .dvc-deed-group-add-form .dvc-rs__single-value,
.dvc-root .dvc-deed-group-add-form .dvc-rs__placeholder,
.dvc-root .dvc-deed-group-add-form .dvc-rs__input-container input {
  font-family: inherit !important;
  font-size: 0.875rem !important;
  letter-spacing: 0.01em;
}
.dvc-root .dvc-field-table-wrap .dvc-field-table {
  font-family: inherit;
}
.dvc-root .dvc-repeat-row {
  border: 1px solid var(--dvc-border);
  border-radius: var(--dvc-radius-sm);
  padding: 0.85rem 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%);
}
.dvc-root .dvc-repeatable-foot {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border-top: 1px solid rgba(148, 163, 184, 0.18) !important;
  padding: 0.7rem 1.15rem !important;
  font-size: 0.8rem;
  color: #64748b;
}
.dvc-root .dvc-doc-quote {
  border-left-color: #7dd3fc !important;
}
`;

const MASTER_UNSAVED_MESSAGE =
  'You have unsaved changes to the master setup. Use "Save master setup" before leaving, or your changes will be lost.';

export class DynamicChecklistManagement extends Component<Props, State> {
  private historyUnblock?: () => void;

  state: State = ((): State => {
    const master: ChecklistSectionDef[] = [];
    return {
      master,
      checklistValues: emptyValuesFromMaster(master),
      fieldVerified: initFieldVerified(master),
      repeatableValues: emptyRepeatableValuesFromMaster(master),
      sectionUiOpen: initSectionOpen(master),
      newFieldDraft: defaultNewFieldDraft(
        master[0]?.id ?? "",
        master[0] ? sectionFlowMode(master[0]) : "checklist"
      ),
      newSectionTitle: "",
      newSectionFlowMode: "document_types",
      documentTypeDraftBySection: {},
      deedGroupFieldDrafts: {},
      editingFieldKey: null,
      fieldEditDraft: emptyFieldEditDraft(),
      activeTab: "master",
      validationMessages: [],
      saveBanner: null,
      deedTemplatesForScrutiny: [],
      inputMasterDynamicFields: [],
      measurementDirectionDynamicTags: [],
      deedMasterList: [],
      bankProductList: [],
      bankProductDraftBySection: {},
      naturePropertyDraftBySection: {},
      surveyHierarchyDraftBySection: {},
      deedFieldsLoading: false,
      frontOfficeFields: [],
      newFrontOfficeFieldName: "",
      masterLoading: false,
      masterSaving: false,
      frontOfficeMasterLoading: false,
      frontOfficeMasterSaving: false,
      masterSaveBanner: null,
      masterSavedSnapshot: JSON.stringify([] as ChecklistSectionDef[]),
      frontOfficeSaveBanner: null,
      masterUnsavedBarInset: null,
    };
  })();

  masterConfigSnapshot = (master: ChecklistSectionDef[] = this.state.master): string =>
    JSON.stringify(sortSections(master));

  isMasterDirty = (): boolean => {
    if (this.state.masterLoading) return false;
    return this.masterConfigSnapshot() !== this.state.masterSavedSnapshot;
  };

  handleBeforeUnload = (e: BeforeUnloadEvent) => {
    if (!this.isMasterDirty()) return;
    e.preventDefault();
    e.returnValue = MASTER_UNSAVED_MESSAGE;
  };

  confirmLeaveMasterSetup = (): boolean => {
    if (!this.isMasterDirty()) return true;
    return window.confirm(`${MASTER_UNSAVED_MESSAGE}\n\nLeave without saving?`);
  };

  handleMasterTabSelect = (nextTab: string) => {
    const tab = nextTab || "master";
    if (tab === this.state.activeTab) return;
    if (this.state.activeTab === "master" && !this.confirmLeaveMasterSetup()) return;
    this.setState({ activeTab: tab }, () => this.scheduleMasterUnsavedBarLayout());
  };

  scheduleMasterUnsavedBarLayout = () => {
    window.requestAnimationFrame(() => {
      this.updateMasterUnsavedBarLayout();
      window.requestAnimationFrame(() => this.updateMasterUnsavedBarLayout());
    });
  };

  updateMasterUnsavedBarLayout = () => {
    if (this.state.activeTab !== "master" || !this.isMasterDirty()) {
      if (this.state.masterUnsavedBarInset !== null) {
        this.setState({ masterUnsavedBarInset: null });
      }
      return;
    }
    const header = document.querySelector(".pcoded-header") as HTMLElement | null;
    const main = document.querySelector(".pcoded-main-container") as HTMLElement | null;
    const top = header ? Math.ceil(header.getBoundingClientRect().bottom) : 85;
    const left = main ? Math.ceil(main.getBoundingClientRect().left) : 264;
    const width = main
      ? Math.ceil(main.getBoundingClientRect().width)
      : Math.max(0, window.innerWidth - left);
    const bar = document.querySelector(".dvc-master-unsaved-fixed") as HTMLElement | null;
    const height = bar ? Math.ceil(bar.getBoundingClientRect().height) : 52;
    const next = { top, left, width, height };
    const cur = this.state.masterUnsavedBarInset;
    if (
      cur &&
      cur.top === next.top &&
      cur.left === next.left &&
      cur.width === next.width &&
      cur.height === next.height
    ) {
      return;
    }
    this.setState({ masterUnsavedBarInset: next });
  };

  componentDidMount() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    window.addEventListener("resize", this.scheduleMasterUnsavedBarLayout);
    window.addEventListener("scroll", this.scheduleMasterUnsavedBarLayout, true);
    if (this.props.history?.block) {
      this.historyUnblock = this.props.history.block(() => {
        if (!this.isMasterDirty()) return undefined;
        return MASTER_UNSAVED_MESSAGE;
      });
    }
    this.loadDeedTemplatesForScrutiny();
    this.loadInputMasterDynamicFields();
    this.loadMeasurementDirectionDynamicTags();
    this.loadDeedMasterList();
    this.loadBankProductList();
    this.loadFrontOfficeMaster();
    this.loadDynamicChecklistMaster();
    this.scheduleMasterUnsavedBarLayout();
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    const dirtyNow = this.isMasterDirty();
    const dirtyWas =
      !prevState.masterLoading &&
      JSON.stringify(sortSections(prevState.master)) !== prevState.masterSavedSnapshot;
    if (
      this.state.activeTab === "master" &&
      (dirtyNow !== dirtyWas ||
        this.state.activeTab !== prevState.activeTab ||
        this.state.master !== prevState.master)
    ) {
      this.scheduleMasterUnsavedBarLayout();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("resize", this.scheduleMasterUnsavedBarLayout);
    window.removeEventListener("scroll", this.scheduleMasterUnsavedBarLayout, true);
    if (this.historyUnblock) {
      this.historyUnblock();
      this.historyUnblock = undefined;
    }
  }

  /** Loads template-manager Dynamic Fields from `input_master_get.php`. */
  loadInputMasterDynamicFields = () => {
    callApi("POST", "input_master_get.php")
      .then((res) => res.data)
      .then((response) => {
        const raw = response?.data;
        if (!Array.isArray(raw)) {
          this.setState({ inputMasterDynamicFields: [] });
          return;
        }
        const seen = new Set<string>();
        const list: DeedTemplateScrutinyRow[] = [];
        (raw as { id?: unknown; name?: unknown; label?: unknown }[]).forEach((row) => {
          const key = row?.name == null ? "" : String(row.name).trim();
          if (!key || seen.has(key)) return;
          seen.add(key);
          const label = row?.label == null ? "" : String(row.label).trim();
          const display = label && label.toLowerCase() !== key.toLowerCase() ? `${key} — ${label}` : key;
          list.push({ id: key, name: display });
        });
        list.sort((a, b) => a.name.localeCompare(b.name));
        this.setState({ inputMasterDynamicFields: list });
      })
      .catch(() => this.setState({ inputMasterDynamicFields: [] }));
  };

  loadMeasurementDirectionDynamicTags = () => {
    callApi("GET", "measure_get.php")
      .then((res) => res.data)
      .then((response) => {
        const raw = response?.data;
        if (!Array.isArray(raw)) {
          this.setState({ measurementDirectionDynamicTags: [] });
          return;
        }
        const labels = (raw as { name?: unknown }[])
          .map((row) => (row?.name == null ? "" : String(row.name).trim()))
          .filter((name) => name !== "");
        const list = buildMeasurementDirectionDynamicTags(labels).map((row) => ({
          id: row.id,
          name: row.name,
        }));
        this.setState({ measurementDirectionDynamicTags: list });
      })
      .catch(() => this.setState({ measurementDirectionDynamicTags: [] }));
  };

  combinedDynamicValueTags = (): DeedTemplateScrutinyRow[] => {
    const seen = new Set<string>();
    const out: DeedTemplateScrutinyRow[] = [];
    const push = (row: DeedTemplateScrutinyRow) => {
      const id = String(row.id);
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push(row);
    };
    GENERAL_DETAIL_DYNAMIC_TAGS.forEach(push);
    MISSING_LOD_DYNAMIC_TAGS.forEach(push);
    DOCUMENT_SCRUTINY_DYNAMIC_TAGS.forEach(push);
    DOCUMENT_SCHEDULE_DYNAMIC_TAGS.forEach(push);
    ADDON_FEATURE_DYNAMIC_TAGS.forEach(push);
    SURVEY_VERIFICATION_DYNAMIC_TAGS.forEach(push);
    SURVEY_HIERARCHY_DYNAMIC_TAGS.forEach(push);
    this.state.measurementDirectionDynamicTags.forEach(push);
    EC_VERIFICATION_DYNAMIC_TAGS.forEach(push);
    this.state.deedTemplatesForScrutiny.forEach(push);
    this.state.inputMasterDynamicFields.forEach(push);
    return out;
  };

  /** Read the current logged-in user id from localStorage (best-effort). */
  currentUserId = (): number => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return 0;
      const parsed = JSON.parse(raw);
      const id = parsed?.userId ?? parsed?.id;
      const n = Number(id);
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  };

  /**
   * Loads the persisted dynamic checklist master config from
   * `dynamic_checklist_master_get.php` and replaces in-memory `master` with it.
   * Runtime values (`checklistValues`, `repeatableValues`, `fieldVerified`) are
   * reseeded from the freshly loaded master, so the runtime preview stays in
   * sync with the designer.
   */
  loadDynamicChecklistMaster = () => {
    this.setState({ masterLoading: true });
    callApi("POST", "dynamic_checklist_master_get.php")
      .then((res) => res?.data)
      .then((response) => {
        const data = response?.data;
        if (response?.result === false || !data) {
          this.setState({ masterLoading: false });
          return;
        }
        const rawMaster = Array.isArray(data.master) ? data.master : [];
        const master = normalizeMasterFromApi(rawMaster as ChecklistSectionDef[]);
        const masterSavedSnapshot = JSON.stringify(sortSections(master));
        this.setState({
          master,
          masterSavedSnapshot,
          checklistValues: emptyValuesFromMaster(master),
          fieldVerified: initFieldVerified(master),
          repeatableValues: emptyRepeatableValuesFromMaster(master),
          sectionUiOpen: initSectionOpen(master),
          newFieldDraft: defaultNewFieldDraft(
            master[0]?.id ?? "",
            master[0] ? sectionFlowMode(master[0]) : "checklist"
          ),
          masterLoading: false,
        });
      })
      .catch(() => {
        this.setState({
          masterLoading: false,
          masterSaveBanner: {
            variant: "danger",
            text: "Could not load dynamic checklist master. Please retry.",
          },
        });
      });
  };

  /**
   * Persists the current designer state via `dynamic_checklist_master_save.php`.
   * Only the master config is sent — runtime answers stay local to the preview.
   */
  saveDynamicChecklistMaster = () => {
    if (this.state.masterSaving) return;
    this.setState({ masterSaving: true, masterSaveBanner: null });
    const payload = {
      master: this.state.master,
      userId: this.currentUserId(),
      version: new Date().toISOString(),
    };
    callApi("POST", "dynamic_checklist_master_save.php", payload)
      .then((res) => res?.data)
      .then((response) => {
        if (response && response.result === false) {
          const msg = String(response.message || "Save failed. Please retry.");
          this.setState({
            masterSaving: false,
            masterSaveBanner: { variant: "danger", text: msg },
          });
          ToastsStore.error(msg);
          return;
        }
        this.setState({
          masterSaving: false,
          masterSavedSnapshot: this.masterConfigSnapshot(),
          masterSaveBanner: {
            variant: "success",
            text: String(response?.message || "Master configuration saved."),
          },
        });
        ToastsStore.success(
          String(response?.message || "Master configuration saved.")
        );
      })
      .catch(() => {
        this.setState({
          masterSaving: false,
          masterSaveBanner: {
            variant: "danger",
            text: "Network error while saving master. Please retry.",
          },
        });
      });
  };

  /**
   * Loads the persisted front-office field selection from
   * `front_office_master_get.php`. Rows arrive in catalog-key form so they can
   * be plugged straight into `state.frontOfficeFields` (each row's `id` is the
   * `TBL_FRONTOFFICE` column key).
   */
  loadFrontOfficeMaster = () => {
    this.setState({ frontOfficeMasterLoading: true });
    callApi("POST", "front_office_checklist_master_get.php")
      .then((res) => res?.data)
      .then((response) => {
        const raw = response?.data;
        if (response?.result === false || !Array.isArray(raw)) {
          this.setState({ frontOfficeMasterLoading: false });
          return;
        }
        const seen = new Set<string>();
        const list: FrontOfficeFieldRow[] = [];
        (raw as { id?: unknown; name?: unknown }[]).forEach((row) => {
          const key = row?.id == null ? "" : String(row.id).trim();
          if (!key || seen.has(key)) return;
          const catalog = FRONT_OFFICE_CATALOG.find((c) => c.id === key);
          if (!catalog) return; // ignore rows that no longer match the catalog
          seen.add(key);
          const label =
            row?.name != null && String(row.name).trim()
              ? String(row.name).trim()
              : catalog.name;
          list.push({ id: catalog.id, name: label });
        });
        this.setState({
          frontOfficeFields: list,
          frontOfficeMasterLoading: false,
        });
      })
      .catch(() => {
        this.setState({
          frontOfficeMasterLoading: false,
          frontOfficeSaveBanner: {
            variant: "danger",
            text: "Could not load front office master. Please retry.",
          },
        });
      });
  };

  /**
   * Persists the current front-office selection via
   * `front_office_master_save.php`. Server replaces the active list with the
   * payload (soft-deletes anything not included).
   */
  saveFrontOfficeMaster = () => {
    if (this.state.frontOfficeMasterSaving) return;
    this.setState({ frontOfficeMasterSaving: true, frontOfficeSaveBanner: null });
    const payload = {
      fields: this.state.frontOfficeFields,
      userId: this.currentUserId(),
    };
    callApi("POST", "front_office_checklist_master_save.php", payload)
      .then((res) => res?.data)
      .then((response) => {
        if (response && response.result === false) {
          const msg = String(response.message || "Save failed. Please retry.");
          this.setState({
            frontOfficeMasterSaving: false,
            frontOfficeSaveBanner: { variant: "danger", text: msg },
          });
          ToastsStore.error(msg);
          return;
        }
        this.setState({
          frontOfficeMasterSaving: false,
          frontOfficeSaveBanner: {
            variant: "success",
            text: String(response?.message || "Front office master saved."),
          },
        });
        ToastsStore.success(
          String(response?.message || "Front office master saved.")
        );
      })
      .catch(() => {
        this.setState({
          frontOfficeMasterSaving: false,
          frontOfficeSaveBanner: {
            variant: "danger",
            text: "Network error while saving front office master. Please retry.",
          },
        });
      });
  };

  /**
   * Existing free-text "Front Office master" rows (added before the catalog binding
   * was introduced) get rebound to their `TBL_FRONTOFFICE` column key by matching
   * the label case-insensitively. Rows with no catalog match are dropped, and any
   * checklist field that pointed at the old free-text id is rewritten to point at
   * the catalog id so the mapping survives the upgrade.
   */
  migrateFrontOfficeFieldsToCatalog = () => {
    this.setState((prev) => {
      const seen = new Set<string>();
      const idRewrites = new Map<string, string>();
      const next: FrontOfficeFieldRow[] = [];
      prev.frontOfficeFields.forEach((row) => {
        if (FRONT_OFFICE_CATALOG.some((c) => c.id === row.id)) {
          if (!seen.has(row.id)) {
            seen.add(row.id);
            next.push({ ...row });
          }
          return;
        }
        const match = FRONT_OFFICE_CATALOG_BY_NAME[row.name.trim().toLowerCase()];
        if (match && !seen.has(match.id)) {
          seen.add(match.id);
          idRewrites.set(row.id, match.id);
          next.push({ ...match });
        }
      });
      if (
        next.length === prev.frontOfficeFields.length &&
        idRewrites.size === 0 &&
        next.every((n, i) => n.id === prev.frontOfficeFields[i].id)
      ) {
        return {} as Pick<State, never>;
      }
      const rewriteScrutiny = (
        field: ChecklistFieldDef | DropdownOptionFollowUpDef
      ) => {
        if (field.dynamicValueSource !== "front_office") return field;
        const id = normalizeDeedIdForSelect(field.scrutinyLinkedDeedId);
        const replacement = idRewrites.get(id);
        if (!replacement) return field;
        return { ...field, scrutinyLinkedDeedId: replacement };
      };
      const rewriteFollowUps = (
        nodes: DropdownOptionFollowUpDef[] | undefined
      ): DropdownOptionFollowUpDef[] | undefined => {
        if (!nodes) return nodes;
        return nodes.map((n) => {
          const next = rewriteScrutiny(n) as DropdownOptionFollowUpDef;
          return {
            ...next,
            nestedFollowUps: rewriteFollowUps(next.nestedFollowUps),
          };
        });
      };
      const master = prev.master.map((s) => ({
        ...s,
        fields: s.fields.map((f) => {
          const rewritten = rewriteScrutiny(f) as ChecklistFieldDef;
          return {
            ...rewritten,
            optionFollowUps: rewriteFollowUps(rewritten.optionFollowUps),
          };
        }),
        repeatableGroups: (s.repeatableGroups || []).map((g) => ({
          ...g,
          fields: g.fields.map((f) => rewriteScrutiny(f) as ChecklistFieldDef),
        })),
      }));
      return {
        frontOfficeFields: next,
        master,
      } as Pick<State, "frontOfficeFields" | "master">;
    });
  };

  /** Active bank products from `bank_product_get.php`. */
  loadBankProductList = () => {
    callApi("GET", "bank_product_get.php")
      .then((res) => res?.data)
      .then((result) => {
        if (result?.result === false) {
          this.setState({ bankProductList: [] });
          return;
        }
        const raw = result?.data;
        const list: BankProductRow[] = Array.isArray(raw)
          ? raw
            .map((row: any) => ({
              id: Number(row.id),
              bank_name: String(row.bank_name ?? "").trim(),
              product_name: String(row.product_name ?? "").trim(),
              product_id: row.product_id != null ? Number(row.product_id) : undefined,
              bank_id: row.bank_id != null ? Number(row.bank_id) : undefined,
              branch_id: row.branch_id != null ? Number(row.branch_id) : undefined,
            }))
            .filter(
              (row) =>
                Number.isFinite(row.id) &&
                row.id > 0 &&
                (row.bank_name !== "" || row.product_name !== "")
            )
          : [];
        this.setState({ bankProductList: list });
      })
      .catch(() => {
        this.setState({ bankProductList: [] });
      });
  };

  /** Same source as ScrutinyForm `getDeeds` → `deed_master_get.php`. */
  applyDeedMasterResponse = (response: { result?: boolean; data?: unknown }) => {
    const raw = response?.data;
    if (response?.result === false || !Array.isArray(raw)) {
      this.setState({ deedMasterList: [] });
      return;
    }
    const list: DeedMasterRow[] = (raw as { id: number | string; name: string }[])
      .map((row) => ({
        id: Number(row.id),
        name: String(row.name || "").trim(),
      }))
      .filter((row) => Number.isFinite(row.id) && row.id > 0 && row.name);
    this.setState({ deedMasterList: list.sort((a, b) => a.name.localeCompare(b.name)) });
  };

  loadDeedMasterList = () => {
    const category =
      this.props.match?.params?.type ||
      (typeof window !== "undefined"
        ? new URLSearchParams(window.location.search).get("type")
        : null) ||
      "";

    const loadPost = () =>
      callApi("POST", "deed_master_get.php", { category })
        .then((res) => res.data)
        .then((response) => this.applyDeedMasterResponse(response))
        .catch(() => this.setState({ deedMasterList: [] }));

    callApi("GET", "deed_master_get.php")
      .then((res) => res.data)
      .then((response) => {
        if (response?.result !== false && Array.isArray(response?.data) && response.data.length) {
          this.applyDeedMasterResponse(response);
          return;
        }
        return loadPost();
      })
      .catch(() => loadPost());
  };

  mergeDocumentTypeItemSet = (
    section: ChecklistSectionDef,
    deed: DeedMasterRow,
    inputs: InputMasterFieldRow[],
    uniqueSuffix: string
  ): {
    section: ChecklistSectionDef;
    checklistValues: Record<string, string>;
    repeatableValues: Record<string, Record<string, string>[]>;
    repeatableKeysToDrop: string[];
  } => {
    const deedId = deed.id;
    const gateId = gateFieldIdForDeed(deedId);
    const groupId = deedGroupIdForDeed(deedId);
    let fields = [...section.fields];
    let groups = [...(section.repeatableGroups || [])];
    const repeatableKeysToDrop: string[] = [];

    const oldGroup = groups.find((g) => g.id === groupId);
    if (oldGroup) {
      repeatableKeysToDrop.push(`${section.id}::${groupId}`);
      groups = groups.filter((g) => g.id !== groupId);
    }

    if (!fields.some((f) => f.id === gateId)) {
      const gateOrder = fields.length ? Math.max(...fields.map((f) => f.order)) + 1 : 1;
      fields.push({
        id: gateId,
        label: deed.name,
        type: "yes_no",
        order: gateOrder,
        layout: "full",
        deedMasterId: deedId,
      });
    }

    const mapped =
      inputs.length > 0
        ? inputs.map((inp, i) => mapInputMasterToChecklistField(inp, i + 1, deedId, uniqueSuffix))
        : [];
    const rgOrder = groups.length ? Math.max(...groups.map((g) => g.order)) + 1 : 1;
    groups.push({
      id: groupId,
      label: deed.name,
      order: rgOrder,
      addButtonLabel: `Add ${deed.name}`,
      minInstances: 1,
      maxInstances: 8,
      deedMasterId: deedId,
      gateFieldId: gateId,
      fields: mapped,
    });

    const enabled = [...(section.enabledDeedMasterIds || [])];
    if (!enabled.includes(deedId)) enabled.push(deedId);

    const checklistValues: Record<string, string> = { [gateId]: "No" };
    mapped.forEach((f) => {
      checklistValues[f.id] = f.type === "yes_no" ? "No" : "";
    });

    const repeatableValues: Record<string, Record<string, string>[]> = {};
    repeatableValues[repeatableEntriesKey(section.id, groupId)] = [emptyRepeatableRow(mapped)];

    return {
      section: { ...section, fields, repeatableGroups: groups, enabledDeedMasterIds: enabled },
      checklistValues,
      repeatableValues,
      repeatableKeysToDrop,
    };
  };

  /** Adds document type Yes/No gate + empty field block (fields added manually in Step 2). */
  addDocumentTypeToSection = (sectionId: string, deedId: number) => {
    const deed = this.state.deedMasterList.find((d) => d.id === deedId);
    if (!deed) return;
    const uniqueSuffix = String(Date.now());
    this.setState((prev): DeedFieldsLoadingStateUpdate => {
      const section = prev.master.find((s) => s.id === sectionId);
      if (!section) return finishDeedFieldsLoading(prev);
      const merged = this.mergeDocumentTypeItemSet(section, deed, [], uniqueSuffix);
      let checklistValues = { ...prev.checklistValues, ...merged.checklistValues };
      const repeatableValues = { ...prev.repeatableValues };
      merged.repeatableKeysToDrop.forEach((k) => delete repeatableValues[k]);
      Object.assign(repeatableValues, merged.repeatableValues);
      return {
        master: sortSections(
          prev.master.map((s) => (s.id === sectionId ? merged.section : s))
        ),
        checklistValues,
        repeatableValues,
        documentTypeDraftBySection: {
          ...prev.documentTypeDraftBySection,
          [sectionId]: "",
        },
        deedFieldsLoading: false,
      };
    });
    ToastsStore.success(`Added "${deed.name}". Use the card below to add checklist fields.`);
  };

  mergeBankProductItemSet = (
    section: ChecklistSectionDef,
    bankProduct: BankProductRow
  ): {
    section: ChecklistSectionDef;
    repeatableKeysToDrop: string[];
  } => {
    const bankProductId = bankProduct.id;
    const groupId = bankGroupIdForProduct(bankProductId);
    let groups = [...(section.repeatableGroups || [])];
    const repeatableKeysToDrop: string[] = [];

    const oldGroup = groups.find((g) => g.id === groupId);
    if (oldGroup) {
      repeatableKeysToDrop.push(repeatableEntriesKey(section.id, groupId));
      groups = groups.filter((g) => g.id !== groupId);
    }

    const rgOrder = groups.length ? Math.max(...groups.map((g) => g.order)) + 1 : 1;
    groups.push({
      id: groupId,
      label: formatBankProductLabelFromRow(bankProduct),
      order: rgOrder,
      addButtonLabel: `Add ${formatBankProductLabelFromRow(bankProduct)}`,
      minInstances: 1,
      maxInstances: 1,
      bankProductId,
      fields: [],
    });

    const enabled = [...(section.enabledBankProductIds || [])];
    if (!enabled.includes(bankProductId)) enabled.push(bankProductId);

    return {
      section: {
        ...section,
        repeatableGroups: groups,
        enabledBankProductIds: enabled,
      },
      repeatableKeysToDrop,
    };
  };

  addBankProductToSection = (sectionId: string, bankProductId: number) => {
    const bankProduct = this.state.bankProductList.find((b) => b.id === bankProductId);
    if (!bankProduct) return;
    const section = this.state.master.find((s) => s.id === sectionId);
    if (!section) return;
    this.setState((prev): BankProductSectionStateUpdate => {
      const merged = this.mergeBankProductItemSet(section, bankProduct);
      const repeatableValues = { ...prev.repeatableValues };
      merged.repeatableKeysToDrop.forEach((k) => delete repeatableValues[k]);
      return {
        master: sortSections(
          prev.master.map((s) => (s.id === sectionId ? merged.section : s))
        ),
        repeatableValues,
        bankProductDraftBySection: {
          ...prev.bankProductDraftBySection,
          [sectionId]: "",
        },
      };
    });
    ToastsStore.success(
      `Added "${formatBankProductLabelFromRow(bankProduct)}". Add checklist fields below.`
    );
  };

  mergeNaturePropertyItemSet = (
    section: ChecklistSectionDef,
    natureKey: string,
    natureLabel: string
  ): {
    section: ChecklistSectionDef;
    repeatableKeysToDrop: string[];
  } => {
    const groupId = surveyGroupIdForNature(natureKey);
    let groups = [...(section.repeatableGroups || [])];
    const repeatableKeysToDrop: string[] = [];
    const oldGroup = groups.find((g) => g.id === groupId);
    if (oldGroup) {
      repeatableKeysToDrop.push(repeatableEntriesKey(section.id, groupId));
      groups = groups.filter((g) => g.id !== groupId);
    }
    const rgOrder = groups.length ? Math.max(...groups.map((g) => g.order)) + 1 : 1;
    groups.push({
      id: groupId,
      label: natureLabel,
      order: rgOrder,
      addButtonLabel: `Add ${natureLabel}`,
      minInstances: 1,
      maxInstances: 1,
      naturePropertyKey: natureKey,
      fields: [],
    });
    const enabled = [...(section.enabledNaturePropertyKeys || [])];
    if (!enabled.includes(natureKey)) enabled.push(natureKey);
    return {
      section: {
        ...section,
        repeatableGroups: groups,
        enabledNaturePropertyKeys: enabled,
      },
      repeatableKeysToDrop,
    };
  };

  addNaturePropertyToSection = (sectionId: string, natureKey: string) => {
    const row = NATURE_PROPERTY_CATALOG.find((r) => r.key === natureKey);
    if (!row) return;
    const section = this.state.master.find((s) => s.id === sectionId);
    if (!section) return;
    this.setState((prev) => {
      const merged = this.mergeNaturePropertyItemSet(section, row.key, row.label);
      const repeatableValues = { ...prev.repeatableValues };
      merged.repeatableKeysToDrop.forEach((k) => delete repeatableValues[k]);
      return {
        master: sortSections(
          prev.master.map((s) => (s.id === sectionId ? merged.section : s))
        ),
        repeatableValues,
        naturePropertyDraftBySection: {
          ...prev.naturePropertyDraftBySection,
          [sectionId]: "",
        },
      };
    });
    ToastsStore.success(`Added "${row.label}". Add Schedule, Survey, Boundary, and Measurement levels below.`);
  };

  mergeSurveyHierarchyLevelGroup = (
    section: ChecklistSectionDef,
    natureKey: string,
    level: SurveyHierarchyLevel,
    levelLabel: string
  ): {
    section: ChecklistSectionDef;
    repeatableKeysToDrop: string[];
  } => {
    const groupId = surveyHierarchyGroupId(natureKey, level);
    let groups = [...(section.repeatableGroups || [])];
    const repeatableKeysToDrop: string[] = [];
    const oldGroup = groups.find((g) => g.id === groupId);
    if (oldGroup) {
      repeatableKeysToDrop.push(repeatableEntriesKey(section.id, groupId));
      groups = groups.filter((g) => g.id !== groupId);
    }
    const rgOrder = groups.length ? Math.max(...groups.map((g) => g.order)) + 1 : 1;
    groups.push({
      id: groupId,
      label: levelLabel,
      order: rgOrder,
      addButtonLabel: `Add ${levelLabel}`,
      minInstances: 1,
      maxInstances: 1,
      naturePropertyKey: natureKey,
      surveyHierarchyLevel: level,
      fields: [],
    });
    const enabled = [...(section.enabledSurveyHierarchyLevels || [])];
    if (!enabled.includes(level)) enabled.push(level);
    return {
      section: {
        ...section,
        repeatableGroups: groups,
        enabledSurveyHierarchyLevels: enabled,
      },
      repeatableKeysToDrop,
    };
  };

  surveyHierarchyDraftKey = (sectionId: string, natureKey: string): string =>
    `${sectionId}::${natureKey}`;

  addSurveyHierarchyLevelToNature = (
    sectionId: string,
    natureKey: string,
    level: SurveyHierarchyLevel
  ) => {
    const row = SURVEY_HIERARCHY_LEVELS.find((r) => r.key === level);
    if (!row) return;
    const section = this.state.master.find((s) => s.id === sectionId);
    if (!section) return;
    this.setState((prev) => {
      const merged = this.mergeSurveyHierarchyLevelGroup(section, natureKey, row.key, row.label);
      const repeatableValues = { ...prev.repeatableValues };
      merged.repeatableKeysToDrop.forEach((k) => delete repeatableValues[k]);
      const draftKey = this.surveyHierarchyDraftKey(sectionId, natureKey);
      return {
        master: sortSections(
          prev.master.map((s) => (s.id === sectionId ? merged.section : s))
        ),
        repeatableValues,
        surveyHierarchyDraftBySection: {
          ...prev.surveyHierarchyDraftBySection,
          [draftKey]: "",
        },
      };
    });
    ToastsStore.success(`Added "${row.label}" under ${naturePropertyLabel(natureKey)}.`);
  };

  addSurveyHierarchyLevelToSection = (sectionId: string, level: SurveyHierarchyLevel) => {
    const section = this.state.master.find((s) => s.id === sectionId);
    const natureKey = section?.enabledNaturePropertyKeys?.[0];
    if (!natureKey) {
      ToastsStore.warning("Add a nature of property first.");
      return;
    }
    this.addSurveyHierarchyLevelToNature(sectionId, natureKey, level);
  };

  removeSurveyHierarchyLevelFromNature = (
    sectionId: string,
    natureKey: string,
    level: SurveyHierarchyLevel
  ) => {
    const row = SURVEY_HIERARCHY_LEVELS.find((r) => r.key === level);
    const label = row?.label ?? level;
    const natureLabel = naturePropertyLabel(natureKey) || natureKey;
    if (
      !window.confirm(
        `Remove "${label}" level and all checklist fields under ${natureLabel}?`
      )
    ) {
      return;
    }
    const groupId = surveyHierarchyGroupId(natureKey, level);
    this.setState((prev) => {
      const sec = prev.master.find((s) => s.id === sectionId);
      const group = sec?.repeatableGroups?.find((g) => g.id === groupId);
      const checklistValues = { ...prev.checklistValues };
      group?.fields.forEach((f) => {
        delete checklistValues[f.id];
        collectFollowUpValueKeys(f.optionFollowUps).forEach((k) => {
          delete checklistValues[k];
        });
      });
      const repeatableValues = { ...prev.repeatableValues };
      delete repeatableValues[repeatableEntriesKey(sectionId, groupId)];
      return {
        master: sortSections(
          prev.master.map((s) => {
            if (s.id !== sectionId) return s;
            return {
              ...s,
              repeatableGroups: (s.repeatableGroups || []).filter((g) => g.id !== groupId),
            };
          })
        ),
        checklistValues,
        repeatableValues,
      };
    });
    ToastsStore.info(`Removed "${label}" from ${natureLabel}.`);
  };

  removeSurveyHierarchyLevelFromSection = (sectionId: string, level: SurveyHierarchyLevel) => {
    const section = this.state.master.find((s) => s.id === sectionId);
    const natureKey = section?.enabledNaturePropertyKeys?.[0];
    if (natureKey) {
      this.removeSurveyHierarchyLevelFromNature(sectionId, natureKey, level);
      return;
    }
    const row = SURVEY_HIERARCHY_LEVELS.find((r) => r.key === level);
    const label = row?.label ?? level;
    if (
      !window.confirm(`Remove "${label}" level and all checklist fields under it from this section?`)
    ) {
      return;
    }
    const groupId = legacySurveyHierarchyGroupId(level);
    this.setState((prev) => {
      const sec = prev.master.find((s) => s.id === sectionId);
      const group = sec?.repeatableGroups?.find((g) => g.id === groupId);
      const checklistValues = { ...prev.checklistValues };
      group?.fields.forEach((f) => {
        delete checklistValues[f.id];
        collectFollowUpValueKeys(f.optionFollowUps).forEach((k) => {
          delete checklistValues[k];
        });
      });
      const repeatableValues = { ...prev.repeatableValues };
      delete repeatableValues[repeatableEntriesKey(sectionId, groupId)];
      return {
        master: sortSections(
          prev.master.map((s) => {
            if (s.id !== sectionId) return s;
            return {
              ...s,
              repeatableGroups: (s.repeatableGroups || []).filter((g) => g.id !== groupId),
              enabledSurveyHierarchyLevels: (s.enabledSurveyHierarchyLevels || []).filter(
                (k) => k !== level
              ),
            };
          })
        ),
        checklistValues,
        repeatableValues,
      };
    });
    ToastsStore.info(`Removed "${label}" from this section.`);
  };

  removeNaturePropertyFromSection = (sectionId: string, natureKey: string) => {
    const label = naturePropertyLabel(natureKey) || natureKey;
    if (
      !window.confirm(`Remove "${label}" and all checklist fields under it from this section?`)
    ) {
      return;
    }
    const groupId = surveyGroupIdForNature(natureKey);
    const hierarchyGroupIds = SURVEY_HIERARCHY_LEVELS.map((l) =>
      surveyHierarchyGroupId(natureKey, l.key)
    );
    this.setState((prev) => {
      const sec = prev.master.find((s) => s.id === sectionId);
      const group = sec?.repeatableGroups?.find((g) => g.id === groupId);
      const checklistValues = { ...prev.checklistValues };
      group?.fields.forEach((f) => {
        delete checklistValues[f.id];
        collectFollowUpValueKeys(f.optionFollowUps).forEach((k) => {
          delete checklistValues[k];
        });
      });
      (sec?.repeatableGroups || [])
        .filter((g) => g.naturePropertyKey === natureKey && g.surveyHierarchyLevel != null)
        .forEach((hg) => {
          hg.fields.forEach((f) => {
            delete checklistValues[f.id];
            collectFollowUpValueKeys(f.optionFollowUps).forEach((k) => {
              delete checklistValues[k];
            });
          });
        });
      const repeatableValues = { ...prev.repeatableValues };
      delete repeatableValues[repeatableEntriesKey(sectionId, groupId)];
      hierarchyGroupIds.forEach((hgId) => {
        delete repeatableValues[repeatableEntriesKey(sectionId, hgId)];
      });
      return {
        master: sortSections(
          prev.master.map((s) => {
            if (s.id !== sectionId) return s;
            return {
              ...s,
              repeatableGroups: (s.repeatableGroups || []).filter(
                (g) =>
                  g.id !== groupId &&
                  !(g.naturePropertyKey === natureKey && g.surveyHierarchyLevel != null)
              ),
              enabledNaturePropertyKeys: (s.enabledNaturePropertyKeys || []).filter(
                (k) => k !== natureKey
              ),
            };
          })
        ),
        checklistValues,
        repeatableValues,
      };
    });
  };

  removeBankProductFromSection = (sectionId: string, bankProductId: number) => {
    const label =
      formatBankProductLabel(bankProductId, this.state.bankProductList) ||
      `bank product #${bankProductId}`;
    if (
      !window.confirm(`Remove "${label}" and all checklist fields under it from this section?`)
    ) {
      return;
    }
    const groupId = bankGroupIdForProduct(bankProductId);
    this.setState((prev): BankProductRemoveStateUpdate => {
      const sec = prev.master.find((s) => s.id === sectionId);
      const group = sec?.repeatableGroups?.find((g) => g.id === groupId);
      const checklistValues = { ...prev.checklistValues };
      group?.fields.forEach((f) => {
        delete checklistValues[f.id];
        collectFollowUpValueKeys(f.optionFollowUps).forEach((k) => {
          delete checklistValues[k];
        });
      });
      const repeatableValues = { ...prev.repeatableValues };
      delete repeatableValues[repeatableEntriesKey(sectionId, groupId)];
      return {
        master: sortSections(
          prev.master.map((s) => {
            if (s.id !== sectionId) return s;
            return {
              ...s,
              repeatableGroups: (s.repeatableGroups || []).filter((g) => g.id !== groupId),
              enabledBankProductIds: (s.enabledBankProductIds || []).filter(
                (id) => id !== bankProductId
              ),
            };
          })
        ),
        checklistValues,
        repeatableValues,
      };
    });
  };

  /**
   * Loads the same scrutiny tag list that the deed template "Scrutiny Table Content"
   * mention field shows. Source: `autocomplete_tag.php` (see AddDeedTemplate.getSuggestions).
   * Response shape: `{ result, data: [{ id: "<tag value>", display: "<tag value>" }] }`.
   */
  loadDeedTemplatesForScrutiny = () => {
    callApi("POST", "autocomplete_tag.php")
      .then((res) => res.data)
      .then((response) => {
        const raw = response?.data;
        if (response?.result === false || !Array.isArray(raw)) {
          this.setState({ deedTemplatesForScrutiny: [] });
          return;
        }
        const seen = new Set<string>();
        const list: DeedTemplateScrutinyRow[] = [];
        const stripTagWrap = (s: string) => s.replace(/^@@+/, "").replace(/@@+$/, "").trim();
        (raw as { id?: unknown; display?: unknown; value?: unknown }[]).forEach((row) => {
          const idRaw = row?.id ?? row?.value ?? row?.display;
          const displayRaw = row?.display ?? row?.id ?? row?.value;
          const id = idRaw == null ? "" : String(idRaw).trim();
          const displayRawStr = displayRaw == null ? "" : String(displayRaw).trim();
          const display = stripTagWrap(displayRawStr) || stripTagWrap(id) || id;
          if (!id || seen.has(id)) return;
          seen.add(id);
          list.push({ id, name: display });
        });
        list.sort((a, b) => a.name.localeCompare(b.name));
        this.setState({ deedTemplatesForScrutiny: list });
      })
      .catch(() => {
        this.setState({ deedTemplatesForScrutiny: [] });
      });
  };

  /**
   * Add a catalog-bound row to the front-office master. `newFrontOfficeFieldName`
   * holds the catalog id (TBL_FRONTOFFICE column key) chosen from the picker.
   */
  addFrontOfficeField = () => {
    const key = (this.state.newFrontOfficeFieldName || "").trim();
    if (!key) return;
    const catalog = FRONT_OFFICE_CATALOG.find((c) => c.id === key);
    if (!catalog) {
      ToastsStore.error(`"${key}" is not a recognized Front Office field.`);
      return;
    }
    this.setState((prev) => {
      if (prev.frontOfficeFields.some((f) => f.id === catalog.id)) {
        ToastsStore.error(`"${catalog.name}" is already in the front office master.`);
        return { newFrontOfficeFieldName: "" } as Pick<State, "newFrontOfficeFieldName">;
      }
      return {
        frontOfficeFields: [...prev.frontOfficeFields, { ...catalog }],
        newFrontOfficeFieldName: "",
      } as Pick<State, "frontOfficeFields" | "newFrontOfficeFieldName">;
    });
  };

  removeFrontOfficeField = (id: string) => {
    this.setState((prev) => ({
      frontOfficeFields: prev.frontOfficeFields.filter((f) => f.id !== id),
    }));
  };

  setChecklistValue = (fieldId: string, value: string) => {
    this.setState((prev) => {
      let next = { ...prev.checklistValues, [fieldId]: value };
      if (prev.checklistValues[fieldId] !== value) {
        next = reconcileAllFollowUpClears(prev.master, next);
      }
      return {
        checklistValues: next,
        validationMessages: [],
        saveBanner: null,
      };
    });
  };

  toggleSectionOpen = (sectionId: string) => {
    this.setState((prev) => ({
      sectionUiOpen: { ...prev.sectionUiOpen, [sectionId]: !prev.sectionUiOpen[sectionId] },
    }));
  };

  addRepeatableRow = (sectionId: string, groupId: string) => {
    const sec = this.state.master.find((s) => s.id === sectionId);
    const g = sec?.repeatableGroups?.find((x) => x.id === groupId);
    if (!g) return;
    const key = repeatableEntriesKey(sectionId, groupId);
    const rows = [...(this.state.repeatableValues[key] || [])];
    const max = g.maxInstances ?? 99;
    if (rows.length >= max) return;
    this.setState((prev): RepeatableRowStateUpdate => ({
      repeatableValues: {
        ...prev.repeatableValues,
        [key]: [...rows, emptyRepeatableRow(g.fields)],
      },
      validationMessages: [],
      saveBanner: null,
    }));
  };

  removeRepeatableRow = (sectionId: string, groupId: string, index: number) => {
    const sec = this.state.master.find((s) => s.id === sectionId);
    const g = sec?.repeatableGroups?.find((x) => x.id === groupId);
    if (!g) return;
    const min = Math.max(1, g.minInstances ?? 1);
    const key = repeatableEntriesKey(sectionId, groupId);
    const rows = [...(this.state.repeatableValues[key] || [])];
    if (rows.length <= min || index < 0 || index >= rows.length) return;
    const nextRows = [...rows];
    nextRows.splice(index, 1);
    this.setState((prev): RepeatableRowStateUpdate => ({
      repeatableValues: { ...prev.repeatableValues, [key]: nextRows },
      validationMessages: [],
      saveBanner: null,
    }));
  };

  setRepeatableCell = (
    sectionId: string,
    groupId: string,
    rowIndex: number,
    fieldId: string,
    value: string
  ) => {
    const key = repeatableEntriesKey(sectionId, groupId);
    const rows = [...(this.state.repeatableValues[key] || [])];
    if (!rows[rowIndex]) return;
    const nextRows = [...rows];
    nextRows[rowIndex] = { ...nextRows[rowIndex], [fieldId]: value };
    this.setState((prev): RepeatableCellStateUpdate => ({
      repeatableValues: { ...prev.repeatableValues, [key]: nextRows },
      validationMessages: [],
      saveBanner: null,
    }));
  };

  parseOptionsFromText(text: string): string[] {
    return text
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  removeField = (sectionId: string, fieldId: string) => {
    const section = this.state.master.find((s) => s.id === sectionId);
    const field = section?.fields.find((f) => f.id === fieldId);
    if (field && isDeedGateFieldDef(field)) {
      this.removeDocumentTypeFromSection(sectionId, Number(field.deedMasterId));
      return;
    }
    this.setState((prev) => {
      const sec = prev.master.find((s) => s.id === sectionId);
      const field = sec?.fields.find((f) => f.id === fieldId);
      const fuKeys = collectFollowUpValueKeys(field?.optionFollowUps);
      const master = prev.master.map((s) =>
        s.id === sectionId ? { ...s, fields: s.fields.filter((f) => f.id !== fieldId) } : s
      );
      const checklistValues = { ...prev.checklistValues };
      delete checklistValues[fieldId];
      fuKeys.forEach((k) => {
        delete checklistValues[k];
      });
      const fieldVerified = { ...prev.fieldVerified };
      delete fieldVerified[fieldId];
      const key =
        prev.editingFieldKey === `${sectionId}:${fieldId}` ? null : prev.editingFieldKey;
      return { master: sortSections(master), checklistValues, fieldVerified, editingFieldKey: key };
    });
  };

  addFieldFromDraft = () => {
    const { newFieldDraft, master } = this.state;
    if (!newFieldDraft.label.trim()) return;
    const id = `f_${Date.now()}`;
    const section = master.find((s) => s.id === newFieldDraft.sectionId);
    const nextOrder = section ? Math.max(0, ...section.fields.map((f) => f.order)) + 1 : 1;
    const followUpBuilt = buildOptionFollowUpsFromDraft(
      id,
      newFieldDraft.type,
      newFieldDraft.dropdownFollowUpsEnabled,
      newFieldDraft.followUpRows,
      newFieldDraft.dropdownOptionsText,
      undefined
    );
    const options = followUpBuilt.options;
    const optionFollowUps = followUpBuilt.optionFollowUps;
    const dropdownFollowUpsEnabled = followUpBuilt.dropdownFollowUpsEnabled;
    const trimmedScrutinyId = newFieldDraft.scrutinyLinkedDeedId.trim();
    const field: ChecklistFieldDef = {
      id,
      label: newFieldDraft.label.trim(),
      type: newFieldDraft.type,
      required: newFieldDraft.required,
      disabled: newFieldDraft.disabled,
      order: nextOrder,
      layout: newFieldDraft.layout,
      options,
      documentMapKey: newFieldDraft.documentMapKey.trim() || undefined,
      labelTag: newFieldDraft.labelTag.trim() || undefined,
      dropdownFollowUpsEnabled,
      optionFollowUps,
      scrutinyLinkedDeedId: trimmedScrutinyId || undefined,
      dynamicValueSource:
        trimmedScrutinyId && newFieldDraft.dynamicValueSource === "front_office"
          ? "front_office"
          : undefined,
      showVerifyCheckbox:
        newFieldDraft.showVerifyCheckbox && newFieldDraft.type !== "yes_no",
    };
    const initialVal =
      newFieldDraft.type === "checkbox" ? "false" : newFieldDraft.type === "yes_no" ? "No" : "";
    const extraVals: Record<string, string> = { [id]: initialVal };
    initChecklistSliceForFollowUpTree(optionFollowUps, extraVals);
    const fieldVerified =
      field.showVerifyCheckbox
        ? { ...this.state.fieldVerified, [id]: "false" }
        : this.state.fieldVerified;
    this.setState((prev) => ({
      master: sortSections(
        prev.master.map((s) =>
          s.id === newFieldDraft.sectionId ? { ...s, fields: [...s.fields, field] } : s
        )
      ),
      checklistValues: {
        ...prev.checklistValues,
        ...extraVals,
      },
      fieldVerified,
      newFieldDraft: {
        ...defaultNewFieldDraft(
          newFieldDraft.sectionId,
          section ? sectionFlowMode(section) : "checklist"
        ),
        sectionId: newFieldDraft.sectionId,
      },
    }));
  };

  addSection = () => {
    const title = this.state.newSectionTitle.trim();
    if (!title) return;
    const id = `sec_${Date.now()}`;
    const order = Math.max(0, ...this.state.master.map((s) => s.order)) + 1;
    const flowMode = this.state.newSectionFlowMode;
    this.setState((prev) => {
      const master = sortSections([
        ...prev.master,
        {
          id,
          title,
          order,
          flowMode,
          fields: [],
          collapsible: flowMode === "checklist",
          defaultExpanded: true,
          headerVariant: flowMode === "checklist" ? "warning" : "default",
          repeatableGroups: [],
          enabledDeedMasterIds: flowMode === "bank_product" || flowMode === "survey_verification" || flowMode === "ec_verification" ? undefined : [],
          enabledBankProductIds: flowMode === "bank_product" ? [] : undefined,
          enabledNaturePropertyKeys: flowMode === "survey_verification" ? [] : undefined,
        },
      ]);
      return {
        master,
        newSectionTitle: "",
        newFieldDraft: defaultNewFieldDraft(id, flowMode),
        documentTypeDraftBySection: { ...prev.documentTypeDraftBySection, [id]: "" },
        bankProductDraftBySection: { ...prev.bankProductDraftBySection, [id]: "" },
        naturePropertyDraftBySection: { ...prev.naturePropertyDraftBySection, [id]: "" },
        sectionUiOpen: { ...prev.sectionUiOpen, [id]: true },
      };
    });
  };

  onAddSectionFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    this.addSection();
  };

  /** Append an empty section with a default name (no title input required). */
  addBlankSectionAtEnd = () => {
    const id = `sec_${Date.now()}`;
    this.setState((prev) => {
      const order = Math.max(0, ...prev.master.map((s) => s.order)) + 1;
      const title = `New section ${order}`;
      const flowMode = prev.newSectionFlowMode;
      const master = sortSections([
        ...prev.master,
        {
          id,
          title,
          order,
          flowMode,
          fields: [],
          collapsible: flowMode === "checklist",
          defaultExpanded: true,
          headerVariant: flowMode === "checklist" ? "warning" : "default",
          repeatableGroups: [],
          enabledDeedMasterIds: flowMode === "bank_product" || flowMode === "survey_verification" || flowMode === "ec_verification" ? undefined : [],
          enabledBankProductIds: flowMode === "bank_product" ? [] : undefined,
          enabledNaturePropertyKeys: flowMode === "survey_verification" ? [] : undefined,
        },
      ]);
      return {
        master,
        newFieldDraft: defaultNewFieldDraft(id, flowMode),
        documentTypeDraftBySection: { ...prev.documentTypeDraftBySection, [id]: "" },
        bankProductDraftBySection: { ...prev.bankProductDraftBySection, [id]: "" },
        naturePropertyDraftBySection: { ...prev.naturePropertyDraftBySection, [id]: "" },
        sectionUiOpen: { ...prev.sectionUiOpen, [id]: true },
      };
    });
  };

  /** Insert an empty section immediately after the given section and renumber orders. */
  insertSectionAfter = (afterSectionId: string) => {
    const id = `sec_${Date.now()}`;
    this.setState((prev) => {
      const sorted = [...prev.master].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((s) => s.id === afterSectionId);
      const insertAt = idx < 0 ? sorted.length : idx + 1;
      const flowMode = prev.newSectionFlowMode;
      const newSection: ChecklistSectionDef = {
        id,
        title: "New section",
        order: 0,
        flowMode,
        fields: [],
        collapsible: flowMode === "checklist",
        defaultExpanded: true,
        headerVariant: flowMode === "checklist" ? "warning" : "default",
        repeatableGroups: [],
        enabledDeedMasterIds:
          flowMode === "bank_product" || flowMode === "survey_verification" || flowMode === "ec_verification" ? undefined : [],
        enabledBankProductIds: flowMode === "bank_product" ? [] : undefined,
        enabledNaturePropertyKeys: flowMode === "survey_verification" ? [] : undefined,
      };
      const merged = [...sorted.slice(0, insertAt), newSection, ...sorted.slice(insertAt)];
      const renumbered = merged.map((s, i) => ({ ...s, order: i + 1 }));
      return {
        master: sortSections(renumbered),
        newFieldDraft: defaultNewFieldDraft(id, flowMode),
        documentTypeDraftBySection: { ...prev.documentTypeDraftBySection, [id]: "" },
        bankProductDraftBySection: { ...prev.bankProductDraftBySection, [id]: "" },
        naturePropertyDraftBySection: { ...prev.naturePropertyDraftBySection, [id]: "" },
        sectionUiOpen: { ...prev.sectionUiOpen, [id]: true },
      };
    });
  };

  removeSection = (sectionId: string) => {
    const section = this.state.master.find((s) => s.id === sectionId);
    if (!section) return;
    const rgCount = (section.repeatableGroups || []).length;
    const msg =
      section.fields.length > 0 || rgCount > 0
        ? `Remove section "${section.title}" (${section.fields.length} field(s)${rgCount ? `, ${rgCount} repeatable group(s)` : ""
        })?`
        : `Remove empty section "${section.title}"?`;
    if (!window.confirm(msg)) return;
    this.setState((prev) => {
      const master = prev.master.filter((s) => s.id !== sectionId);
      const checklistValues = { ...prev.checklistValues };
      section.fields.forEach((f) => {
        delete checklistValues[f.id];
        collectFollowUpValueKeys(f.optionFollowUps).forEach((k) => {
          delete checklistValues[k];
        });
      });
      (section.repeatableGroups || []).forEach((g) => {
        g.fields.forEach((f) => {
          delete checklistValues[f.id];
        });
      });
      const prefix = `${sectionId}::`;
      const repeatableValues = { ...prev.repeatableValues };
      Object.keys(repeatableValues).forEach((k) => {
        if (k.startsWith(prefix)) delete repeatableValues[k];
      });
      const sectionUiOpen = { ...prev.sectionUiOpen };
      delete sectionUiOpen[sectionId];
      const fieldVerified = { ...prev.fieldVerified };
      section.fields.forEach((f) => {
        delete fieldVerified[f.id];
      });
      const documentTypeDraftBySection = { ...prev.documentTypeDraftBySection };
      delete documentTypeDraftBySection[sectionId];
      const newFieldDraft = {
        ...defaultNewFieldDraft(
          master[0]?.id ?? prev.newFieldDraft.sectionId,
          master[0] ? sectionFlowMode(master[0]) : "checklist"
        ),
      };
      return {
        master: sortSections(master),
        checklistValues,
        fieldVerified,
        repeatableValues,
        sectionUiOpen,
        documentTypeDraftBySection,
        newFieldDraft,
        editingFieldKey: null,
      };
    });
  };

  updateSectionTitle = (sectionId: string, title: string) => {
    this.setState((prev) => ({
      master: sortSections(
        prev.master.map((s) => (s.id === sectionId ? { ...s, title: title.trim() || s.title } : s))
      ),
    }));
  };

  moveSection = (sectionId: string, direction: -1 | 1) => {
    const sorted = [...this.state.master].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((s) => s.id === sectionId);
    const j = idx + direction;
    if (idx < 0 || j < 0 || j >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[j];
    this.setState((prev): MoveSectionStateUpdate => ({
      master: sortSections(
        prev.master.map((s) => {
          if (s.id === a.id) return { ...s, order: b.order };
          if (s.id === b.id) return { ...s, order: a.order };
          return s;
        })
      ),
    }));
  };

  moveField = (sectionId: string, fieldId: string, direction: -1 | 1) => {
    this.setState((prev) => ({
      master: prev.master.map((sec) => {
        if (sec.id !== sectionId) return sec;
        const next = reorderAdjacentFields(sec.fields, fieldId, direction);
        return next ? { ...sec, fields: next } : sec;
      }),
    }));
  };

  startEditField = (sectionId: string, field: ChecklistFieldDef) => {
    const followUpRows = followUpRowsFromFieldDef(field);
    this.setState({
      editingFieldKey: `${sectionId}:${field.id}`,
      fieldEditDraft: {
        label: field.label,
        type: field.type,
        required: !!field.required,
        disabled: !!field.disabled,
        documentMapKey: field.documentMapKey ?? "",
        labelTag: field.labelTag ?? "",
        dropdownOptionsText: (field.options || []).join("\n"),
        layout: field.layout ?? "half",
        dropdownFollowUpsEnabled: !!(
          fieldTypeSupportsOptionFollowUps(field.type) && field.dropdownFollowUpsEnabled
        ),
        followUpRows,
        showVerifyCheckbox: !!field.showVerifyCheckbox,
        scrutinyLinkedDeedId: normalizeDeedIdForSelect(field.scrutinyLinkedDeedId),
        dynamicValueSource: (field.dynamicValueSource ?? "tag") as DynamicValueSource,
      },
    });
  };

  cancelEditField = () => {
    this.setState({ editingFieldKey: null, fieldEditDraft: emptyFieldEditDraft() });
  };

  saveEditField = (sectionId: string, fieldId: string) => {
    const { fieldEditDraft } = this.state;
    if (!fieldEditDraft.label.trim()) return;
    this.setState((prev) => {
      const oldField = prev.master.find((s) => s.id === sectionId)?.fields.find((f) => f.id === fieldId);
      const d = fieldEditDraft;
      const followUpBuilt = buildOptionFollowUpsFromDraft(
        fieldId,
        d.type,
        d.dropdownFollowUpsEnabled,
        d.followUpRows,
        d.dropdownOptionsText,
        oldField
      );
      const nextOptions = followUpBuilt.options;
      const optionFollowUps = followUpBuilt.optionFollowUps;
      const dropdownFollowUpsEnabled = followUpBuilt.dropdownFollowUpsEnabled;

      let checklistValues = { ...prev.checklistValues };
      const oldKeys = collectFollowUpValueKeys(oldField?.optionFollowUps);
      const newKeySet = new Set(collectFollowUpValueKeys(optionFollowUps));
      oldKeys.forEach((k) => {
        if (!newKeySet.has(k)) {
          delete checklistValues[k];
        }
      });
      checklistValues = ensureFollowUpDefaultsInChecklist(optionFollowUps, checklistValues);
      if (!fieldTypeSupportsOptionFollowUps(d.type)) {
        collectFollowUpValueKeys(oldField?.optionFollowUps).forEach((k) => {
          delete checklistValues[k];
        });
      }

      const showVerify = d.showVerifyCheckbox && d.type !== "yes_no";
      const fieldVerified = { ...prev.fieldVerified };
      if (showVerify) {
        if (fieldVerified[fieldId] === undefined) {
          fieldVerified[fieldId] = "false";
        }
      } else {
        delete fieldVerified[fieldId];
      }

      const isDeedGateField =
        !!oldField &&
        oldField.type === "yes_no" &&
        oldField.deedMasterId != null &&
        oldField.id === gateFieldIdForDeed(Number(oldField.deedMasterId));

      return {
        master: sortSections(
          prev.master.map((s) => {
            if (s.id !== sectionId) return s;
            return {
              ...s,
              fields: s.fields.map((f) =>
                f.id === fieldId
                  ? {
                    ...f,
                    label: d.label.trim(),
                    type: d.type,
                    required: d.required,
                    disabled: d.disabled,
                    layout: d.layout,
                    documentMapKey: d.documentMapKey.trim() || undefined,
                    labelTag: d.labelTag.trim() || undefined,
                    options: d.type === "dropdown" ? nextOptions : undefined,
                    dropdownFollowUpsEnabled: fieldTypeSupportsOptionFollowUps(d.type)
                      ? dropdownFollowUpsEnabled
                      : false,
                    optionFollowUps: fieldTypeSupportsOptionFollowUps(d.type)
                      ? optionFollowUps
                      : undefined,
                    scrutinyLinkedDeedId: d.scrutinyLinkedDeedId.trim() || undefined,
                    dynamicValueSource:
                      d.scrutinyLinkedDeedId.trim() &&
                        d.dynamicValueSource === "front_office"
                        ? "front_office"
                        : undefined,
                    deedMasterId: isDeedGateField ? oldField!.deedMasterId : undefined,
                    showVerifyCheckbox: showVerify,
                  }
                  : f
              ),
            };
          })
        ),
        checklistValues,
        fieldVerified,
        editingFieldKey: null,
        fieldEditDraft: emptyFieldEditDraft(),
      };
    });
  };

  setFieldVerified = (fieldId: string, checked: boolean) => {
    this.setState((prev) => ({
      fieldVerified: { ...prev.fieldVerified, [fieldId]: checked ? "true" : "false" },
    }));
  };

  updateSectionFlowMode = (sectionId: string, flowMode: ChecklistSectionFlowMode) => {
    this.setState((prev) => {
      const nextDraft =
        prev.newFieldDraft.sectionId === sectionId
          ? {
            ...prev.newFieldDraft,
            layout: (flowMode === "checklist" ? "full" : "half") as ChecklistFieldLayout,
          }
          : prev.newFieldDraft;
      return {
        master: sortSections(
          prev.master.map((s) => {
            if (s.id !== sectionId) return s;
            let nextSection: ChecklistSectionDef = {
              ...s,
              flowMode,
              collapsible: flowMode === "checklist" ? s.collapsible ?? true : s.collapsible,
              headerVariant:
                flowMode === "checklist" ? s.headerVariant ?? "warning" : s.headerVariant,
              repeatableGroups: s.repeatableGroups ?? [],
              enabledDeedMasterIds:
                flowMode === "bank_product" ||
                flowMode === "survey_verification" ||
                flowMode === "ec_verification"
                  ? undefined
                  : s.enabledDeedMasterIds ?? [],
              enabledBankProductIds:
                flowMode === "bank_product" ? s.enabledBankProductIds ?? [] : undefined,
              enabledNaturePropertyKeys:
                flowMode === "survey_verification"
                  ? s.enabledNaturePropertyKeys ?? []
                  : undefined,
            };
            if (flowMode === "ec_verification") {
              nextSection = ensureEcVerificationGroups(nextSection);
            }
            return nextSection;
          })
        ),
        newFieldDraft: nextDraft,
      };
    });
  };

  removeDocumentTypeFromSection = (sectionId: string, deedId: number) => {
    const section = this.state.master.find((s) => s.id === sectionId);
    const deedName =
      formatDeedMasterLabel(deedId, this.state.deedMasterList) || `document type #${deedId}`;
    if (
      !window.confirm(
        `Remove "${deedName}" and all checklist fields under it from this section?`
      )
    ) {
      return;
    }
    if (!section) return;
    const gateId = gateFieldIdForDeed(deedId);
    const groupId = deedGroupIdForDeed(deedId);
    const group = section.repeatableGroups?.find((g) => g.id === groupId);
    this.setState((prev): RemoveDocumentTypeStateUpdate => {
      const checklistValues = { ...prev.checklistValues };
      delete checklistValues[gateId];
      group?.fields.forEach((f) => {
        delete checklistValues[f.id];
        collectFollowUpValueKeys(f.optionFollowUps).forEach((k) => {
          delete checklistValues[k];
        });
      });
      const repeatableValues = { ...prev.repeatableValues };
      delete repeatableValues[repeatableEntriesKey(sectionId, groupId)];
      const fieldVerified = { ...prev.fieldVerified };
      delete fieldVerified[gateId];
      const dk = deedGroupDraftKey(sectionId, groupId);
      const deedGroupFieldDrafts = { ...prev.deedGroupFieldDrafts };
      if (deedGroupFieldDrafts[dk]?.editingFieldId) {
        deedGroupFieldDrafts[dk] = defaultDeedGroupFieldDraft();
      }
      return {
        master: sortSections(
          prev.master.map((s) => {
            if (s.id !== sectionId) return s;
            return {
              ...s,
              fields: s.fields.filter((f) => f.id !== gateId),
              repeatableGroups: (s.repeatableGroups || []).filter((g) => g.id !== groupId),
              enabledDeedMasterIds: (s.enabledDeedMasterIds || []).filter((id) => id !== deedId),
            };
          })
        ),
        checklistValues,
        repeatableValues,
        fieldVerified,
        editingFieldKey:
          prev.editingFieldKey === `${sectionId}:${gateId}` ? null : prev.editingFieldKey,
        deedGroupFieldDrafts,
      };
    });
  };

  getDeedGroupFieldDraft(sectionId: string, groupId: string): DeedGroupFieldDraft {
    return (
      this.state.deedGroupFieldDrafts[deedGroupDraftKey(sectionId, groupId)] ??
      defaultDeedGroupFieldDraft()
    );
  }

  setDeedGroupFieldDraft = (
    sectionId: string,
    groupId: string,
    patch: Partial<DeedGroupFieldDraft>
  ) => {
    const dk = deedGroupDraftKey(sectionId, groupId);
    this.setState((prev) => ({
      deedGroupFieldDrafts: {
        ...prev.deedGroupFieldDrafts,
        [dk]: { ...(prev.deedGroupFieldDrafts[dk] ?? defaultDeedGroupFieldDraft()), ...patch },
      },
    }));
  };

  startEditDeedGroupField = (
    sectionId: string,
    groupId: string,
    field: ChecklistFieldDef
  ) => {
    const dk = deedGroupDraftKey(sectionId, groupId);
    this.setState({
      deedGroupFieldDrafts: {
        ...this.state.deedGroupFieldDrafts,
        [dk]: {
          label: field.label,
          labelTag: field.labelTag ?? "",
          type: field.type,
          layout: field.layout ?? "third",
          required: !!field.required,
          disabled: !!field.disabled,
          showVerifyCheckbox: !!field.showVerifyCheckbox,
          scrutinyLinkedDeedId: normalizeDeedIdForSelect(field.scrutinyLinkedDeedId),
          dynamicValueSource: (field.dynamicValueSource ?? "tag") as DynamicValueSource,
          dropdownOptionsText: (field.options || []).join("\n"),
          dropdownFollowUpsEnabled: !!(
            fieldTypeSupportsOptionFollowUps(field.type) && field.dropdownFollowUpsEnabled
          ),
          followUpRows: followUpRowsFromFieldDef(field),
          editingFieldId: field.id,
        },
      },
    });
  };

  cancelEditDeedGroupField = (sectionId: string, groupId: string) => {
    const dk = deedGroupDraftKey(sectionId, groupId);
    this.setState((prev) => ({
      deedGroupFieldDrafts: { ...prev.deedGroupFieldDrafts, [dk]: defaultDeedGroupFieldDraft() },
    }));
  };

  addFieldToDeedGroup = (sectionId: string, groupId: string) => {
    const dk = deedGroupDraftKey(sectionId, groupId);
    const draft = this.state.deedGroupFieldDrafts[dk] ?? defaultDeedGroupFieldDraft();
    if (!draft.label.trim()) return;
    if (draft.editingFieldId) {
      this.saveDeedGroupFieldEdit(sectionId, groupId, draft.editingFieldId, draft);
      return;
    }
    const groupLabel =
      this.state.master
        .find((s) => s.id === sectionId)
        ?.repeatableGroups?.find((g) => g.id === groupId)?.label ?? "document type";
    const fieldId = `f_dg_${Date.now()}`;
    const sec = this.state.master.find((s) => s.id === sectionId);
    const group = sec?.repeatableGroups?.find((g) => g.id === groupId);
    if (!sec || !group) return;
    const nextOrder = group.fields.length
      ? Math.max(...group.fields.map((f) => f.order)) + 1
      : 1;
    const isFlatGroupField =
      group.bankProductId != null ||
      group.naturePropertyKey != null;
    const trimmedDraftScrutinyId = draft.scrutinyLinkedDeedId.trim();
    const followUpBuilt = buildOptionFollowUpsFromDraft(
      fieldId,
      draft.type,
      draft.dropdownFollowUpsEnabled,
      draft.followUpRows,
      draft.dropdownOptionsText,
      undefined
    );
    const field: ChecklistFieldDef = {
      id: fieldId,
      label: draft.label.trim(),
      labelTag: draft.labelTag.trim() || undefined,
      type: draft.type,
      required: draft.required,
      disabled: draft.disabled,
      order: nextOrder,
      layout: draft.layout,
      options: followUpBuilt.options,
      dropdownFollowUpsEnabled: followUpBuilt.dropdownFollowUpsEnabled,
      optionFollowUps: followUpBuilt.optionFollowUps,
      deedMasterId: group.deedMasterId,
      bankProductId: group.bankProductId,
      scrutinyLinkedDeedId: trimmedDraftScrutinyId || undefined,
      dynamicValueSource:
        trimmedDraftScrutinyId && draft.dynamicValueSource === "front_office"
          ? "front_office"
          : undefined,
      showVerifyCheckbox: draft.showVerifyCheckbox && draft.type !== "yes_no",
    };
    const initialVal =
      field.type === "checkbox" ? "false" : field.type === "yes_no" ? "No" : "";
    const extraVals: Record<string, string> = { [fieldId]: initialVal };
    initChecklistSliceForFollowUpTree(field.optionFollowUps, extraVals);
    const rk = repeatableEntriesKey(sectionId, groupId);
    if (isFlatGroupField) {
      this.setState((prev): DeedGroupFieldAddBankStateUpdate => ({
        master: sortSections(
          prev.master.map((s) =>
            s.id === sectionId
              ? {
                ...s,
                repeatableGroups: (s.repeatableGroups || []).map((g) =>
                  g.id === groupId ? { ...g, fields: [...g.fields, field] } : g
                ),
              }
              : s
          )
        ),
        checklistValues: { ...prev.checklistValues, ...extraVals },
        deedGroupFieldDrafts: {
          ...prev.deedGroupFieldDrafts,
          [dk]: defaultDeedGroupFieldDraft(),
        },
      }));
    } else {
      this.setState((prev): DeedGroupFieldAddDeedStateUpdate => {
        const baseRows = prev.repeatableValues[rk]?.length
          ? prev.repeatableValues[rk]
          : [emptyRepeatableRow(group.fields)];
        return {
          master: sortSections(
            prev.master.map((s) =>
              s.id === sectionId
                ? {
                  ...s,
                  repeatableGroups: (s.repeatableGroups || []).map((g) =>
                    g.id === groupId ? { ...g, fields: [...g.fields, field] } : g
                  ),
                }
                : s
            )
          ),
          checklistValues: { ...prev.checklistValues, ...extraVals },
          deedGroupFieldDrafts: {
            ...prev.deedGroupFieldDrafts,
            [dk]: defaultDeedGroupFieldDraft(),
          },
          repeatableValues: {
            ...prev.repeatableValues,
            [rk]: baseRows.map((row) => ({ ...row, [fieldId]: initialVal })),
          },
        };
      });
    }
    ToastsStore.success(`Added "${draft.label.trim()}" to ${groupLabel}.`);
  };

  saveDeedGroupFieldEdit = (
    sectionId: string,
    groupId: string,
    fieldId: string,
    draft: DeedGroupFieldDraft
  ) => {
    const dk = deedGroupDraftKey(sectionId, groupId);
    const groupLabel =
      this.state.master
        .find((s) => s.id === sectionId)
        ?.repeatableGroups?.find((g) => g.id === groupId)?.label ?? "document type";
    const sec = this.state.master.find((s) => s.id === sectionId);
    const group = sec?.repeatableGroups?.find((g) => g.id === groupId);
    const oldField = group?.fields.find((f) => f.id === fieldId);
    if (!sec || !group || !oldField) return;
    this.setState((prev): DeedGroupFieldEditStateUpdate => {
      const trimmedDraftScrutinyId = draft.scrutinyLinkedDeedId.trim();
      const followUpBuilt = buildOptionFollowUpsFromDraft(
        fieldId,
        draft.type,
        draft.dropdownFollowUpsEnabled,
        draft.followUpRows,
        draft.dropdownOptionsText,
        oldField
      );
      let checklistValues = { ...prev.checklistValues };
      const oldKeys = collectFollowUpValueKeys(oldField.optionFollowUps);
      const newKeySet = new Set(collectFollowUpValueKeys(followUpBuilt.optionFollowUps));
      oldKeys.forEach((k) => {
        if (!newKeySet.has(k)) delete checklistValues[k];
      });
      checklistValues = ensureFollowUpDefaultsInChecklist(
        followUpBuilt.optionFollowUps,
        checklistValues
      );
      if (!fieldTypeSupportsOptionFollowUps(draft.type)) {
        collectFollowUpValueKeys(oldField.optionFollowUps).forEach((k) => {
          delete checklistValues[k];
        });
      }
      const updated: ChecklistFieldDef = {
        ...oldField,
        label: draft.label.trim(),
        labelTag: draft.labelTag.trim() || undefined,
        type: draft.type,
        required: draft.required,
        disabled: draft.disabled,
        layout: draft.layout,
        options: draft.type === "dropdown" ? followUpBuilt.options : undefined,
        dropdownFollowUpsEnabled: fieldTypeSupportsOptionFollowUps(draft.type)
          ? followUpBuilt.dropdownFollowUpsEnabled
          : false,
        optionFollowUps: fieldTypeSupportsOptionFollowUps(draft.type)
          ? followUpBuilt.optionFollowUps
          : undefined,
        scrutinyLinkedDeedId: trimmedDraftScrutinyId || undefined,
        dynamicValueSource:
          trimmedDraftScrutinyId && draft.dynamicValueSource === "front_office"
            ? "front_office"
            : undefined,
        showVerifyCheckbox: draft.showVerifyCheckbox && draft.type !== "yes_no",
      };

      return {
        master: sortSections(
          prev.master.map((s) =>
            s.id === sectionId
              ? {
                ...s,
                repeatableGroups: (s.repeatableGroups || []).map((g) =>
                  g.id === groupId
                    ? {
                      ...g,
                      fields: g.fields.map((f) => (f.id === fieldId ? updated : f)),
                    }
                    : g
                ),
              }
              : s
          )
        ),
        checklistValues,
        deedGroupFieldDrafts: { ...prev.deedGroupFieldDrafts, [dk]: defaultDeedGroupFieldDraft() },
      };
    });
    ToastsStore.success(`Updated "${draft.label.trim()}" in ${groupLabel}.`);
  };

  removeFieldFromDeedGroup = (sectionId: string, groupId: string, fieldId: string) => {
    this.setState((prev) => {
      const sec = prev.master.find((s) => s.id === sectionId);
      const group = sec?.repeatableGroups?.find((g) => g.id === groupId);
      const field = group?.fields.find((f) => f.id === fieldId);
      const checklistValues = { ...prev.checklistValues };
      delete checklistValues[fieldId];
      collectFollowUpValueKeys(field?.optionFollowUps).forEach((k) => {
        delete checklistValues[k];
      });
      const rk = repeatableEntriesKey(sectionId, groupId);
      const repeatableValues = { ...prev.repeatableValues };
      if (repeatableValues[rk]) {
        repeatableValues[rk] = repeatableValues[rk].map((row) => {
          const next = { ...row };
          delete next[fieldId];
          return next;
        });
      }
      return {
        master: sortSections(
          prev.master.map((s) =>
            s.id === sectionId
              ? {
                ...s,
                repeatableGroups: (s.repeatableGroups || []).map((g) =>
                  g.id === groupId
                    ? { ...g, fields: g.fields.filter((f) => f.id !== fieldId) }
                    : g
                ),
              }
              : s
          )
        ),
        checklistValues,
        repeatableValues,
        editingFieldKey:
          prev.editingFieldKey === `${sectionId}:${fieldId}` ? null : prev.editingFieldKey,
        deedGroupFieldDrafts: (() => {
          const dk = deedGroupDraftKey(sectionId, groupId);
          const draft = prev.deedGroupFieldDrafts[dk];
          if (draft?.editingFieldId === fieldId) {
            return { ...prev.deedGroupFieldDrafts, [dk]: defaultDeedGroupFieldDraft() };
          }
          return prev.deedGroupFieldDrafts;
        })(),
      };
    });
  };

  applyDeedGroupFieldOrder = (
    sectionId: string,
    groupId: string,
    fields: ChecklistFieldDef[]
  ) => {
    this.setState((prev) => ({
      master: prev.master.map((sec) =>
        sec.id !== sectionId
          ? sec
          : {
              ...sec,
              repeatableGroups: (sec.repeatableGroups || []).map((g) =>
                g.id === groupId ? { ...g, fields } : g
              ),
            }
      ),
    }));
  };

  /**
   * Recursively clones a follow-up subtree and rewrites `valueKey`s so they are
   * scoped to the new (cloned) parent field id. Mirrors the indexing convention
   * used by `buildSavedBranches` (`${fieldId}_fu${pathSuffix}_${idx}`).
   */
  cloneFollowUpsWithFreshKeys = (
    nodes: DropdownOptionFollowUpDef[] | undefined,
    newFieldId: string,
    pathSuffix: string
  ): DropdownOptionFollowUpDef[] | undefined => {
    if (!nodes?.length) return undefined;
    return nodes.map((n, idx) => ({
      ...n,
      valueKey: `${newFieldId}_fu${pathSuffix}_${idx}`,
      followUpOptions: n.followUpOptions ? [...n.followUpOptions] : undefined,
      nestedFollowUps: this.cloneFollowUpsWithFreshKeys(
        n.nestedFollowUps,
        newFieldId,
        `${pathSuffix}_${idx}n`
      ),
    }));
  };

  /**
   * Returns a deep copy of `field` with a brand-new `id` and all dependent
   * follow-up `valueKey`s regenerated so the clone never collides with the
   * original (or any sibling clone) at runtime.
   */
  cloneFieldWithFreshIds = (
    field: ChecklistFieldDef,
    newFieldId: string
  ): ChecklistFieldDef => ({
    ...field,
    id: newFieldId,
    options: field.options ? [...field.options] : undefined,
    optionFollowUps: this.cloneFollowUpsWithFreshKeys(field.optionFollowUps, newFieldId, ""),
  });

  /**
   * Returns initial `checklistValues` entries for a freshly cloned field tree
   * (the field itself plus every follow-up `valueKey`, walked recursively).
   */
  seedValuesForClonedField = (field: ChecklistFieldDef): Record<string, string> => {
    const out: Record<string, string> = {};
    const initialVal = (t: ChecklistFieldType) =>
      t === "checkbox" ? "false" : t === "yes_no" ? "No" : "";
    out[field.id] = initialVal(field.type);
    const walk = (nodes: DropdownOptionFollowUpDef[] | undefined) => {
      if (!nodes?.length) return;
      nodes.forEach((n) => {
        out[n.valueKey] = initialVal(n.questionType);
        walk(n.nestedFollowUps);
      });
    };
    walk(field.optionFollowUps);
    return out;
  };

  /**
   * Lists every valid duplicate target across the master, excluding:
   *   - the source itself (single-field mode), and
   *   - any destination that already contains a field with the same label
   *     as any source field (case-insensitive).
   *
   * Pass one source key for single duplicate, or many for bulk duplicate.
   */
  enumerateDuplicateTargets = (
    sourceKeyOrKeys: string | string[]
  ): { value: string; label: string }[] => {
    const sourceKeys = Array.isArray(sourceKeyOrKeys) ? sourceKeyOrKeys : [sourceKeyOrKeys];
    const sourceFields = sourceKeys
      .map((key) => resolveFieldFromSourceKey(key, this.state.master))
      .filter((f): f is ChecklistFieldDef => !!f);
    const srcLabels = sourceFields
      .map((f) => (f.label || "").trim().toLowerCase())
      .filter(Boolean);
    const labelExistsIn = (fields: ChecklistFieldDef[]) =>
      srcLabels.length > 0 &&
      srcLabels.some((sl) =>
        fields.some((f) => (f.label || "").trim().toLowerCase() === sl)
      );

    const singleKey = sourceKeys.length === 1 ? sourceKeys[0] : "";
    const sourceSectionKey = singleKey.startsWith("field:")
      ? `section:${singleKey.split(":")[1]}`
      : "";
    const sourceGroupKey = singleKey.startsWith("dg:")
      ? singleKey.replace(/:[^:]+$/, "")
      : "";

    const out: { value: string; label: string }[] = [];
    this.state.master.forEach((s) => {
      const flow = sectionFlowMode(s);
      if (flow === "checklist") {
        const value = `section:${s.id}`;
        if (value !== sourceSectionKey && !labelExistsIn(s.fields)) {
          out.push({ value, label: `${s.title} (Section)` });
        }
      }
      (s.repeatableGroups || []).forEach((g) => {
        const value = `dg:${s.id}:${g.id}`;
        if (value !== sourceGroupKey && !labelExistsIn(g.fields)) {
          out.push({ value, label: `${s.title} → ${g.label}` });
        }
      });
    });
    return out;
  };

  applyDuplicateSourceKeys = (sourceKeys: string[], duplicateTargetKey: string) => {
    if (!sourceKeys.length || !duplicateTargetKey) return;

    let nextMaster = this.state.master;
    let nextChecklist = { ...this.state.checklistValues };
    let nextRepeatable = { ...this.state.repeatableValues };
    let nextVerified = { ...this.state.fieldVerified };
    const duplicatedLabels: string[] = [];
    const stamp = Date.now();

    sourceKeys.forEach((sourceKey, index) => {
      const sourceField = resolveFieldFromSourceKey(sourceKey, nextMaster);
      if (!sourceField) return;
      const result = this.insertClonedFieldAtTarget(
        nextMaster,
        nextChecklist,
        nextRepeatable,
        nextVerified,
        sourceField,
        duplicateTargetKey,
        `f_dup_${stamp}_${index}`
      );
      if (!result) return;
      nextMaster = result.master;
      nextChecklist = result.checklistValues;
      nextRepeatable = result.repeatableValues;
      nextVerified = result.fieldVerified;
      duplicatedLabels.push(sourceField.label);
    });

    if (!duplicatedLabels.length) return;

    this.setState({
      master: nextMaster,
      checklistValues: nextChecklist,
      repeatableValues: nextRepeatable,
      fieldVerified: nextVerified,
    });
    ToastsStore.success(
      duplicatedLabels.length === 1
        ? `Duplicated "${duplicatedLabels[0]}".`
        : `Duplicated ${duplicatedLabels.length} fields.`
    );
  };

  insertClonedFieldAtTarget = (
    master: ChecklistSectionDef[],
    checklistValues: Record<string, string>,
    repeatableValues: Record<string, Record<string, string>[]>,
    fieldVerified: Record<string, string>,
    sourceField: ChecklistFieldDef,
    duplicateTargetKey: string,
    newFieldId: string
  ): {
    master: ChecklistSectionDef[];
    checklistValues: Record<string, string>;
    repeatableValues: Record<string, Record<string, string>[]>;
    fieldVerified: Record<string, string>;
  } | null => {
    const cloned = this.cloneFieldWithFreshIds(sourceField, newFieldId);
    const seedVals = this.seedValuesForClonedField(cloned);

    if (duplicateTargetKey.startsWith("section:")) {
      const [, targetSectionId] = duplicateTargetKey.split(":");
      const nextMaster = master.map((s) => {
        if (s.id !== targetSectionId) return s;
        const nextOrder = s.fields.length ? Math.max(...s.fields.map((f) => f.order)) + 1 : 1;
        const finalField: ChecklistFieldDef = {
          ...cloned,
          order: nextOrder,
          deedMasterId: undefined,
          bankProductId: undefined,
        };
        return { ...s, fields: [...s.fields, finalField] };
      });
      const nextVerified = cloned.showVerifyCheckbox
        ? { ...fieldVerified, [cloned.id]: "false" }
        : fieldVerified;
      return {
        master: sortSections(nextMaster),
        checklistValues: { ...checklistValues, ...seedVals },
        repeatableValues,
        fieldVerified: nextVerified,
      };
    }

    if (duplicateTargetKey.startsWith("dg:")) {
      const [, targetSectionId, targetGroupId] = duplicateTargetKey.split(":");
      const targetSec = master.find((s) => s.id === targetSectionId);
      const targetGroup = targetSec?.repeatableGroups?.find((g) => g.id === targetGroupId);
      if (!targetSec || !targetGroup) return null;

      const rk = repeatableEntriesKey(targetSectionId, targetGroupId);
      const nextMaster = master.map((s) => {
        if (s.id !== targetSectionId) return s;
        return {
          ...s,
          repeatableGroups: (s.repeatableGroups || []).map((g) => {
            if (g.id !== targetGroupId) return g;
            const nextOrder = g.fields.length ? Math.max(...g.fields.map((f) => f.order)) + 1 : 1;
            const finalField: ChecklistFieldDef = {
              ...cloned,
              order: nextOrder,
              deedMasterId: g.deedMasterId,
              bankProductId: g.bankProductId,
              showVerifyCheckbox: undefined,
            };
            return { ...g, fields: [...g.fields, finalField] };
          }),
        };
      });
      const baseRows = repeatableValues[rk]?.length ? repeatableValues[rk] : [{}];
      const initialVal =
        cloned.type === "checkbox" ? "false" : cloned.type === "yes_no" ? "No" : "";
      return {
        master: sortSections(nextMaster),
        checklistValues: { ...checklistValues, ...seedVals },
        repeatableValues: {
          ...repeatableValues,
          [rk]: baseRows.map((row) => ({ ...row, [cloned.id]: initialVal })),
        },
        fieldVerified,
      };
    }

    return null;
  };

  validateVisibleChecklist(): string[] {
    const missing: string[] = [];
    const sections = this.visibleSections();

    const checkVal = (f: ChecklistFieldDef, val: string | undefined): boolean => {
      if (!f.required || f.disabled) return true;
      return isChecklistAnswerFilled(f.type, val);
    };

    sections.forEach((s) => {
      const groupFieldIds = fieldIdsInDeedGroups(s);
      s.fields.forEach((f) => {
        if (groupFieldIds.has(f.id)) return;
        if (!checkVal(f, this.state.checklistValues[f.id])) {
          missing.push(`${s.title} → ${f.label}`);
        }
        if (
          f.dropdownFollowUpsEnabled &&
          f.optionFollowUps?.length &&
          fieldTypeSupportsOptionFollowUps(f.type)
        ) {
          const branch = triggerFollowBranchKey(f, this.state.checklistValues);
          if (f.type === "dropdown" && !branch) return;
          collectVisibleFollowUpValidationMissing(
            f.optionFollowUps,
            branch,
            f.type,
            this.state.checklistValues,
            `${s.title} → ${f.label}`,
            missing,
            f.disabled
          );
        }
      });
      (s.repeatableGroups || []).forEach((g) => {
        if (g.bankProductId != null || g.naturePropertyKey != null) {
          g.fields.forEach((f) => {
            if (!checkVal(f, this.state.checklistValues[f.id])) {
              missing.push(`${s.title} → ${g.label} → ${f.label}`);
            }
            if (
              f.dropdownFollowUpsEnabled &&
              f.optionFollowUps?.length &&
              fieldTypeSupportsOptionFollowUps(f.type)
            ) {
              const branch = triggerFollowBranchKey(f, this.state.checklistValues);
              if (f.type === "dropdown" && !branch) return;
              collectVisibleFollowUpValidationMissing(
                f.optionFollowUps,
                branch,
                f.type,
                this.state.checklistValues,
                `${s.title} → ${g.label} → ${f.label}`,
                missing,
                f.disabled
              );
            }
          });
          return;
        }
        if (g.gateFieldId) {
          const gateVal = this.state.checklistValues[g.gateFieldId] ?? "No";
          if (gateVal !== "Yes") return;
        }
        const key = repeatableEntriesKey(s.id, g.id);
        const rows = this.state.repeatableValues[key] || [];
        rows.forEach((row, idx) => {
          g.fields.forEach((f) => {
            if (!checkVal(f, row[f.id])) {
              missing.push(`${s.title} → ${g.label} #${idx + 1} → ${f.label}`);
            }
          });
        });
      });
    });
    return missing;
  };

  /**
   * Runtime preview's "Validate & save" button:
   *   1. Runs the same visible-field validation as before.
   *   2. On success, persists ONLY the master configuration via
   *      `dynamic_checklist_master_save.php`. The preview tab has no file_id
   *      context, so runtime answers (`checklistValues`, `repeatableValues`,
   *      `fieldVerified`) are intentionally not saved here. Use the dedicated
   *      "Save master setup" button on the Master setup tab for the same effect.
   */
  handleSaveChecklist = () => {
    const validationMessages = this.validateVisibleChecklist();
    if (validationMessages.length) {
      this.setState({
        validationMessages,
        saveBanner: {
          variant: "danger",
          text: "Fix required fields before saving.",
        },
      });
      return;
    }
    this.setState({ validationMessages: [], saveBanner: null });
    this.saveDynamicChecklistMaster();
  };

  visibleSections(): ChecklistSectionDef[] {
    return sortSections(this.state.master);
  }

  renderFieldInput(
    field: ChecklistFieldDef,
    valueOverride?: string,
    onChangeOverride?: (v: string) => void,
    radioNameSuffix?: string
  ) {
    const isDisabled = !!field.disabled;
    const v =
      valueOverride !== undefined ? valueOverride : this.state.checklistValues[field.id] ?? "";
    const onChange =
      onChangeOverride ?? ((val: string) => this.setChecklistValue(field.id, val));
    const rn = radioNameSuffix ?? field.id;
    switch (field.type) {
      case "dropdown":
        return (
          <SearchableSelect
            value={v}
            onChange={onChange}
            isDisabled={isDisabled}
            options={(field.options || []).map((opt) => ({ value: opt, label: opt }))}
            ariaLabel={field.label}
          />
        );
      case "date":
        return (
          <Form.Control
            type="date"
            value={v}
            disabled={isDisabled}
            onChange={(e) => onChange(e.target.value)}
            size="sm"
          />
        );
      case "checkbox":
        return (
          <Form.Check
            type="checkbox"
            checked={v === "true"}
            disabled={isDisabled}
            onChange={(e) => onChange(e.target.checked ? "true" : "false")}
            label="Yes"
          />
        );
      case "yes_no":
        return (
          <div className="d-flex align-items-center flex-wrap" style={{ gap: "0.75rem" }}>
            <Form.Check
              type="radio"
              name={`yn-${rn}`}
              id={`yn-yes-${rn}`}
              label="Yes"
              checked={v === "Yes"}
              disabled={isDisabled}
              onChange={() => onChange("Yes")}
            />
            <Form.Check
              type="radio"
              name={`yn-${rn}`}
              id={`yn-no-${rn}`}
              label="No"
              checked={v === "No"}
              disabled={isDisabled}
              onChange={() => onChange("No")}
            />
          </div>
        );
      case "remarks":
        return (
          <Form.Control
            as="textarea"
            rows={2}
            value={v}
            disabled={isDisabled}
            onChange={(e) => onChange(e.target.value)}
            size="sm"
          />
        );
      default:
        return (
          <Form.Control
            type="text"
            value={v}
            disabled={isDisabled}
            onChange={(e) => onChange(e.target.value)}
            size="sm"
          />
        );
    }
  }

  renderConditionalFollowUps(field: ChecklistFieldDef) {
    if (!field.dropdownFollowUpsEnabled || !field.optionFollowUps?.length) return null;
    if (!fieldTypeSupportsOptionFollowUps(field.type)) return null;
    const selected = triggerFollowBranchKey(field, this.state.checklistValues);
    if (field.type === "dropdown" && !selected) return null;
    const fu = field.optionFollowUps.find((x) => x.whenOption === selected);
    if (!fu?.questionLabel.trim()) return null;
    const branchLabel = labelForParentFollowBranch(field.type, fu.whenOption);
    const synthetic: ChecklistFieldDef = {
      id: fu.valueKey,
      label: fu.questionLabel,
      type: fu.questionType,
      order: 0,
      required: fu.required,
      options:
        fu.questionType === "dropdown"
          ? fu.followUpOptions?.length
            ? [...fu.followUpOptions]
            : ["Option A", "Option B"]
          : undefined,
    };
    return (
      <div className="dvc-follow-up-shell mt-2 pt-2 border-top">
        <div className="dvc-follow-up-option mb-1">If “{branchLabel}”</div>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1 font-weight-bold d-block">
            {fu.questionLabel}
            {fu.required && <span className="text-danger"> *</span>}
          </Form.Label>
          {this.renderFieldInput(
            synthetic,
            undefined,
            undefined,
            `${field.id}-fu-${fu.valueKey}`
          )}
          {this.renderNestedFollowUpsRuntime(fu, this.state.checklistValues)}
        </Form.Group>
      </div>
    );
  }

  renderNestedFollowUpsRuntime(
    parentFu: DropdownOptionFollowUpDef,
    checklist: Record<string, string>
  ): React.ReactNode {
    if (!parentFu.nestedFollowUpsEnabled || !parentFu.nestedFollowUps?.length) return null;
    if (!questionTypeSupportsNestedFollowUps(parentFu.questionType)) return null;
    const sub = branchKeyForFollowUpAnswer(parentFu, checklist);
    if (parentFu.questionType === "dropdown" && !sub) return null;
    if (parentFu.questionType === "yes_no" && !sub) return null;
    const n = parentFu.nestedFollowUps.find((x) => x.whenOption === sub);
    if (!n?.questionLabel.trim()) return null;
    const branchLabel = labelForParentFollowBranch(parentFu.questionType, n.whenOption);
    const synthetic: ChecklistFieldDef = {
      id: n.valueKey,
      label: n.questionLabel,
      type: n.questionType,
      order: 0,
      required: n.required,
      options:
        n.questionType === "dropdown"
          ? n.followUpOptions?.length
            ? [...n.followUpOptions]
            : ["Option A", "Option B"]
          : undefined,
    };
    return (
      <div className="dvc-follow-up-nested mt-2 pl-3 border-left border-primary">
        <div className="dvc-follow-up-option mb-1 small">Then if “{branchLabel}”</div>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1 font-weight-bold d-block">
            {n.questionLabel}
            {n.required && <span className="text-danger"> *</span>}
          </Form.Label>
          {this.renderFieldInput(
            synthetic,
            undefined,
            undefined,
            `${parentFu.valueKey}-nested-${n.valueKey}`
          )}
          {this.renderNestedFollowUpsRuntime(n, checklist)}
        </Form.Group>
      </div>
    );
  }

  /**
   * Renders the "Dynamic field value" select alongside a "Use Front Office field"
   * checkbox. When the checkbox is toggled the previously-selected value is cleared
   * because the two masters use disjoint id namespaces.
   */
  renderDynamicFieldValueSelect(
    value: string,
    onChange: (next: string) => void,
    source: DynamicValueSource,
    onSourceChange: (next: DynamicValueSource) => void,
    idSuffix = "default",
    trailingAction?: React.ReactNode,
    layoutOpts?: { checkboxAbove?: boolean }
  ) {
    const checkboxAbove = !!layoutOpts?.checkboxAbove;
    const isFrontOffice = source === "front_office";
    const options = isFrontOffice
      ? this.state.frontOfficeFields.map((f) => ({ value: f.id, label: f.name }))
      : this.combinedDynamicValueTags().map((d) => ({
        value: String(d.id),
        label: d.name,
      }));
    const placeholder = isFrontOffice
      ? this.state.frontOfficeFields.length === 0
        ? "No front office fields yet."
        : "Select a front office field..."
      : "Select...";
    const sourceToggle = (
      <Form.Check
        id={`dvc-dfv-src-${idSuffix}`}
        type="checkbox"
        className={`small dvc-dfv-source-toggle${checkboxAbove ? " dvc-dfv-source-toggle--above" : ""}`}
        checked={isFrontOffice}
        onChange={(e) =>
          onSourceChange(e.target.checked ? "front_office" : "tag")
        }
        label="Use Front Office field"
      />
    );
    const valueLabel = (
      <Form.Label className="small mb-1 d-block">
        Dynamic field value{" "}
        <span className="text-muted font-weight-normal">(optional)</span>
      </Form.Label>
    );
    const selectControl = (
      <SearchableSelect
        inputId={`dvc-dfv-${idSuffix}`}
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
      />
    );

    return (
      <Form.Group className="mb-0">
        {checkboxAbove ? (
          <>
            {valueLabel}
            {sourceToggle}
            {selectControl}
          </>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap">
              <Form.Label className="small mb-0">
                Dynamic field value{" "}
                <span className="text-muted font-weight-normal">(optional)</span>
              </Form.Label>
              {sourceToggle}
            </div>
            {trailingAction ? (
              <Row className="dvc-dfv-with-action-row align-items-center">
                <Col xs={12} sm={8} md={9} lg={10} className="dvc-dfv-select-col">
                  {selectControl}
                </Col>
                <Col
                  xs={12}
                  sm={4}
                  md={3}
                  lg={2}
                  className="dvc-dfv-action-col d-flex align-items-center justify-content-start justify-content-sm-end mt-2 mt-sm-0"
                >
                  {trailingAction}
                </Col>
              </Row>
            ) : (
              selectControl
            )}
          </>
        )}
      </Form.Group>
    );
  }

  renderFollowUpConfigPanel(opts: {
    fieldType: ChecklistFieldType;
    dropdownOptionsText: string;
    dropdownFollowUpsEnabled: boolean;
    followUpRows: DropdownFollowUpDraftRow[];
    controlIdPrefix: string;
    onPatch: (patch: Partial<{
      dropdownOptionsText: string;
      dropdownFollowUpsEnabled: boolean;
      followUpRows: DropdownFollowUpDraftRow[];
    }>) => void;
  }): React.ReactNode {
    const {
      fieldType,
      dropdownOptionsText,
      dropdownFollowUpsEnabled,
      followUpRows,
      controlIdPrefix,
      onPatch,
    } = opts;
    if (!fieldTypeSupportsOptionFollowUps(fieldType)) return null;
    return (
      <>
        {fieldType === "dropdown" && (
          <Col md={12}>
            <Form.Group className="mb-2">
              <Form.Label className="small">Dropdown options (one per line)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={dropdownOptionsText}
                onChange={(e) => {
                  const text = e.target.value;
                  const parsed = this.parseOptionsFromText(text);
                  onPatch({
                    dropdownOptionsText: text,
                    followUpRows: syncFollowUpRowsToOptions(parsed, followUpRows),
                  });
                }}
              />
            </Form.Group>
          </Col>
        )}
        {fieldType === "checkbox" && (
          <Col md={12}>
            <p className="small text-muted mb-2">
              Follow-ups use <strong>Checked</strong> and <strong>Unchecked</strong> branches.
            </p>
          </Col>
        )}
        {fieldType === "yes_no" && (
          <Col md={12}>
            <p className="small text-muted mb-2">
              Follow-ups use <strong>Yes</strong> and <strong>No</strong> branches.
            </p>
          </Col>
        )}
        {fieldType === "text" && (
          <Col md={12}>
            <p className="small text-muted mb-2">
              Follow-ups use <strong>Text filled</strong> and <strong>Text empty</strong>{" "}
              branches (when the user has entered text vs left the field blank).
            </p>
          </Col>
        )}
        <Col md={12}>
          <Form.Group className="mb-2">
            <Form.Check
              type="checkbox"
              id={`${controlIdPrefix}-follow-ups`}
              checked={dropdownFollowUpsEnabled}
              onChange={(e) => {
                const checked = e.target.checked;
                const branchKeys = followUpBranchKeysForFieldType(
                  fieldType,
                  undefined,
                  dropdownOptionsText
                );
                onPatch({
                  dropdownFollowUpsEnabled: checked,
                  followUpRows: checked
                    ? syncFollowUpRowsToOptions(branchKeys, followUpRows)
                    : followUpRows,
                });
              }}
              label="Ask a follow-up question based on the field value"
            />
          </Form.Group>
          {dropdownFollowUpsEnabled && (
            <div className="dvc-follow-up-panel w-100 mb-2">
              <div className="dvc-follow-up-panel__title">
                {fieldType === "checkbox"
                  ? "Follow-up when checked / unchecked"
                  : fieldType === "yes_no"
                    ? "Follow-up when Yes / No"
                    : fieldType === "text"
                      ? "Follow-up when text filled / empty"
                      : "Follow-up per option"}
              </div>
              {this.renderFollowUpDraftRows({
                rows: followUpRows,
                setRows: (next) => onPatch({ followUpRows: next }),
                branchLabelType: fieldType,
                depth: 0,
              })}
            </div>
          )}
        </Col>
      </>
    );
  }

  renderFollowUpDraftRows(opts: {
    rows: DropdownFollowUpDraftRow[];
    setRows: (next: DropdownFollowUpDraftRow[]) => void;
    branchLabelType: ChecklistFieldType;
    depth: number;
  }): React.ReactNode {
    const { rows, setRows, branchLabelType, depth } = opts;
    if (depth >= MAX_FOLLOW_UP_NEST_DEPTH) {
      return (
        <p className="small text-muted mb-0">Maximum follow-up depth ({MAX_FOLLOW_UP_NEST_DEPTH}) reached.</p>
      );
    }
    return rows.map((row, idx) => (
      <div key={`dvc-fu-${depth}-${row.whenOption}-${idx}`} className="dvc-follow-up-draft-card">
        <div className="dvc-follow-up-draft-card__header">
          <span className="dvc-follow-up-branch-badge">
            {labelForParentFollowBranch(branchLabelType, row.whenOption)}
          </span>
        </div>

        <Row className="mb-2 align-items-end">
          <Col xs={12} md={6}>
            <Form.Group className="mb-md-0 mb-2">
              <Form.Label className="small">Follow-up question</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Leave empty to skip"
                value={row.questionLabel}
                onChange={(e) => {
                  const v = e.target.value;
                  setRows(rows.map((r, i) => (i === idx ? { ...r, questionLabel: v } : r)));
                }}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-0">
              <Form.Label className="small">Answer type</Form.Label>
              <SearchableSelect
                value={row.questionType}
                isClearable={false}
                options={FOLLOW_UP_ANSWER_TYPES.map((qt) => ({ value: qt, label: qt }))}
                onChange={(val) => {
                  const qt = val as ChecklistFieldType;
                  setRows(
                    rows.map((r, i) =>
                      i === idx
                        ? {
                          ...r,
                          questionType: qt,
                          followUpDropdownOptionsText:
                            qt === "dropdown"
                              ? r.followUpDropdownOptionsText || "Option A\nOption B"
                              : r.followUpDropdownOptionsText,
                          nestedFollowUpsEnabled: questionTypeSupportsNestedFollowUps(qt)
                            ? r.nestedFollowUpsEnabled
                            : false,
                          nestedRows: questionTypeSupportsNestedFollowUps(qt)
                            ? r.nestedFollowUpsEnabled
                              ? syncFollowUpRowsToOptions(
                                nestedBranchKeysForDraftRow({ ...r, questionType: qt }),
                                r.nestedRows
                              )
                              : []
                            : [],
                        }
                        : r
                    )
                  );
                }}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-2 align-items-end">
          <Col xs={12} md={6}>
            {this.renderDynamicFieldValueSelect(
              row.scrutinyLinkedDeedId,
              (v) =>
                setRows(
                  rows.map((r, i) =>
                    i === idx ? { ...r, scrutinyLinkedDeedId: v } : r
                  )
                ),
              row.dynamicValueSource,
              (nextSource) =>
                setRows(
                  rows.map((r, i) =>
                    i === idx
                      ? {
                        ...r,
                        dynamicValueSource: nextSource,
                        scrutinyLinkedDeedId: "",
                      }
                      : r
                  )
                ),
              `fu-${depth}-${idx}`
            )}
          </Col>
          <Col xs={12} md={6} className="dvc-follow-up-required-col">
            <Form.Group className="mb-0">
              <Form.Label className="small d-block mb-1">Required</Form.Label>
              <Form.Check
                type="checkbox"
                checked={row.required}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setRows(rows.map((r, i) => (i === idx ? { ...r, required: checked } : r)));
                }}
                label="Yes"
              />
            </Form.Group>
          </Col>
        </Row>

        {row.questionType === "dropdown" && (
          <Row className="mb-2">
            <Col xs={12}>
              <Form.Group className="mb-0">
                <Form.Label className="small mb-1">Follow-up dropdown lines (one per line)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  value={row.followUpDropdownOptionsText}
                  onChange={(e) => {
                    const text = e.target.value;
                    setRows(
                      rows.map((r, i) => {
                        if (i !== idx) return r;
                        const nk = nestedBranchKeysForDraftRow({ ...r, followUpDropdownOptionsText: text });
                        const nestedRows =
                          r.nestedFollowUpsEnabled && nk.length
                            ? syncFollowUpRowsToOptions(nk, r.nestedRows)
                            : r.nestedRows;
                        return { ...r, followUpDropdownOptionsText: text, nestedRows };
                      })
                    );
                  }}
                />
              </Form.Group>
            </Col>
          </Row>
        )}

        {questionTypeSupportsNestedFollowUps(row.questionType) && (
          <Row className="mb-2">
            <Col xs={12}>
              <Form.Check
                type="checkbox"
                id={`dvc-nfu-${depth}-${idx}-${row.whenOption}`}
                checked={row.nestedFollowUpsEnabled}
                disabled={depth >= MAX_FOLLOW_UP_NEST_DEPTH - 1}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const nk = nestedBranchKeysForDraftRow(row);
                  setRows(
                    rows.map((r, i) => {
                      if (i !== idx) return r;
                      return {
                        ...r,
                        nestedFollowUpsEnabled: checked,
                        nestedRows: checked ? syncFollowUpRowsToOptions(nk, r.nestedRows) : [],
                      };
                    })
                  );
                }}
                label="Ask another follow-up based on this answer (nested)"
              />
            </Col>
          </Row>
        )}

        {row.nestedFollowUpsEnabled &&
          questionTypeSupportsNestedFollowUps(row.questionType) && (
            <div className="dvc-follow-up-nested-wrap">
              <div className="small font-weight-bold text-muted mb-2">Nested follow-up</div>
              {this.renderFollowUpDraftRows({
                rows: row.nestedRows,
                setRows: (nr) => setRows(replaceFollowUpNestedRows(rows, idx, nr)),
                branchLabelType: row.questionType,
                depth: depth + 1,
              })}
            </div>
          )}
      </div>
    ));
  }

  renderMasterProductGroupFieldCard(
    section: ChecklistSectionDef,
    g: ChecklistRepeatableGroupDef,
    opts: {
      cardClassName: string;
      fieldsSubtitle: string;
      onRemoveGroup?: () => void;
    }
  ) {
    const dgDraft = this.getDeedGroupFieldDraft(section.id, g.id);
    const isEditingGroupField = !!dgDraft.editingFieldId;
    return (
      <div key={g.id} className={`${opts.cardClassName} mb-3 p-3 border rounded bg-white`}>
        <div className="d-flex flex-wrap justify-content-between align-items-start mb-2">
          <div>
            <strong>{g.label}</strong>
            <span className="text-muted small d-block">
              {g.fields.length} checklist field{g.fields.length === 1 ? "" : "s"}
              {opts.fieldsSubtitle ? ` — ${opts.fieldsSubtitle}` : ""}
            </span>
          </div>
          {opts.onRemoveGroup && (
            <Button
              size="sm"
              variant="outline-danger"
              onClick={opts.onRemoveGroup}
            >
              Remove
            </Button>
          )}
        </div>
        {g.fields.length > 0 ? (
          <div className="dvc-field-table-wrap">
            <DeedGroupFieldTable
              section={section}
              group={g}
              editingFieldId={dgDraft.editingFieldId ?? null}
              enumerateTargets={this.enumerateDuplicateTargets}
              onDuplicate={this.applyDuplicateSourceKeys}
              onReorder={this.applyDeedGroupFieldOrder}
              onEdit={this.startEditDeedGroupField}
              onRemove={this.removeFieldFromDeedGroup}
              resolveDynamicFieldLabel={(field) =>
                formatDynamicFieldValueLabel(
                  field.scrutinyLinkedDeedId,
                  this.combinedDynamicValueTags(),
                  field.dynamicValueSource,
                  this.state.frontOfficeFields
                )
              }
            />
          </div>
        ) : (
          <p className="small text-muted mb-2">
            No checklist fields yet. Add fields using the form below.
          </p>
        )}
        <div className="dvc-deed-group-add-form">
          <div className="dvc-panel-heading">
            {isEditingGroupField
              ? `Edit checklist field in ${g.label}`
              : `Add checklist field to ${g.label}`}
          </div>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Label</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. Executant Derived by"
                  value={dgDraft.label}
                  onChange={(e) =>
                    this.setDeedGroupFieldDraft(section.id, g.id, { label: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small">Field type</Form.Label>
                <SearchableSelect
                  value={dgDraft.type}
                  isClearable={false}
                  options={FIELD_TYPES_RUNTIME.map((t) => ({ value: t, label: t }))}
                  onChange={(val) => {
                    const type = val as ChecklistFieldType;
                    this.setDeedGroupFieldDraft(section.id, g.id, {
                      type,
                      showVerifyCheckbox:
                        type === "yes_no" ? false : dgDraft.showVerifyCheckbox,
                      ...followUpStateForFieldTypeChange(type, dgDraft),
                    });
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small">Layout</Form.Label>
                <SearchableSelect
                  value={dgDraft.layout}
                  isClearable={false}
                  options={(["full", "half", "third"] as const).map((lo) => ({
                    value: lo,
                    label: lo,
                  }))}
                  onChange={(val) =>
                    this.setDeedGroupFieldDraft(section.id, g.id, {
                      layout: val as ChecklistFieldLayout,
                    })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small d-block">Required</Form.Label>
                <Form.Check
                  type="checkbox"
                  checked={dgDraft.required}
                  onChange={(e) =>
                    this.setDeedGroupFieldDraft(section.id, g.id, {
                      required: e.target.checked,
                    })
                  }
                  label="Yes"
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="small d-block">Disable</Form.Label>
                <Form.Check
                  type="checkbox"
                  checked={dgDraft.disabled}
                  onChange={(e) =>
                    this.setDeedGroupFieldDraft(section.id, g.id, {
                      disabled: e.target.checked,
                    })
                  }
                  label="Yes"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small d-block">Verification checkbox</Form.Label>
                <Form.Check
                  type="checkbox"
                  disabled={dgDraft.type === "yes_no"}
                  checked={dgDraft.showVerifyCheckbox}
                  onChange={(e) =>
                    this.setDeedGroupFieldDraft(section.id, g.id, {
                      showVerifyCheckbox: e.target.checked,
                    })
                  }
                  label="Show at runtime (right of field)"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small">Label tag (optional)</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="e.g. from Front Office: Borrower Name"
                  value={dgDraft.labelTag}
                  onChange={(e) =>
                    this.setDeedGroupFieldDraft(section.id, g.id, { labelTag: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              {this.renderDynamicFieldValueSelect(
                dgDraft.scrutinyLinkedDeedId,
                (next) =>
                  this.setDeedGroupFieldDraft(section.id, g.id, {
                    scrutinyLinkedDeedId: next,
                    ...draftPatchForOriginalPresentScrutinyTag(next, dgDraft),
                  }),
                dgDraft.dynamicValueSource,
                (nextSource) =>
                  this.setDeedGroupFieldDraft(section.id, g.id, {
                    dynamicValueSource: nextSource,
                    scrutinyLinkedDeedId: "",
                  }),
                `${section.id}-${g.id}`
              )}
            </Col>
            {this.renderFollowUpConfigPanel({
              fieldType: dgDraft.type,
              dropdownOptionsText: dgDraft.dropdownOptionsText,
              dropdownFollowUpsEnabled: dgDraft.dropdownFollowUpsEnabled,
              followUpRows: dgDraft.followUpRows,
              controlIdPrefix: `deed-group-${section.id}-${g.id}`,
              onPatch: (patch) => this.setDeedGroupFieldDraft(section.id, g.id, patch),
            })}
            <Col md={12} className="d-flex justify-content-start flex-wrap">
              {isEditingGroupField ? (
                <>
                  <Button
                    size="sm"
                    variant="primary"
                    className="dvc-btn-add-field font-weight-bold mr-2"
                    onClick={() => this.addFieldToDeedGroup(section.id, g.id)}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="dvc-btn-add-field font-weight-bold"
                    onClick={() => this.cancelEditDeedGroupField(section.id, g.id)}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  size="sm"
                  variant="primary"
                  className="dvc-btn-add-field font-weight-bold"
                  onClick={() => this.addFieldToDeedGroup(section.id, g.id)}
                >
                  Add
                </Button>
              )}
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  renderMasterDocumentTypePanel(section: ChecklistSectionDef) {
    const flow = sectionFlowMode(section);
    if (flow === "bank_product" || flow === "survey_verification" || flow === "ec_verification") return null;
    const groups = deedLinkedGroups(section);
    const draftId = this.state.documentTypeDraftBySection[section.id] ?? "";
    const alreadyIds = new Set((section.enabledDeedMasterIds || []).map(Number));
    return (
      <div className="dvc-master-doc-panel border-top pt-3 mt-3">
        <div className="dvc-panel-heading">Document types</div>
        {groups.length > 0 ? (
          groups.map((g) =>
            this.renderMasterProductGroupFieldCard(section, g, {
              cardClassName: "dvc-deed-group-card",
              fieldsSubtitle: "inside each runtime entry",
              onRemoveGroup: () =>
                g.deedMasterId != null &&
                this.removeDocumentTypeFromSection(section.id, Number(g.deedMasterId)),
            })
          )
        ) : (
          <p className="small text-muted mb-3">
            No document types yet. Pick a type below.
          </p>
        )}
        <Row className="align-items-end border-top pt-3 mt-2">
          <Col xs={12} md={8} lg={6}>
            <Form.Group className="mb-0">
              <Form.Label className="small mb-1">Add another document type</Form.Label>
              <SearchableSelect
                value={draftId}
                options={this.state.deedMasterList
                  .filter((d) => !alreadyIds.has(d.id))
                  .map((d) => ({ value: String(d.id), label: d.name }))}
                onChange={(val) =>
                  this.setState({
                    documentTypeDraftBySection: {
                      ...this.state.documentTypeDraftBySection,
                      [section.id]: val,
                    },
                  })
                }
              />
            </Form.Group>
          </Col>
          <Col md={4} lg={3} className="mt-2 mt-md-0">
            <Button
              size="sm"
              variant="primary"
              className="font-weight-bold"
              disabled={!draftId}
              onClick={() => this.addDocumentTypeToSection(section.id, Number(draftId))}
            >
              {`Add ${formatDeedMasterLabel(draftId, this.state.deedMasterList) || "document type"
                }`}
            </Button>
          </Col>
        </Row>
      </div>
    );
  }

  renderMasterBankProductPanel(section: ChecklistSectionDef) {
    const flow = sectionFlowMode(section);
    if (flow !== "bank_product") return null;
    const groups = bankLinkedGroups(section);
    const draftId = this.state.bankProductDraftBySection[section.id] ?? "";
    const alreadyIds = new Set((section.enabledBankProductIds || []).map(Number));
    return (
      <div className="dvc-master-bank-panel">
        {groups.length > 0 ? (
          groups.map((g) =>
            this.renderMasterProductGroupFieldCard(section, g, {
              cardClassName: "dvc-bank-product-card",
              fieldsSubtitle: "",
              onRemoveGroup: () =>
                g.bankProductId != null &&
                this.removeBankProductFromSection(section.id, Number(g.bankProductId)),
            })
          )
        ) : (
          <p className="small text-muted mb-3">No bank-products yet. Choose one below.</p>
        )}
        <Row className="align-items-end border-top pt-3 mt-2">
          <Col xs={12} md={8} lg={6}>
            <Form.Group className="mb-0">
              <Form.Label className="small mb-1">Add bank-product</Form.Label>
              <SearchableSelect
                value={draftId}
                options={this.state.bankProductList
                  .filter((b) => !alreadyIds.has(b.id))
                  .map((b) => ({ value: String(b.id), label: formatBankProductLabelFromRow(b) }))}
                onChange={(val) =>
                  this.setState({
                    bankProductDraftBySection: {
                      ...this.state.bankProductDraftBySection,
                      [section.id]: val,
                    },
                  })
                }
              />
            </Form.Group>
          </Col>
          <Col md={4} lg={3} className="mt-2 mt-md-0">
            <Button
              size="sm"
              variant="primary"
              className="font-weight-bold"
              disabled={!draftId}
              onClick={() => this.addBankProductToSection(section.id, Number(draftId))}
            >
              {`Add ${formatBankProductLabel(draftId, this.state.bankProductList) || "bank-product"
                }`}
            </Button>
          </Col>
        </Row>
      </div>
    );
  }

  renderMasterSurveyNatureFlowCard(
    section: ChecklistSectionDef,
    natureGroup: ChecklistRepeatableGroupDef
  ) {
    const natureKey = natureGroup.naturePropertyKey;
    if (!natureKey) return null;
    const draftKey = (this.state.surveyHierarchyDraftBySection[
      this.surveyHierarchyDraftKey(section.id, natureKey)
    ] ?? "") as SurveyHierarchyLevel | "";
    const enabledLevels = new Set(surveyHierarchyLevelsForNature(section, natureKey));
    const levelNestClass: Record<SurveyHierarchyLevel, string> = {
      schedule: "dvc-survey-hierarchy-nested--schedule",
      survey: "dvc-survey-hierarchy-nested--survey",
      boundary: "dvc-survey-hierarchy-nested--boundary",
      measurement: "dvc-survey-hierarchy-nested--measurement",
    };

    return (
      <div
        key={natureGroup.id}
        className="dvc-survey-nature-flow-card mb-4 p-3 border rounded bg-white"
      >
        <div className="d-flex flex-wrap justify-content-between align-items-start mb-3">
          <div>
            <strong className="d-block">{natureGroup.label}</strong>
          </div>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => this.removeNaturePropertyFromSection(section.id, natureKey)}
          >
            Remove
          </Button>
        </div>

        {SURVEY_HIERARCHY_LEVELS.filter((l) => enabledLevels.has(l.key)).length === 0 ? (
          <p className="small text-muted mb-3">
            No hierarchy levels yet. Add Schedule, then Survey, Boundary, and Measurement below.
          </p>
        ) : (
          SURVEY_HIERARCHY_LEVELS.filter((l) => enabledLevels.has(l.key)).map((levelRow) => {
            const hg = surveyHierarchyGroupForLevel(section, levelRow.key, natureKey);
            if (!hg) return null;
            return (
              <div
                key={hg.id}
                className={`dvc-survey-hierarchy-nested mb-3 ${levelNestClass[levelRow.key]}`}
              >
                {this.renderMasterProductGroupFieldCard(section, hg, {
                  cardClassName: "dvc-survey-hierarchy-card w-100 mb-0 p-3 border rounded bg-light",
                  fieldsSubtitle: "",
                  onRemoveGroup: () =>
                    this.removeSurveyHierarchyLevelFromNature(section.id, natureKey, levelRow.key),
                })}
              </div>
            );
          })
        )}

        <div className="dvc-master-survey-add-block">
          <div className="dvc-master-survey-add-fields">
            <Form.Group className="mb-0">
              <Form.Label className="small mb-1">Add hierarchy level</Form.Label>
              <div className="dvc-master-survey-select-wrap">
                <SearchableSelect
                  value={draftKey}
                  options={SURVEY_HIERARCHY_LEVELS.filter((r) => !enabledLevels.has(r.key)).map(
                    (r) => ({ value: r.key, label: r.label })
                  )}
                  onChange={(val) =>
                    this.setState({
                      surveyHierarchyDraftBySection: {
                        ...this.state.surveyHierarchyDraftBySection,
                        [this.surveyHierarchyDraftKey(section.id, natureKey)]: val,
                      },
                    })
                  }
                />
              </div>
            </Form.Group>
          </div>
          <div className="dvc-master-survey-add-actions">
            <Button
              size="sm"
              variant="primary"
              className="dvc-btn-add-field font-weight-bold"
              disabled={!draftKey}
              title={
                draftKey
                  ? `Add ${SURVEY_HIERARCHY_LEVELS.find((r) => r.key === draftKey)?.label ?? "level"}`
                  : undefined
              }
              onClick={() =>
                this.addSurveyHierarchyLevelToNature(
                  section.id,
                  natureKey,
                  draftKey as SurveyHierarchyLevel
                )
              }
            >
              Add level
            </Button>
          </div>
        </div>
      </div>
    );
  }

  renderMasterSurveyNaturePanel(section: ChecklistSectionDef) {
    const flow = sectionFlowMode(section);
    if (flow !== "survey_verification") return null;
    const groups = surveyLinkedGroups(section);
    const draftKey = this.state.naturePropertyDraftBySection[section.id] ?? "";
    const alreadyKeys = new Set(section.enabledNaturePropertyKeys || []);
    return (
      <>
        {groups.length > 0 ? (
          groups.map((g) => this.renderMasterSurveyNatureFlowCard(section, g))
        ) : (
          <p className="small text-muted mb-3">
            No nature-of-property flows yet. Choose one below.
          </p>
        )}
        <div className="dvc-master-survey-add-block">
          <div className="dvc-master-survey-add-fields">
            <Form.Group className="mb-0">
              <Form.Label className="small mb-1">Add nature of property</Form.Label>
              <div className="dvc-master-survey-select-wrap">
                <SearchableSelect
                  value={draftKey}
                  options={NATURE_PROPERTY_CATALOG.filter((r) => !alreadyKeys.has(r.key)).map(
                    (r) => ({ value: r.key, label: r.label })
                  )}
                  onChange={(val) =>
                    this.setState({
                      naturePropertyDraftBySection: {
                        ...this.state.naturePropertyDraftBySection,
                        [section.id]: val,
                      },
                    })
                  }
                />
              </div>
            </Form.Group>
          </div>
          <div className="dvc-master-survey-add-actions">
            <Button
              size="sm"
              variant="primary"
              className="dvc-btn-add-field font-weight-bold"
              disabled={!draftKey}
              title={
                draftKey
                  ? `Add ${naturePropertyLabel(draftKey) || "nature of property"}`
                  : undefined
              }
              onClick={() => this.addNaturePropertyToSection(section.id, draftKey)}
            >
              Add nature
            </Button>
          </div>
        </div>
      </>
    );
  }

  renderMasterSurveyHierarchyPanel(_section: ChecklistSectionDef) {
    return null;
  }

  renderMasterEcVerificationPanel(section: ChecklistSectionDef) {
    const flow = sectionFlowMode(section);
    if (flow !== "ec_verification") return null;
    const blockGroup = ecBlockGroup(section);
    const entryGroup = ecEntryGroup(section);
    return (
      <div className="dvc-ec-flow-card mb-4 p-3 border rounded bg-white">
        {EC_HIERARCHY_LEVELS.map((level) => {
          const group =
            level.key === "ec_block" ? blockGroup : entryGroup;
          if (!group) return null;
          return (
            <div
              key={group.id}
              className={`dvc-ec-hierarchy-nested mb-3 dvc-ec-hierarchy-nested--${level.key}`}
            >
              {this.renderMasterProductGroupFieldCard(section, group, {
                cardClassName: "dvc-ec-hierarchy-card w-100 mb-0 p-3 border rounded bg-light",
                fieldsSubtitle: "",
              })}
            </div>
          );
        })}
      </div>
    );
  }

  renderMasterAddFieldPanel(section: ChecklistSectionDef) {
    const flow = sectionFlowMode(section);
    if (flow === "bank_product" || flow === "survey_verification" || flow === "ec_verification") return null;
    const base =
      this.state.newFieldDraft.sectionId === section.id
        ? this.state.newFieldDraft
        : defaultNewFieldDraft(section.id, flow);
    const setDraft = (patch: Partial<NewFieldDraft>) =>
      this.setState({
        newFieldDraft: { ...base, ...patch, sectionId: section.id },
      });
    const newFieldDraft = { ...base, sectionId: section.id };
    return (
      <div className="dvc-master-add-field-panel">
        <div className="dvc-panel-heading">Checklist field</div>
        <Row>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="small">Label</Form.Label>
              <Form.Control
                size="sm"
                value={newFieldDraft.label}
                onChange={(e) => setDraft({ label: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label className="small">Field type</Form.Label>
              <SearchableSelect
                value={newFieldDraft.type}
                isClearable={false}
                options={FIELD_TYPES_RUNTIME.map((t) => ({ value: t, label: t }))}
                onChange={(val) => {
                  const type = val as ChecklistFieldType;
                  setDraft({
                    type,
                    ...followUpStateForFieldTypeChange(type, newFieldDraft),
                  });
                }}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label className="small">Layout</Form.Label>
              <SearchableSelect
                value={newFieldDraft.layout}
                isClearable={false}
                options={(["full", "half", "third"] as const).map((lo) => ({
                  value: lo,
                  label: lo,
                }))}
                onChange={(val) => setDraft({ layout: val as ChecklistFieldLayout })}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label className="small d-block">Required</Form.Label>
              <Form.Check
                type="checkbox"
                checked={newFieldDraft.required}
                onChange={(e) => setDraft({ required: e.target.checked })}
                label="Yes"
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label className="small d-block">Disable</Form.Label>
              <Form.Check
                type="checkbox"
                checked={newFieldDraft.disabled}
                onChange={(e) => setDraft({ disabled: e.target.checked })}
                label="Yes"
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="small d-block">Verification checkbox</Form.Label>
              <Form.Check
                type="checkbox"
                disabled={newFieldDraft.type === "yes_no"}
                checked={newFieldDraft.showVerifyCheckbox}
                onChange={(e) => setDraft({ showVerifyCheckbox: e.target.checked })}
                label="Show at runtime (right of field)"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label className="small">Label tag (optional)</Form.Label>
              <Form.Control
                size="sm"
                placeholder="e.g. from Front Office: Borrower Name"
                value={newFieldDraft.labelTag}
                onChange={(e) => setDraft({ labelTag: e.target.value })}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            {this.renderDynamicFieldValueSelect(
              newFieldDraft.scrutinyLinkedDeedId,
              (next) =>
                setDraft({
                  scrutinyLinkedDeedId: next,
                  ...draftPatchForOriginalPresentScrutinyTag(next, newFieldDraft),
                }),
              newFieldDraft.dynamicValueSource,
              (nextSource) =>
                setDraft({
                  dynamicValueSource: nextSource,
                  scrutinyLinkedDeedId: "",
                }),
              `checklist-${section.id}`
            )}
          </Col>
          {this.renderFollowUpConfigPanel({
            fieldType: newFieldDraft.type,
            dropdownOptionsText: newFieldDraft.dropdownOptionsText,
            dropdownFollowUpsEnabled: newFieldDraft.dropdownFollowUpsEnabled,
            followUpRows: newFieldDraft.followUpRows,
            controlIdPrefix: `add-field-${section.id}`,
            onPatch: (patch) => setDraft(patch),
          })}
          <Col md={12} className="d-flex justify-content-start">
            <Button
              size="sm"
              variant="primary"
              className="dvc-btn-add-field font-weight-bold"
              onClick={() => {
                this.setState({ newFieldDraft }, () => this.addFieldFromDraft());
              }}
            >
              Add
            </Button>
          </Col>
        </Row>
      </div>
    );
  }

  renderRuntimeFieldWithOptionalVerify(
    field: ChecklistFieldDef,
    input: React.ReactNode,
    verifyKey: string = field.id
  ) {
    if (field.disabled || !field.showVerifyCheckbox) return input;
    return (
      <div className="dvc-general-input-row">
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          {input}
        </div>
        <Form.Check
          type="checkbox"
          className="dvc-verify-checkbox"
          checked={this.state.fieldVerified[verifyKey] === "true"}
          onChange={(e) => this.setFieldVerified(verifyKey, e.target.checked)}
          aria-label={`Verified: ${field.label}`}
          title="Verification"
        />
      </div>
    );
  }

  renderRuntimeGeneralFieldRow(field: ChecklistFieldDef) {
    const foHint = this.frontOfficeHintForField(field);
    const tagHint = labelTagHintForField(field);
    return (
      <div key={field.id} className="dvc-general-field-row">
        <label className="dvc-general-label">
          {field.label}
          {field.required && <span className="text-danger"> *</span>}
          {tagHint && (
            <span className="dvc-fo-hint" title={tagHint.title}>
              {tagHint.text}
            </span>
          )}
          {foHint && <span className="dvc-fo-hint" title={foHint.title}>{foHint.text}</span>}
        </label>
        {this.renderRuntimeFieldWithOptionalVerify(
          field,
          <>
            {this.renderFieldInput(field)}
            {this.renderConditionalFollowUps(field)}
          </>
        )}
      </div>
    );
  }

  /**
   * Returns the small "from Front Office: ..." hint badge content if the field
   * is bound to a front-office catalog row, or null otherwise. Used at runtime
   * to make the mapping visible without changing how the field is rendered.
   *
   * At runtime in the work-file context, the consuming component should call
   * `front_office_get.php` with the current file id and prefill / lock the
   * field value using `response.data[0].arr[0][field.scrutinyLinkedDeedId]`.
   */
  frontOfficeHintForField = (field: ChecklistFieldDef) => {
    if (field.dynamicValueSource !== "front_office") return null;
    const id = normalizeDeedIdForSelect(field.scrutinyLinkedDeedId);
    if (!id) return null;
    const catalog = FRONT_OFFICE_CATALOG.find((c) => c.id === id);
    const label = catalog?.name ?? id;
    return {
      text: ` (from Front Office: ${label})`,
      title: `Bound to TBL_FRONTOFFICE.${id} — populated from front_office_get.php at runtime`,
    };
  };

  renderMasterUnsavedFixedBar = (masterLoading: boolean, masterSaving: boolean) => {
    if (typeof document === "undefined") return null;
    const inset = this.state.masterUnsavedBarInset;
    const style: CSSProperties = {
      top: inset?.top ?? 85,
      left: inset?.left ?? 264,
      width: inset?.width ?? undefined,
      right: inset ? undefined : 0,
    };
    return ReactDOM.createPortal(
      <div className="dvc-master-unsaved-fixed" style={style} role="status" aria-live="polite">
        <div className="dvc-master-unsaved-fixed-inner">
          <span className="dvc-master-unsaved-fixed-text">
            <strong>Unsaved changes.</strong> You added, edited, or removed sections or fields. Save
            before reloading, switching tabs, or leaving this page.
          </span>
          <Button
            size="sm"
            variant="warning"
            className="dvc-btn-master-save-pending font-weight-bold px-4"
            disabled={masterSaving || masterLoading}
            onClick={this.saveDynamicChecklistMaster}
          >
            {masterSaving ? "Saving…" : "Save master setup"}
          </Button>
        </div>
      </div>,
      document.body
    );
  };

  renderMasterSetup() {
    const { master, masterLoading, masterSaving, masterSaveBanner } = this.state;
    const masterDirty = this.isMasterDirty();
    return (
      <div
        className={`dvc-master-setup-wrap${masterDirty ? " dvc-master-setup-dirty" : ""}`}
      >
        {masterDirty && (
          <div
            className="dvc-master-unsaved-fixed-spacer"
            aria-hidden="true"
            style={{ height: this.state.masterUnsavedBarInset?.height ?? 52 }}
          />
        )}
        {masterSaveBanner && (
          <Alert
            variant={masterSaveBanner.variant === "success" ? "success" : "danger"}
            className="mb-3"
            onClose={() => this.setState({ masterSaveBanner: null })}
            dismissible
          >
            {masterSaveBanner.text}
          </Alert>
        )}
        <Card className="mb-4 dvc-card dvc-main-sections-card">
          <Card.Header className="dvc-card-header-subtle dvc-sections-header-sticky d-flex justify-content-between align-items-center flex-wrap py-3">
            <div className="dvc-sections-title">Sections</div>
            <div className="d-flex align-items-center">
              {masterLoading && (
                <span className="small text-muted mr-2">Loading…</span>
              )}
              {masterDirty && !masterLoading && (
                <span className="small text-warning font-weight-bold mr-2">Unsaved</span>
              )}
              <Button
                size="sm"
                variant={masterDirty ? "warning" : "success"}
                className={masterDirty ? "dvc-btn-master-save-pending" : undefined}
                disabled={masterSaving || masterLoading}
                onClick={this.saveDynamicChecklistMaster}
              >
                {masterSaving ? "Saving…" : "Save master setup"}
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="pt-3">
            <div className="dvc-master-toolbar mb-4">
              <div className="dvc-master-toolbar-label">Add section</div>
              <form onSubmit={this.onAddSectionFormSubmit}>
                <Row className="align-items-center flex-wrap">
                  <Col xs={12} md={6} lg={5} className="mb-2 mb-md-0">
                    <InputGroup className="dvc-form-control">
                      <Form.Control
                        placeholder="Section title (e.g. General Verification)"
                        value={this.state.newSectionTitle}
                        onChange={(e) => this.setState({ newSectionTitle: e.target.value })}
                        aria-label="New section title"
                      />
                    </InputGroup>
                  </Col>
                  <Col xs="auto" className="mb-2 mb-md-0 pr-2">
                    <Button
                      type="submit"
                      variant="primary"
                      size="sm"
                      className="font-weight-bold px-3 dvc-toolbar-action-btn"
                    >
                      Add section
                    </Button>
                  </Col>
                  <Col xs="auto" className="mb-2 mb-md-0">
                    <Button
                      type="button"
                      variant="outline-primary"
                      size="sm"
                      className="font-weight-bold dvc-toolbar-action-btn"
                      onClick={this.addBlankSectionAtEnd}
                      title="Append an empty section you can rename"
                    >
                      + Blank section
                    </Button>
                  </Col>
                </Row>
              </form>
            </div>

            {master.map((section) => {
              const flow = sectionFlowMode(section);
              return (
                <Card key={section.id} className="mb-4 dvc-card dvc-section-card">
                  <Card.Header className="py-3 px-3 dvc-section-card-header border-bottom">
                    <div className="d-flex flex-wrap align-items-start justify-content-between">
                      <div className="d-flex align-items-start flex-grow-1 pr-3 mb-2 mb-md-0">
                        <span className="dvc-order-badge mr-3">{section.order}</span>
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <Form.Control
                            size="sm"
                            className="dvc-section-title-input border-0 bg-transparent px-0"
                            style={{ boxShadow: "none" }}
                            value={section.title}
                            onChange={(e) => this.updateSectionTitle(section.id, e.target.value)}
                          />
                          <div className="mt-2" style={{ maxWidth: 280 }}>
                            <SearchableSelect
                              value={flow}
                              isClearable={false}
                              options={[
                                { value: "checklist", label: "Primary flow: checklist fields" },
                                { value: "document_types", label: "Primary flow: document types" },
                                { value: "bank_product", label: "Bank product (exclusive)" },
                                {
                                  value: "survey_verification",
                                  label: "Survey verification (General Detail hierarchy)",
                                },
                                {
                                  value: "ec_verification",
                                  label: "EC verification (document + entry details)",
                                },
                              ]}
                              onChange={(val) =>
                                this.updateSectionFlowMode(
                                  section.id,
                                  val as ChecklistSectionFlowMode
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center dvc-section-actions flex-shrink-0">
                        <ButtonGroup size="sm" className="mr-2">
                          <Button
                            variant="outline-secondary"
                            title="Move section up"
                            onClick={() => this.moveSection(section.id, -1)}
                          >
                            ↑
                          </Button>
                          <Button
                            variant="outline-secondary"
                            title="Move section down"
                            onClick={() => this.moveSection(section.id, 1)}
                          >
                            ↓
                          </Button>
                        </ButtonGroup>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          title="Insert a new empty section after this one"
                          onClick={() => this.insertSectionAfter(section.id)}
                        >
                          Add below
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="ml-1"
                          onClick={() => this.removeSection(section.id)}
                        >
                          Remove section
                        </Button>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0 dvc-table-wrap dvc-field-table-wrap">
                    {flow !== "bank_product" && flow !== "survey_verification" && (
                      <Table responsive hover size="sm" className="mb-0 dvc-field-table">
                        <MasterFieldDuplicateTable
                          key={`dup-section-${section.id}-${section.fields.length}`}
                          sectionId={section.id}
                          fields={section.fields}
                          colSpan={9}
                          enumerateTargets={this.enumerateDuplicateTargets}
                          onDuplicate={this.applyDuplicateSourceKeys}
                          renderChrome={(api) => (
                            <>
                              <thead className="thead-light">
                                <tr>
                                  <th
                                    className="dvc-col-select border-0"
                                    title="Select for bulk duplicate"
                                  >
                                    {api.renderSelectAllHeader()}
                                  </th>
                                  <th className="dvc-col-num border-0">#</th>
                                  <th className="border-0">Label</th>
                                  <th style={{ width: 108 }} className="border-0">
                                    Type
                                  </th>
                                  <th style={{ width: 88 }} className="border-0">
                                    Layout
                                  </th>
                                  <th style={{ width: 96 }} className="border-0">
                                    Required
                                  </th>
                                  <th style={{ width: 96 }} className="border-0">
                                    Disable
                                  </th>
                                  <th className="border-0">Dynamic field value</th>
                                  <th style={{ width: 200 }} className="text-right border-0">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody>{api.renderBody()}</tbody>
                            </>
                          )}
                          renderRow={(ctx) => {
                            const f = ctx.field;
                            const idx = ctx.index;
                            const editKey = `${section.id}:${f.id}`;
                            const isEditing = this.state.editingFieldKey === editKey;
                            const dynamicFieldLabel = formatDynamicFieldValueLabel(
                              f.scrutinyLinkedDeedId,
                              this.combinedDynamicValueTags(),
                              f.dynamicValueSource,
                              this.state.frontOfficeFields
                            );
                            const isDeedGate = isDeedGateFieldDef(f);
                            return (
                              <Fragment key={f.id}>
                                <tr>
                                  <td className="dvc-col-select">{ctx.selectionCell}</td>
                                  <td className="dvc-col-num">{idx + 1}</td>
                                  <td className="dvc-field-label-cell">
                                    {f.label}
                                    {/* {isDeedGate && (
                                      <span className="dvc-deed-gate-pill" title="Document type gate">
                                        Gate
                                      </span>
                                    )} */}
                                    {f.required && <span className="dvc-req-pill">Required</span>}
                                    {f.disabled && <span className="dvc-dis-pill">Disabled</span>}
                                    {f.labelTag && (
                                      <span
                                        className="dvc-fo-hint d-inline-block ml-1"
                                        title={f.labelTag}
                                      >
                                        {f.labelTag.startsWith("(") ? f.labelTag : `(${f.labelTag})`}
                                      </span>
                                    )}
                                  </td>
                                  <td>{renderChecklistFieldTypeBadges(f)}</td>
                                  <td>
                                    <span className="dvc-layout-muted">{f.layout ?? "half"}</span>
                                  </td>
                                  <td>
                                    {f.required ? (
                                      <span className="dvc-req-yes">Yes</span>
                                    ) : (
                                      <span className="dvc-req-dash" aria-hidden>
                                        —
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {f.disabled ? (
                                      <span className="dvc-dis-yes">Yes</span>
                                    ) : (
                                      <span className="dvc-req-dash" aria-hidden>
                                        —
                                      </span>
                                    )}
                                  </td>
                                  <td className="dvc-dynamic-field-cell">
                                    {dynamicFieldLabel ? (
                                      <span
                                        className="small d-inline-block text-truncate"
                                        style={{ maxWidth: 240 }}
                                        title={dynamicFieldLabel}
                                      >
                                        {dynamicFieldLabel}
                                      </span>
                                    ) : (
                                      <span className="text-muted small">—</span>
                                    )}
                                  </td>
                                  <td className="text-right">
                                    <ButtonGroup size="sm" className="dvc-section-actions">
                                      <Button
                                        variant="outline-secondary"
                                        title="Move field up"
                                        onClick={() => this.moveField(section.id, f.id, -1)}
                                      >
                                        ↑
                                      </Button>
                                      <Button
                                        variant="outline-secondary"
                                        title="Move field down"
                                        onClick={() => this.moveField(section.id, f.id, 1)}
                                      >
                                        ↓
                                      </Button>
                                      <Button
                                        variant="outline-primary"
                                        onClick={() => this.startEditField(section.id, f)}
                                      >
                                        Edit
                                      </Button>
                                      <Button
                                        variant="outline-info"
                                        title="Duplicate this field to another section or document type"
                                        onClick={ctx.openSingleDuplicatePicker}
                                      >
                                        Duplicate
                                      </Button>
                                      <Button
                                        variant="outline-danger"
                                        onClick={() => this.removeField(section.id, f.id)}
                                      >
                                        Remove
                                      </Button>
                                    </ButtonGroup>
                                  </td>
                                </tr>
                                {isEditing && (
                                  <tr className="dvc-table-edit-row">
                                    <td colSpan={9} className="p-3 border-top-0">
                                      <div className="w-100 dvc-edit-field-wrap">
                                        <Row className="mx-0 w-100">
                                          <Col md={2}>
                                            <Form.Group>
                                              <Form.Label className="small">Label</Form.Label>
                                              <Form.Control
                                                size="sm"
                                                value={this.state.fieldEditDraft.label}
                                                onChange={(e) =>
                                                  this.setState({
                                                    fieldEditDraft: {
                                                      ...this.state.fieldEditDraft,
                                                      label: e.target.value,
                                                    },
                                                  })
                                                }
                                              />
                                            </Form.Group>
                                          </Col>
                                          <Col md={2}>
                                            <Form.Group>
                                              <Form.Label className="small">Type</Form.Label>
                                              <SearchableSelect
                                                value={this.state.fieldEditDraft.type}
                                                isClearable={false}
                                                options={FIELD_TYPES_RUNTIME.map((t) => ({
                                                  value: t,
                                                  label: t,
                                                }))}
                                                onChange={(val) => {
                                                  const type = val as ChecklistFieldType;
                                                  const draft = this.state.fieldEditDraft;
                                                  this.setState({
                                                    fieldEditDraft: {
                                                      ...draft,
                                                      type,
                                                      ...followUpStateForFieldTypeChange(type, draft),
                                                    },
                                                  });
                                                }}
                                              />
                                            </Form.Group>
                                          </Col>
                                          <Col md={2}>
                                            <Form.Group>
                                              <Form.Label className="small">Layout</Form.Label>
                                              <SearchableSelect
                                                value={this.state.fieldEditDraft.layout}
                                                isClearable={false}
                                                options={(["full", "half", "third"] as const).map(
                                                  (lo) => ({ value: lo, label: lo })
                                                )}
                                                onChange={(val) =>
                                                  this.setState({
                                                    fieldEditDraft: {
                                                      ...this.state.fieldEditDraft,
                                                      layout: val as ChecklistFieldLayout,
                                                    },
                                                  })
                                                }
                                              />
                                            </Form.Group>
                                          </Col>
                                          <Col md={3}>
                                            <Form.Group>
                                              <Form.Label className="small">Label tag</Form.Label>
                                              <Form.Control
                                                size="sm"
                                                placeholder="e.g. from Front Office: Borrower Name"
                                                value={this.state.fieldEditDraft.labelTag}
                                                onChange={(e) =>
                                                  this.setState({
                                                    fieldEditDraft: {
                                                      ...this.state.fieldEditDraft,
                                                      labelTag: e.target.value,
                                                    },
                                                  })
                                                }
                                              />
                                            </Form.Group>
                                          </Col>
                                        </Row>
                                        {!isDeedGate && (
                                          <Row className="mx-0 mt-2 align-items-end">
                                            <Col md={5}>
                                              {this.renderDynamicFieldValueSelect(
                                                this.state.fieldEditDraft.scrutinyLinkedDeedId,
                                                (val) =>
                                                  this.setState({
                                                    fieldEditDraft: {
                                                      ...this.state.fieldEditDraft,
                                                      scrutinyLinkedDeedId: val,
                                                      ...draftPatchForOriginalPresentScrutinyTag(
                                                        val,
                                                        this.state.fieldEditDraft
                                                      ),
                                                    },
                                                  }),
                                                this.state.fieldEditDraft.dynamicValueSource,
                                                (nextSource) =>
                                                  this.setState({
                                                    fieldEditDraft: {
                                                      ...this.state.fieldEditDraft,
                                                      dynamicValueSource: nextSource,
                                                      scrutinyLinkedDeedId: "",
                                                    },
                                                  }),
                                                `edit-${this.state.editingFieldKey ?? "field"}`
                                              )}
                                            </Col>
                                            <Col md={2} className="dvc-follow-up-required-col">
                                              <Form.Group>
                                                <Form.Label className="small d-block">Required</Form.Label>
                                                <Form.Check
                                                  type="checkbox"
                                                  checked={this.state.fieldEditDraft.required}
                                                  onChange={(e) =>
                                                    this.setState({
                                                      fieldEditDraft: {
                                                        ...this.state.fieldEditDraft,
                                                        required: e.target.checked,
                                                      },
                                                    })
                                                  }
                                                  label="Yes"
                                                />
                                              </Form.Group>
                                            </Col>
                                            <Col md={2} className="dvc-follow-up-required-col">
                                              <Form.Group>
                                                <Form.Label className="small d-block">Disable</Form.Label>
                                                <Form.Check
                                                  type="checkbox"
                                                  checked={this.state.fieldEditDraft.disabled}
                                                  onChange={(e) =>
                                                    this.setState({
                                                      fieldEditDraft: {
                                                        ...this.state.fieldEditDraft,
                                                        disabled: e.target.checked,
                                                      },
                                                    })
                                                  }
                                                  label="Yes"
                                                />
                                              </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                              <Form.Group>
                                                <Form.Label className="small d-block">
                                                  Verification checkbox
                                                </Form.Label>
                                                <Form.Check
                                                  type="checkbox"
                                                  disabled={
                                                    this.state.fieldEditDraft.type === "yes_no"
                                                  }
                                                  checked={
                                                    this.state.fieldEditDraft.showVerifyCheckbox
                                                  }
                                                  onChange={(e) =>
                                                    this.setState({
                                                      fieldEditDraft: {
                                                        ...this.state.fieldEditDraft,
                                                        showVerifyCheckbox: e.target.checked,
                                                      },
                                                    })
                                                  }
                                                  label="Show at runtime (right of field)"
                                                />
                                              </Form.Group>
                                            </Col>
                                          </Row>
                                        )}
                                        <Row className="mx-0 mt-1">
                                          {this.renderFollowUpConfigPanel({
                                            fieldType: this.state.fieldEditDraft.type,
                                            dropdownOptionsText:
                                              this.state.fieldEditDraft.dropdownOptionsText,
                                            dropdownFollowUpsEnabled:
                                              this.state.fieldEditDraft.dropdownFollowUpsEnabled,
                                            followUpRows: this.state.fieldEditDraft.followUpRows,
                                            controlIdPrefix: `edit-fu-${section.id}-${f.id}`,
                                            onPatch: (patch) =>
                                              this.setState({
                                                fieldEditDraft: {
                                                  ...this.state.fieldEditDraft,
                                                  ...patch,
                                                },
                                              }),
                                          })}
                                        </Row>
                                        <Row className="mx-0 mt-2">
                                          <Col md={12}>
                                            <Button
                                              size="sm"
                                              variant="success"
                                              className="mr-2"
                                              onClick={() => this.saveEditField(section.id, f.id)}
                                            >
                                              Save field
                                            </Button>
                                            <Button size="sm" variant="outline-secondary" onClick={this.cancelEditField}>
                                              Cancel
                                            </Button>
                                          </Col>
                                        </Row>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          }}
                        />
                      </Table>
                    )}
                    {flow !== "bank_product" && flow !== "survey_verification" && this.renderMasterAddFieldPanel(section)}
                    {flow !== "bank_product" && flow !== "survey_verification" && flow !== "ec_verification" && this.renderMasterDocumentTypePanel(section)}
                    {flow === "bank_product" && this.renderMasterBankProductPanel(section)}
                    {flow === "survey_verification" && (
                      <div className="dvc-master-survey-flow px-3 pb-3">
                        <div className="dvc-panel-heading mb-2 mt-3">
                          Survey verification flow
                        </div>
                        {this.renderMasterSurveyNaturePanel(section)}
                      </div>
                    )}
                    {flow === "ec_verification" && (
                      <div className="dvc-master-ec-flow px-3 pb-3">
                        <div className="dvc-panel-heading mb-2 mt-3">
                          EC verification flow
                        </div>
                        {this.renderMasterEcVerificationPanel(section)}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              );
            })}
          </Card.Body>
        </Card>
      </div>
    );
  }

  renderRepeatableGroupRuntime(
    section: ChecklistSectionDef,
    g: ChecklistRepeatableGroupDef,
    showHeader = true
  ) {
    const rk = repeatableEntriesKey(section.id, g.id);
    const rows = this.state.repeatableValues[rk] || [];
    return (
      <div key={g.id} className={showHeader ? "mt-2 mb-1" : "mb-1"}>
        {showHeader && (
          <div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
            <span className="small text-muted font-weight-bold text-uppercase">{g.label}</span>
            <Button
              size="sm"
              variant="primary"
              disabled={rows.length >= (g.maxInstances ?? 99)}
              onClick={() => this.addRepeatableRow(section.id, g.id)}
            >
              {g.addButtonLabel}
            </Button>
          </div>
        )}
        {rows.map((row, rowIdx) => (
          <div key={rowIdx} className="dvc-repeat-row mb-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Badge variant="secondary">Entry #{rowIdx + 1}</Badge>
              {rows.length > (g.minInstances ?? 1) && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="py-0 px-2"
                  title="Remove this entry"
                  onClick={() => this.removeRepeatableRow(section.id, g.id, rowIdx)}
                >
                  Remove
                </Button>
              )}
            </div>
            <Row>
              {g.fields.map((field) => {
                const foHint = this.frontOfficeHintForField(field);
                const tagHint = labelTagHintForField(field);
                return (
                  <Col md={layoutToColMd(field.layout)} key={field.id} className="mb-2">
                    <div className="dvc-field-tile dvc-form-control py-2">
                      <Form.Group className="mb-0">
                        <Form.Label className="small mb-1 font-weight-bold d-block">
                          {field.label}
                          {field.required && <span className="text-danger"> *</span>}
                          {tagHint && (
                            <span className="dvc-fo-hint" title={tagHint.title}>
                              {tagHint.text}
                            </span>
                          )}
                          {foHint && (
                            <span className="dvc-fo-hint" title={foHint.title}>
                              {foHint.text}
                            </span>
                          )}
                        </Form.Label>
                        {this.renderRuntimeFieldWithOptionalVerify(
                          field,
                          this.renderFieldInput(
                            field,
                            row[field.id] ?? "",
                            (val) =>
                              this.setRepeatableCell(section.id, g.id, rowIdx, field.id, val),
                            `${section.id}-${g.id}-${rowIdx}-${field.id}`
                          ),
                          `${field.id}::${rowIdx}`
                        )}
                      </Form.Group>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        ))}
      </div>
    );
  }

  renderSectionRuntimeFields(section: ChecklistSectionDef) {
    const flow = sectionFlowMode(section);
    const gates = sectionGateFields(section);
    const groupFieldIds = fieldIdsInDeedGroups(section);
    const gateIds = new Set(gates.map((g) => g.id));
    const standaloneFields = section.fields.filter(
      (f) => !gateIds.has(f.id) && !groupFieldIds.has(f.id)
    );
    const plainGroups = (section.repeatableGroups || []).filter((g) => !g.deedMasterId);

    if (flow === "bank_product") {
      return (
        <>
          {bankLinkedGroups(section).map((group) => (
            <div key={group.id} className="dvc-bank-product-card mb-4 p-3 border rounded bg-white">
              <div className="dvc-deed-type-head mb-3">
                <span className="dvc-deed-type-title">{group.label}</span>
              </div>
              {group.fields.length > 0 ? (
                <Row>
                  {group.fields.map((field) => {
                    const foHint = this.frontOfficeHintForField(field);
                    const tagHint = labelTagHintForField(field);
                    return (
                      <Col md={layoutToColMd(field.layout)} key={field.id} className="mb-3">
                        <div className="dvc-field-tile dvc-form-control">
                          <Form.Group className="mb-0">
                            <Form.Label className="small mb-2 font-weight-bold d-block">
                              {field.label}
                              {field.required && <span className="text-danger"> *</span>}
                              {tagHint && (
                                <span className="dvc-fo-hint" title={tagHint.title}>
                                  {tagHint.text}
                                </span>
                              )}
                              {foHint && (
                                <span className="dvc-fo-hint" title={foHint.title}>
                                  {foHint.text}
                                </span>
                              )}
                            </Form.Label>
                            {this.renderRuntimeFieldWithOptionalVerify(
                              field,
                              <>
                                {this.renderFieldInput(field)}
                                {this.renderConditionalFollowUps(field)}
                              </>
                            )}
                          </Form.Group>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              ) : (
                <p className="small text-muted mb-0">No checklist fields configured.</p>
              )}
            </div>
          ))}
        </>
      );
    }

    if (flow === "survey_verification") {
      return (
        <>
          {surveyLinkedGroups(section).map((group) => {
            const natureKey = group.naturePropertyKey ?? "";
            const hierarchyGroups = natureKey
              ? surveyHierarchyLinkedGroups(section, natureKey)
              : [];
            return (
              <div
                key={group.id}
                className="dvc-survey-nature-flow-card mb-4 p-3 border rounded bg-white"
              >
                <div className="dvc-deed-type-head mb-3">
                  <span className="dvc-deed-type-title">{group.label}</span>
                </div>
                {hierarchyGroups.length === 0 ? (
                  <p className="small text-muted mb-0">
                    No hierarchy levels configured. Add Schedule, Survey, Boundary, or Measurement
                    in master setup.
                  </p>
                ) : (
                  hierarchyGroups.map((hg) => (
                    <div
                      key={hg.id}
                      className={`dvc-survey-hierarchy-nested mb-2 p-2 border rounded bg-light dvc-survey-hierarchy-nested--${hg.surveyHierarchyLevel ?? "schedule"}`}
                    >
                      <div className="font-weight-bold mb-1">{hg.label}</div>
                      {hg.fields.length > 0 ? (
                        <ul className="small mb-0 pl-3">
                          {hg.fields.map((f) => (
                            <li key={f.id}>
                              {f.label} <span className="text-muted">({f.type})</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="small text-muted mb-0">No fields yet.</p>
                      )}
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </>
      );
    }

    if (flow === "ec_verification") {
      const blockGroup = ecBlockGroup(section);
      const entryGroup = ecEntryGroup(section);
      return (
        <div className="dvc-ec-flow-card mb-4 p-3 border rounded bg-white">
          {[blockGroup, entryGroup].filter(Boolean).map((group) => (
            <div key={group!.id} className="mb-2 p-2 border rounded bg-light">
              <div className="font-weight-bold mb-1">{group!.label}</div>
              {group!.fields.length > 0 ? (
                <ul className="small mb-0 pl-3">
                  {group!.fields.map((f) => (
                    <li key={f.id}>
                      {f.label} <span className="text-muted">({f.type})</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="small text-muted mb-0">No fields yet.</p>
              )}
            </div>
          ))}
        </div>
      );
    }

    const hasStandalones = standaloneFields.length > 0;
    const hasPlainGroups = plainGroups.length > 0;
    const hasGates = gates.length > 0;

    return (
      <>
        {hasStandalones &&
          standaloneFields.map((field) => this.renderRuntimeGeneralFieldRow(field))}
        {hasPlainGroups &&
          plainGroups.map((g) => this.renderRepeatableGroupRuntime(section, g, true))}
        {hasGates &&
          gates.map((gate) => {
            const group = deedLinkedGroups(section).find(
              (g) => String(g.deedMasterId) === String(gate.deedMasterId)
            );
            const gateOpen = (this.state.checklistValues[gate.id] ?? "No") === "Yes";
            const rk = group ? repeatableEntriesKey(section.id, group.id) : "";
            const rows = group ? this.state.repeatableValues[rk] || [] : [];
            return (
              <div key={gate.id} className="dvc-deed-type-block mb-4 pb-3 border-bottom">
                <div className="dvc-deed-type-head d-flex flex-wrap align-items-center justify-content-between">
                  <div className="d-flex flex-wrap align-items-center">
                    <span className="dvc-deed-type-title">{gate.label}</span>
                    {this.renderFieldInput(gate)}
                  </div>
                  {group && gateOpen && (
                    <Button
                      size="sm"
                      variant="primary"
                      className="mt-2 mt-md-0"
                      disabled={rows.length >= (group.maxInstances ?? 99)}
                      onClick={() => this.addRepeatableRow(section.id, group.id)}
                    >
                      {group.addButtonLabel}
                    </Button>
                  )}
                </div>
                {group && gateOpen && this.renderRepeatableGroupRuntime(section, group, false)}
              </div>
            );
          })}
      </>
    );
  }

  renderFrontOfficeMaster() {
    const {
      frontOfficeFields,
      newFrontOfficeFieldName,
      frontOfficeMasterLoading,
      frontOfficeMasterSaving,
      frontOfficeSaveBanner,
    } = this.state;
    const usedIds = new Set(frontOfficeFields.map((f) => f.id));
    const remainingCatalog = FRONT_OFFICE_CATALOG.filter((c) => !usedIds.has(c.id));
    const selectOptions = remainingCatalog.map((c) => ({
      value: c.id,
      label: c.name,
    }));
    return (
      <div>
        {frontOfficeSaveBanner && (
          <Alert
            variant={frontOfficeSaveBanner.variant === "success" ? "success" : "danger"}
            className="mb-3"
            onClose={() => this.setState({ frontOfficeSaveBanner: null })}
            dismissible
          >
            {frontOfficeSaveBanner.text}
          </Alert>
        )}
        <Card className="mb-4 dvc-card">
          <Card.Header className="dvc-card-header-subtle d-flex justify-content-between align-items-center flex-wrap py-3">
            <div className="dvc-sections-title">Front office master</div>
            <div className="d-flex align-items-center">
              {frontOfficeMasterLoading && (
                <span className="small text-muted mr-2">Loading…</span>
              )}
              <Button
                size="sm"
                variant="success"
                disabled={frontOfficeMasterSaving || frontOfficeMasterLoading}
                onClick={this.saveFrontOfficeMaster}
              >
                {frontOfficeMasterSaving ? "Saving…" : "Save front office master"}
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="pt-3">
            <Row className="align-items-end mb-3">
              <Col md={7} lg={6}>
                <Form.Group className="mb-0">
                  <Form.Label className="small mb-1">Add front office field</Form.Label>
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <SearchableSelect
                        inputId="dvc-fo-add"
                        value={newFrontOfficeFieldName}
                        onChange={(v) =>
                          this.setState({ newFrontOfficeFieldName: v })
                        }
                        options={selectOptions}
                        placeholder={
                          remainingCatalog.length === 0
                            ? "All front office fields already added"
                            : "Choose a Front Office field..."
                        }
                        isDisabled={remainingCatalog.length === 0}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="primary"
                      className="font-weight-bold d-inline-flex align-items-center justify-content-center"
                      style={{ height: 31, minWidth: 96 }}
                      onClick={this.addFrontOfficeField}
                      disabled={
                        !newFrontOfficeFieldName ||
                        remainingCatalog.length === 0
                      }
                    >
                      Add
                    </Button>
                  </div>
                </Form.Group>
              </Col>
              <Col md={5} lg={6}>
                <Form.Label className="small mb-1 invisible" aria-hidden>
                  spacer
                </Form.Label>
                <div
                  className="small text-muted d-flex align-items-center"
                  style={{ height: 31 }}
                >
                  {frontOfficeFields.length} / {FRONT_OFFICE_CATALOG.length}{" "}
                  fields exposed to dynamic checklist
                </div>
              </Col>
            </Row>

            {frontOfficeFields.length === 0 ? (
              <Alert variant="light" className="mb-0 text-muted small border">
                No front office fields added yet. Use the picker above to
                expose Front Office fields (Borrower Name, App Id, Loan
                Amount, …) to the dynamic checklist.
              </Alert>
            ) : (
              <Table size="sm" className="dvc-table mb-0 text-center">
                <thead className="dvc-table-head">
                  <tr>
                    <th style={{ width: 64 }} className="border-0 text-center">
                      #
                    </th>
                    <th className="border-0 text-center">Front office field</th>
                    <th style={{ width: 220 }} className="border-0 text-center">
                      Key
                    </th>
                    <th style={{ width: 140 }} className="border-0 text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {frontOfficeFields.map((row, idx) => (
                    <tr key={row.id}>
                      <td className="dvc-col-num text-center align-middle">{idx + 1}</td>
                      <td className="text-center align-middle">{row.name}</td>
                      <td className="text-center align-middle">
                        <code className="small">{row.id}</code>
                      </td>
                      <td className="text-center align-middle">
                        <ButtonGroup size="sm" className="dvc-section-actions">
                          <Button
                            variant="outline-danger"
                            onClick={() => this.removeFrontOfficeField(row.id)}
                          >
                            Remove
                          </Button>
                        </ButtonGroup>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }

  renderDynamicChecklist() {
    const sections = this.visibleSections();
    const { validationMessages, saveBanner } = this.state;
    return (
      <div>
        <Alert variant="light" className="dvc-alert-intro small mb-3">
          <strong>Runtime checklist</strong> — Checklist fields render with label, input and an optional
          verification checkbox. Document types appear with a Yes/No gate and a repeatable field grid
          when set to Yes. Save validates all visible rows.
        </Alert>
        {saveBanner && (
          <Alert
            variant={saveBanner.variant}
            dismissible
            onClose={() => this.setState({ saveBanner: null })}
            className="shadow-sm"
          >
            {saveBanner.text}
          </Alert>
        )}
        {validationMessages.length > 0 && (
          <Alert variant="danger" className="shadow-sm">
            <div className="font-weight-bold mb-1">Required fields missing</div>
            <ul className="mb-0 pl-3">
              {validationMessages.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </Alert>
        )}
        {sections.length === 0 ? (
          <Alert variant="warning">
            No sections in the master. Add sections in the Master tab.
          </Alert>
        ) : (
          sections.map((section) => {
            const isCollapsible = !!section.collapsible;
            const open = this.state.sectionUiOpen[section.id] !== false;
            const warn = section.headerVariant === "warning";
            const rgFieldCount = (section.repeatableGroups || []).reduce((n, g) => n + g.fields.length, 0);
            const totalFields = section.fields.length + rgFieldCount;

            return (
              <Card key={section.id} className="mb-3 dvc-card dvc-runtime-section">
                <Card.Header
                  className={
                    isCollapsible
                      ? `pt-3 px-3 pb-2 d-flex align-items-center justify-content-between ${warn ? "dvc-runtime-warn-head" : ""
                      }`
                      : "pb-0 pt-3 px-3"
                  }
                  onClick={isCollapsible ? () => this.toggleSectionOpen(section.id) : undefined}
                  style={isCollapsible ? { cursor: "pointer" } : undefined}
                >
                  <div className="d-flex align-items-baseline justify-content-between flex-wrap w-100 pr-1">
                    <h6 className="mb-0">{section.title}</h6>
                    <div className="d-flex align-items-center">
                      <Badge pill variant="light" className="text-muted border font-weight-normal mr-2">
                        {totalFields} field{totalFields === 1 ? "" : "s"}
                      </Badge>
                      {isCollapsible && (
                        <span className="text-muted small font-weight-bold" aria-hidden>
                          {open ? "−" : "+"}
                        </span>
                      )}
                    </div>
                  </div>
                </Card.Header>
                <Collapse in={!isCollapsible || open}>
                  <div>
                    <Card.Body className="pt-3 border-top-0">
                      {this.renderSectionRuntimeFields(section)}
                    </Card.Body>
                  </div>
                </Collapse>
              </Card>
            );
          })
        )}
        <Button variant="success" className="dvc-btn-save px-4" onClick={this.handleSaveChecklist}>
          Validate &amp; save
        </Button>
      </div>
    );
  }

  render() {
    return (
      <>
        <style type="text/css">{DVC_CSS}</style>
        <section className="pcoded-main-container">
          <ToastsContainer store={ToastsStore} position={ToastsContainerPosition.TOP_RIGHT} />
          <div className="pcoded-wrapper">
            <div className="pcoded-content">
              <div className="pcoded-inner-content">
                <div className="main-body">
                  <div className="page-wrapper">
                    <div
                      className="dvc-root container-fluid py-4 px-md-4"
                      style={{
                        width: "100%",
                        maxWidth: "100%",
                        background:
                          "linear-gradient(180deg, #f0f9ff 0%, #f8fafc 18%, #fafbfc 50%, #ffffff 100%)",
                        minHeight: "100vh",
                      }}
                    >
                      <div className="dvc-hero mb-4">
                        <div className="dvc-hero-inner">
                          <h4 className="mb-2 font-weight-bold">
                            Dynamic process / verification checklist
                          </h4>
                          <p
                            className="mb-0 small dvc-hero-lead"
                            style={{ maxWidth: "52rem", lineHeight: 1.55 }}
                          >
                            Configure the master checklist and capture verification answers at runtime.
                          </p>
                        </div>
                      </div>

                      {this.state.activeTab === "master" &&
                        this.isMasterDirty() &&
                        this.renderMasterUnsavedFixedBar(
                          this.state.masterLoading,
                          this.state.masterSaving
                        )}

                      <Tabs
                        id="dynamic-checklist-tabs"
                        activeKey={this.state.activeTab}
                        onSelect={(k) => this.handleMasterTabSelect((k as string) || "master")}
                        className="dvc-tabs mb-4"
                      >
                        <Tab
                          eventKey="master"
                          title="1. Master setup"
                          tabClassName="font-weight-bold"
                        >
                          {this.state.activeTab === "master" && this.renderMasterSetup()}
                        </Tab>
                        <Tab
                          eventKey="front_office_master"
                          title="2. Front office master"
                          tabClassName="font-weight-bold"
                        >
                          {this.state.activeTab === "front_office_master" &&
                            this.renderFrontOfficeMaster()}
                        </Tab>
                        {/* <Tab
                          eventKey="runtime"
                          title="3. Dynamic checklist"
                          tabClassName="font-weight-bold"
                        >
                          {this.state.activeTab === "runtime" && this.renderDynamicChecklist()}
                        </Tab> */}
                      </Tabs>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }
}

export default DynamicChecklistManagement;
